import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import { formatMoneyDisplayNumber } from "./utils/priceFormat";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
// MADDE 1: Klavye görünümünü (açık/koyu) uygulama temasına bağlamak için.
import { Keyboard, KeyboardStyle } from "@capacitor/keyboard";
import { nativeCallBridgeKur, familyIdAta, oturumBilgisiniNativeYaz } from "./services/nativeCallBridge";
import callManager from "./services/callManager";
import AramaEkrani from "./components/AramaEkrani";
import { useNativePush }        from "./hooks/useNativePush";
// NOT: registerNativePush (nativeNotificationService.js) artık burada kullanılmıyor.
// Native push kaydı tek kaynaktan, useNativePush hook'u üzerinden yönetiliyor (bkz. aşağıda).
// Servis dosyası kasıtlı olarak silinmedi; başka bir yerden çağrılmak istenirse hazır kalsın.

/* ── i18n ── */
import { useLanguage } from "./i18n/LanguageContext";

/* ── Firebase ── */

/* ── Hooks ── */
import { useAuth }          from "./hooks/useAuth";
import { useFamily }        from "./hooks/useFamily";
import { useAppData }       from "./hooks/useAppData";
import { useNotifications } from "./hooks/useNotifications";
import { usePermissions }   from "./hooks/usePermissions";
import { usePremium }       from "./hooks/usePremium";
import { useUserTimezone }  from "./hooks/useUserTimezone";
import { appAlert, appConfirm } from "./components/AppDialog";
import {
  areAndroidHealthFeaturesDisabled,
  normalizeAndroidPage,
  isIOSPlatform,
} from "./utils/platformFeatures";

/* ── Servisler ── */
import {
  belgeYukleService, belgeSilService,
  belgeGuncelleService, belgeVersiyonEkleService,
} from "./services/documentService";
import {
  takvimEtkinlikEkleService,
  takvimHariciEtkinlikleriTopluAktarService,
  takvimEtkinlikSilService,
  takvimEtkinlikGuncelleService,
} from "./services/calendarService";
import {
  finansEkleService, finansSilService,
} from "./services/financeService";
import {
  gorevEkleService, gorevTamamlaService, gorevSilService,
} from "./services/taskService";
import {
  urunEkleService, urunleriTopluEkleService, urunSilService, alindiDegistirService,
} from "./services/shoppingService";
import {
  harcamaEkleService, harcamaSilService,
} from "./services/sharedExpenseService";
import { requestAndSaveNotificationToken } from "./services/notificationService";
import { konumGuncelleService, nativeBolgeListesiGetirService } from "./services/haritaService";
import {
  getCurrentLocationOnce as nativeGetLocationOnce,
  hasNativeLocationPermission,
  isNativeLocationSharingRunning,
  isNativeLocationAvailable,
  setBackgroundLocationContext,
  setNativeBackgroundLocationSharingEnabled,
  syncNativeBackgroundLocationAuth,
  startNativeLocationSharing,
  stopNativeLocationSharing,
} from "./services/nativeLocationService";
import { bildirimHerkeseYazService, bildirimEkleService, bildirimTakvimTopluAktarimService } from "./services/bildirimService";
import { zamanliBildirimOlustur } from "./services/scheduledNotificationService";
import { trackEngagementPageView } from "./services/engagementService";
import { apiRequest, getAccessToken, getRefreshToken } from "./services/apiClient";
import { formatFamilyInviteInput } from "./services/familyInviteService";
import {
  getAppReviewEligibility,
  recordAppUsageDay,
  refreshAppReviewEligibility,
  requestNativeAppReview,
  subscribeAppReviewUsage,
} from "./services/appReviewService";
import {
  getStoredThemePreference,
  getSystemTheme,
  SYSTEM_THEME,
} from "./utils/themePreference";
import { claimBirthdayExperience, findBirthdayOccurrence, getUpcomingBirthdays, resolveBirthdayOccurrence } from "./services/birthdayService";
import { trackCoreAction, trackScreenView } from "./services/analyticsService";

/* ── Bileşenler ── */
import Header             from "./components/Header";
import SideMenu           from "./components/SideMenu";
import UyeModal           from "./components/UyeModal";
import InAppNotification  from "./components/InAppNotification";
import BildirimPopup      from "./components/BildirimPopup";
import OfflineBanner      from "./components/OfflineBanner";
import AccountDeletionSurvey from "./components/AccountDeletionSurvey";
import AuthPage           from "./components/auth/AuthPage";
import ResetPasswordPage  from "./components/auth/ResetPasswordPage";
import VerifyEmailPage    from "./components/auth/VerifyEmailPage";
import PremiumPopup       from "./components/PremiumPopup";
import GunlukHatirlaticiPopup from "./components/GunlukHatirlaticiPopup";
import PremiumStartupSplash from "./components/PremiumStartupSplash";
import KonfetiCanvas from "./components/KonfetiCanvas";
import AppTour from "./components/AppTour";
import BirthdayCelebrationScreen from "./components/BirthdayCelebrationScreen";
import BirthdayDetailSheet from "./components/BirthdayDetailSheet";

/* ── Hoşgeldin popup asset PNG'leri ── */
import wpHarita    from "./assets/aileharitasiasset.png";
import wpCocuk     from "./assets/cocuktakibiasset.png";
import wpFinans    from "./assets/finansasset.png";
import wpAsistan   from "./assets/aileasistaniasset.png";
import wpBelgeler  from "./assets/belgelerasset.png";
import wpPisirsem  from "./assets/nepisirsemasset.png";
import wpAlisveris from "./assets/alisverisasset.png";
import wpHarcama   from "./assets/harcamalarasset.png";

/* ── Sayfalar ── */
import HomePage           from "./pages/HomePage";
import { totalChildTrackingRecordCount } from "./utils/childTrackingCounts";
import ShoppingPage       from "./pages/ShoppingPage";
import TasksPage          from "./pages/TasksPage";
import FinancePage        from "./pages/FinancePage";
import SharedExpensesPage from "./pages/SharedExpensesPage";
import DocumentsPage      from "./pages/DocumentsPage";
import CalendarPage       from "./pages/CalendarPage";
import CalendarIntegrationSettingsPage from "./pages/CalendarIntegrationSettingsPage";
import HealthPage         from "./pages/HealthPage";
import ChildrenPage       from "./pages/ChildrenPage";
import ChatPage           from "./pages/ChatPage";
import NePisirsamPage     from "./pages/NePisirsamPage";
import UserManagementPage from "./pages/UserManagementPage";
import HaritaPage         from "./pages/HaritaPage";
import ActivityFeedPage   from "./pages/ActivityFeedPage";
import AssistantPage      from "./pages/AssistantPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import LegalDocumentPage from "./pages/legal/LegalDocumentPage";
import FeedbackPage from "./pages/FeedbackPage";
import { getCalendarIntegrationPreferences, readDeviceCalendarEvents, saveCalendarIntegrationPreferences } from "./services/externalCalendarService";

/* ── Utils ── */
import {
  gelirKategorileri, giderKategorileri,
  harcamaKategorileri, takvimRenkleri,
} from "./utils/constants";
import { yasHesapla } from "./utils/formatters";
import { financeAmountInBase } from "./utils/financeCurrency";
import { getGlobalAssetPricesOnce } from "./services/financeToolsService";

/* ═══════════════════════════════════════════════════════════
   ANA APP COMPONENT
═══════════════════════════════════════════════════════════ */

const EMPTY_EMAIL_ACTION = { resetPasswordToken: "", verifyEmailToken: "" };

const APP_PAGE_PERMISSION_KEYS = {
  ana: "home", alisveris: "shopping", takvim: "calendar", finans: "finance",
  gorev: "tasks", harcama: "sharedExpenses", saglik: "health", harita: "harita",
  cocuk: "children", chat: "chat", belgeler: "documents", nePisirsam: "nePisirsam",
  activity: "activity", assistant: "assistant", kullaniciYonetimi: "userManagement",
};

/* Ana sayfa ve menüdeki 14 uygulama sayfası aynı native status-bar korumasını
   paylaşır. Alt sayfalar aktif sayfa anahtarını koruduğu için ayrıca listelenmez. */
const STATUS_BAR_GUARD_PAGES = new Set(Object.keys(APP_PAGE_PERMISSION_KEYS));

const familySnapshotAllowsPage = (snapshot, pageKey) => {
  const role = String(snapshot?.rol || snapshot?.role || snapshot?.aileYetkisi || "").trim().toLowerCase();
  if (["aile yöneticisi", "aile yoneticisi", "family admin", "admin", "owner", "yönetici", "yonetici"].includes(role)) return true;
  return snapshot?.permissions?.pages?.[pageKey] !== false;
};

function parseEmailActionUrl(rawUrl) {
  try {
    const fallbackBase = typeof window !== "undefined" ? window.location.origin : "https://app.aileyonetimi.com";
    const url = new URL(String(rawUrl || ""), fallbackBase);
    const path = String(url.pathname || "").toLowerCase();
    const hash = String(url.hash || "");
    const hashQuery = hash.includes("?") ? hash.slice(hash.indexOf("?")) : "";
    const hashParams = new URLSearchParams(hashQuery);
    const action = String(url.searchParams.get("action") || hashParams.get("action") || "").trim();
    const token = String(
      url.searchParams.get("token") ||
      url.searchParams.get("resetToken") ||
      url.searchParams.get("passwordResetToken") ||
      url.searchParams.get("verifyToken") ||
      hashParams.get("token") ||
      hashParams.get("resetToken") ||
      hashParams.get("passwordResetToken") ||
      hashParams.get("verifyToken") ||
      ""
    ).trim();

    const isVerify = action === "verify-email" || path.endsWith("/deep-link/verify-email");
    if (isVerify) return { ...EMPTY_EMAIL_ACTION, verifyEmailToken: token };

    const isReset = action === "reset-password"
      || path === "/reset-password"
      || path.endsWith("/deep-link/reset-password")
      || hash.startsWith("#/reset-password");
    if (isReset || token) return { ...EMPTY_EMAIL_ACTION, resetPasswordToken: token };
  } catch {}
  return { ...EMPTY_EMAIL_ACTION };
}

function MainApp() {
  const webPageClass = Capacitor.isNativePlatform() ? "" : " desktopWebPage";

  /* ── i18n ── */
  const { setLang, t, lang } = useLanguage();
  const defaultCurrencyForLang = (value) => (value === "en" ? "USD" : "TRY");

  /* ── Premium başlangıç splash akışı ──
     Native splash React hazır olana kadar açık kalır; siyah ekranı engellemek için
     uygulama içi animated splash görünürken manuel olarak kapatılır. */
  const [startupSplashVisible, setStartupSplashVisible] = useState(true);
  const [loginStartupVisible, setLoginStartupVisible] = useState(false);
  const [loginStartupReady, setLoginStartupReady] = useState(false);
  const [loginStartupRequiresReady, setLoginStartupRequiresReady] = useState(false);
  const [loginHydrationUid, setLoginHydrationUid] = useState("");
  const [loginSplashRunKey, setLoginSplashRunKey] = useState(0);
  // MADDE 14: Aile değiştirilirken (aynı kullanıcı, farklı aile) veri yeniden
  // yüklenene kadar ~1 sn boş arka plan görünüyordu. Bu state, o boşlukta
  // premium startup animasyonunu gösterir. loginStartup mekanizmasından ayrıdır
  // (o kullanıcı-UID bazlı; bu aile-ID bazlı) ve iOS/web'i de aynı şekilde kapsar.
  const [aileGecisSplashVisible, setAileGecisSplashVisible] = useState(false);
  const [aileGecisHedefId, setAileGecisHedefId] = useState("");
  const [aileGecisRunKey, setAileGecisRunKey] = useState(0);
  const [aileGecisCikabilir, setAileGecisCikabilir] = useState(false);
  const [aileGecisIcerikGoster, setAileGecisIcerikGoster] = useState(false);
  const [webSocialPopupPending, setWebSocialPopupPending] = useState(false);
  const nativeSplashHiddenRef = useRef(false);
  const startupSplashVisibleRef = useRef(true);

  useEffect(() => {
    startupSplashVisibleRef.current = startupSplashVisible;
  }, [startupSplashVisible]);

  const handleStartupVisualReady = useCallback(() => {
    if (!Capacitor.isNativePlatform() || nativeSplashHiddenRef.current) return;
    nativeSplashHiddenRef.current = true;
    SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => {
      // Geçici bir native eklenti hatasında sonraki hazır sinyaline izin ver.
      nativeSplashHiddenRef.current = false;
    });
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let disposed = false;
    let listenerHandle = null;

    const registerForegroundRecovery = async () => {
      listenerHandle = await CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        // launchAutoHide=false olduğu için işletim sisteminin uzun arka plan
        // sonrasında yeniden oluşturabildiği native splash katmanı kendiliğinden
        // kapanmaz. Gerçek cold-start animasyonu sürerken dokunma; animasyon bir
        // kez tamamlandıysa foreground dönüşünde olası yetim katmanı kaldır.
        if (!isActive || startupSplashVisibleRef.current) return;
        SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => {});
      });

      if (disposed) listenerHandle?.remove?.();
    };

    registerForegroundRecovery().catch(() => {});
    return () => {
      disposed = true;
      listenerHandle?.remove?.();
    };
  }, []);

  const premiumStartupVisible = startupSplashVisible || loginStartupVisible || aileGecisSplashVisible;
  const startupIcerikHazirlaniyor =
    startupSplashVisible ||
    loginStartupVisible ||
    (aileGecisSplashVisible && !aileGecisIcerikGoster);

  const withStartupSplash = (content) => (
    <>
      <div className={startupIcerikHazirlaniyor ? "startupContent startupContentPreparing" : "startupContent startupContentReady"}>
        {content}
      </div>
      <PremiumStartupSplash
        visible={premiumStartupVisible}
        animationKey={
          startupSplashVisible ? "initial-startup"
          : loginStartupVisible ? loginSplashRunKey
          : aileGecisSplashVisible ? aileGecisRunKey
          : 0
        }
        variant={!startupSplashVisible && aileGecisSplashVisible ? "family-switch" : "default"}
        minimumDurationMs={startupSplashVisible ? 3000 : 2000}
        canLeave={startupSplashVisible ? true : (aileGecisSplashVisible ? aileGecisCikabilir : (loginStartupVisible && loginStartupRequiresReady ? loginStartupReady : true))}
        onVisualReady={startupSplashVisible ? handleStartupVisualReady : undefined}
        onLeaveStart={!startupSplashVisible && aileGecisSplashVisible ? () => setAileGecisIcerikGoster(true) : undefined}
        onLeaveComplete={startupSplashVisible ? () => {
          setStartupSplashVisible(false);
        } : aileGecisSplashVisible ? () => {
          setAileGecisSplashVisible(false);
          setAileGecisHedefId("");
          setAileGecisCikabilir(false);
          setAileGecisIcerikGoster(false);
        } : loginStartupVisible ? () => {
          setLoginStartupVisible(false);
          setLoginStartupReady(false);
          setLoginStartupRequiresReady(false);
          setLoginHydrationUid("");
        } : undefined}
      />
    </>
  );

  const [emailAction, setEmailAction] = useState(() => parseEmailActionUrl(window.location.href));
  const { resetPasswordToken, verifyEmailToken } = emailAction;

  const clearEmailAction = useCallback(() => {
    setEmailAction({ ...EMPTY_EMAIL_ACTION });
    try { window.history.replaceState({}, "", "/"); } catch {}
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let disposed = false;
    let listenerHandle = null;
    const acceptNativeUrl = (rawUrl) => {
      const parsed = parseEmailActionUrl(rawUrl);
      if (!parsed.resetPasswordToken && !parsed.verifyEmailToken) return;
      setEmailAction(parsed);
      // Token native state'e alındıktan sonra WebView adres çubuğu/geçmişinde
      // tutulmaz. Backend token doğrulama mantığı aynen devam eder.
      try { window.history.replaceState({}, "", "/"); } catch {}
    };

    const setupDeepLinks = async () => {
      try {
        const handle = await CapacitorApp.addListener("appUrlOpen", ({ url }) => acceptNativeUrl(url));
        if (disposed) handle?.remove?.();
        else listenerHandle = handle;

        const launch = await CapacitorApp.getLaunchUrl();
        if (!disposed && launch?.url) acceptNativeUrl(launch.url);
      } catch (error) {
        console.warn("[DeepLink] E-posta aksiyon bağlantısı dinlenemedi:", error?.message || error);
      }
    };

    setupDeepLinks();
    return () => {
      disposed = true;
      listenerHandle?.remove?.();
    };
  }, []);

  /* ── Sayfa navigasyon state'i ── */
  const [aktifSayfa, setAktifSayfa]             = useState(() => normalizeAndroidPage(localStorage.getItem("varsayilanSayfa") || "ana"));
  const [menuAcik, setMenuAcik]                 = useState(false);
  const [ayarlarAcik, setAyarlarAcik]           = useState(false);
  const [chatRoomAcik, setChatRoomAcik]         = useState(false);
  const [healthDetailAcik, setHealthDetailAcik] = useState(false);
  const [childrenDetailAcik, setChildrenDetailAcik] = useState(false);
  const [documentsDetailAcik, setDocumentsDetailAcik] = useState(false);
  const [calendarIntegrationOpen, setCalendarIntegrationOpen] = useState(false);
  const [externalCalendarEvents, setExternalCalendarEvents] = useState([]);
  const [calendarIntegrationRevision, setCalendarIntegrationRevision] = useState(0);
  const [externalCalendarRefreshing, setExternalCalendarRefreshing] = useState(false);
  const [externalCalendarImporting, setExternalCalendarImporting] = useState(false);
  const [legalDocumentKey, setLegalDocumentKey] = useState("privacy");
  const [legalTransitionDirection, setLegalTransitionDirection] = useState("left");
  const [compactShoppingViewport, setCompactShoppingViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    const viewport = window.visualViewport;
    const width = viewport?.width || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    return width <= 360 || height <= 720;
  });
  const [compactCalendarViewport, setCompactCalendarViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    const viewport = window.visualViewport;
    const width = viewport?.width || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    return width <= 360 || height <= 720;
  });
  const [compactTasksViewport, setCompactTasksViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    const viewport = window.visualViewport;
    const width = viewport?.width || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    return width <= 360 || height <= 720;
  });

  const aktifSayfaRef  = useRef(aktifSayfa);
  const pagePermissionCheckRef = useRef(() => true);
  const menuAcikRef    = useRef(false);
  const calendarIntegrationOpenRef = useRef(false);
  const calendarIntegrationDirtyRef = useRef(false);
  // Konum paylaşım durumunu ref olarak tut — setInterval useEffect'i
  // her SSE güncellemesinde yeniden kurulmasın (checkPermissions döngüsünü önler)
  const globalLocationImmediateWriteRef = useRef("");
  const globalNativeRegionsSyncRef = useRef("");
  const globalNativeLocationContextRef = useRef("");
  const documentsDetailAcikRef = useRef(false);
  const phoneContainerRef = useRef(null);
  // Native iOS swipe-back için DOM ref'leri
  const activePageRef  = useRef(null); // sürüklenen sayfa wrapper'ı
  const edgeShadowRef  = useRef(null); // sol kenar gölge overlay
  const bgInnerRef     = useRef(null); // swipeBgLayer iç wrapper — scroll sync

  // İç içe sayfa swipe-back için: hangi bg içeriği gösterileceği
  const [swipeBgMode, setSwipeBgMode] = useState(null); // null | "ana" | sayfa adı
  const swipeBgModeRef = useRef("ana");
  const assistantReturnPageRef = useRef(null);
  const scrollResetHandlesRef = useRef([]);
  // İlk sayfa açılışında activePageRef henüz yoktur.
  // Bu ref, yeni sayfa mount olduktan sonra giriş animasyonunu garantiler.
  const pendingPageEnterAnimationRef = useRef(false);
  const reviewRequestInFlightRef = useRef(false);
  const lastReviewInteractionAtRef = useRef(0);
  const reviewCandidateTriggerRef = useRef("");
  const [reviewEligibilityTick, setReviewEligibilityTick] = useState(0);

  const calendarIntegrationPreferencesChanged = useCallback(() => {
    // Tercih değişikliği sayfayı yeniden mount etmez. Ana takvime dönüldüğünde
    // cihaz etkinlikleri yeni seçimlerle yalnızca bir kez okunur.
    calendarIntegrationDirtyRef.current = true;
  }, []);

  const closeCalendarIntegration = useCallback(() => {
    setCalendarIntegrationOpen(false);
    if (calendarIntegrationDirtyRef.current) {
      calendarIntegrationDirtyRef.current = false;
      setCalendarIntegrationRevision((value) => value + 1);
    }
  }, []);

  const sayfaEnUsteAl = () => {
    let iptal = false;

    const reset = () => {
      if (iptal) return;
      try {
        const scrollingEl = document.scrollingElement || document.documentElement;
        if (scrollingEl) {
          scrollingEl.scrollTop = 0;
          scrollingEl.scrollLeft = 0;
        }
        document.documentElement.scrollTop = 0;
        document.documentElement.scrollLeft = 0;
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });

        // Bazı sayfalar kendi içinde scroll container oluşturuyor.
        // Swipe-to-back transform yapısına dokunmadan yalnızca scroll pozisyonları sıfırlanır.
        [
          phoneContainerRef.current,
          activePageRef.current,
          document.querySelector(".swipePageWrap"),
          document.querySelector(".page"),
          document.querySelector(".pageContent"),
        ].forEach((el) => {
          if (el && typeof el.scrollTo === "function") {
            el.scrollTo({ top: 0, left: 0, behavior: "auto" });
          } else if (el) {
            el.scrollTop = 0;
            el.scrollLeft = 0;
          }
        });
      } catch {
        window.scrollTo(0, 0);
      }
    };

    scrollResetHandlesRef.current.forEach((handle) => {
      if (handle?.type === "raf") cancelAnimationFrame(handle.id);
      if (handle?.type === "timeout") clearTimeout(handle.id);
      if (handle?.type === "listener") handle.cleanup?.();
    });
    scrollResetHandlesRef.current = [];

    const kullaniciScrollBaslatti = () => {
      iptal = true;
    };

    window.addEventListener("wheel", kullaniciScrollBaslatti, { passive: true, once: true });
    window.addEventListener("touchmove", kullaniciScrollBaslatti, { passive: true, once: true });
    scrollResetHandlesRef.current.push({
      type: "listener",
      cleanup: () => {
        window.removeEventListener("wheel", kullaniciScrollBaslatti);
        window.removeEventListener("touchmove", kullaniciScrollBaslatti);
      },
    });

    reset();

    // iOS/Safari bazı sayfalarda içerik boyu sonradan değişince eski ana sayfa
    // scroll offset'ini tekrar uygulayabiliyor. Kısa ve güvenli bir pencere boyunca
    // sıfırlama tekrarlanır; kullanıcı scroll'a başlarsa hemen bırakılır.
    const startedAt = Date.now();
    const rafLoop = () => {
      if (iptal || Date.now() - startedAt > 1100) return;
      reset();
      const id = requestAnimationFrame(rafLoop);
      scrollResetHandlesRef.current.push({ type: "raf", id });
    };
    const rafId = requestAnimationFrame(rafLoop);
    scrollResetHandlesRef.current.push({ type: "raf", id: rafId });

    [40, 120, 260, 420, 650, 900, 1150].forEach((ms) => {
      const id = setTimeout(reset, ms);
      scrollResetHandlesRef.current.push({ type: "timeout", id });
    });

    const cleanupId = setTimeout(() => {
      iptal = true;
      window.removeEventListener("wheel", kullaniciScrollBaslatti);
      window.removeEventListener("touchmove", kullaniciScrollBaslatti);
    }, 1250);
    scrollResetHandlesRef.current.push({ type: "timeout", id: cleanupId });
  };

  useLayoutEffect(() => {
    // Ana sayfadan herhangi bir sayfaya geçerken eski body scroll offset'i
    // yeni sayfaya taşınmasın. Swipe-to-back gesture koduna dokunmadan,
    // sadece aktif sayfa değiştiği anda scroll pozisyonu sıfırlanır.
    sayfaEnUsteAl();
    return () => {
      scrollResetHandlesRef.current.forEach((handle) => {
        if (handle?.type === "raf") cancelAnimationFrame(handle.id);
        if (handle?.type === "timeout") clearTimeout(handle.id);
      });
      scrollResetHandlesRef.current = [];
    };
  }, [aktifSayfa]); // eslint-disable-line

  useEffect(() => { aktifSayfaRef.current = aktifSayfa; }, [aktifSayfa]);

  useEffect(() => {
    if (aktifSayfa !== "alisveris") return undefined;

    const updateCompactShoppingViewport = () => {
      const viewport = window.visualViewport;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      setCompactShoppingViewport(width <= 360 || height <= 720);
    };

    updateCompactShoppingViewport();
    window.addEventListener("resize", updateCompactShoppingViewport);
    window.addEventListener("orientationchange", updateCompactShoppingViewport);
    window.visualViewport?.addEventListener("resize", updateCompactShoppingViewport);

    return () => {
      window.removeEventListener("resize", updateCompactShoppingViewport);
      window.removeEventListener("orientationchange", updateCompactShoppingViewport);
      window.visualViewport?.removeEventListener("resize", updateCompactShoppingViewport);
    };
  }, [aktifSayfa]);

  useEffect(() => {
    if (aktifSayfa !== "takvim" || calendarIntegrationOpen) return undefined;

    const updateCompactCalendarViewport = () => {
      const viewport = window.visualViewport;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      setCompactCalendarViewport(width <= 360 || height <= 720);
    };

    updateCompactCalendarViewport();
    window.addEventListener("resize", updateCompactCalendarViewport);
    window.addEventListener("orientationchange", updateCompactCalendarViewport);
    window.visualViewport?.addEventListener("resize", updateCompactCalendarViewport);

    return () => {
      window.removeEventListener("resize", updateCompactCalendarViewport);
      window.removeEventListener("orientationchange", updateCompactCalendarViewport);
      window.visualViewport?.removeEventListener("resize", updateCompactCalendarViewport);
    };
  }, [aktifSayfa, calendarIntegrationOpen]);

  useEffect(() => {
    if (aktifSayfa !== "gorev") return undefined;

    const updateCompactTasksViewport = () => {
      const viewport = window.visualViewport;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      setCompactTasksViewport(width <= 360 || height <= 720);
    };

    updateCompactTasksViewport();
    window.addEventListener("resize", updateCompactTasksViewport);
    window.addEventListener("orientationchange", updateCompactTasksViewport);
    window.visualViewport?.addEventListener("resize", updateCompactTasksViewport);

    return () => {
      window.removeEventListener("resize", updateCompactTasksViewport);
      window.removeEventListener("orientationchange", updateCompactTasksViewport);
      window.visualViewport?.removeEventListener("resize", updateCompactTasksViewport);
    };
  }, [aktifSayfa]);

  useEffect(() => {
    trackScreenView(aktifSayfa);
  }, [aktifSayfa]);

  useEffect(() => {
    const scrollLockSayfalari = {
      alisveris: { className: "shopping-scroll-lock", allowedScrollSelector: ".shoppingItemsCard" },
      gorev: { className: "tasks-scroll-lock", allowedScrollSelector: ".tasksListCard, .taskCreatePremiumBody" },
      takvim: { className: "calendar-scroll-lock", allowedScrollSelector: ".calUpcoming, .calModalCard, .calYillikGrid" },
    };
    // Entegrasyon ayarları aynı "takvim" rotasında açılır ancak uzun bir ayar
    // sayfasıdır. Ana takvimin sabit ekran kilidini yalnızca takvim görünümünde tut.
    const lockConfig = (aktifSayfa === "takvim" && calendarIntegrationOpen)
      || (aktifSayfa === "alisveris" && compactShoppingViewport)
      || (aktifSayfa === "takvim" && compactCalendarViewport)
      || (aktifSayfa === "gorev" && compactTasksViewport)
      ? null
      : scrollLockSayfalari[aktifSayfa];
    if (!lockConfig) return undefined;

    const root = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const previous = {
      rootOverflow: root.style.overflow,
      rootHeight: root.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    root.classList.add(lockConfig.className);
    body.classList.add(lockConfig.className);
    root.style.overflow = "hidden";
    root.style.height = "100dvh";
    body.style.overflow = "hidden";
    body.style.height = "100dvh";
    // body.position:"fixed" + body.top:"-scrollYpx" sayfayı viewport dışına itebilir.
    // sayfaEnUsteAl() async çalıştığı için scrollY henüz 0 olmayabilir.
    // Scroll-lock sayfalarında (takvim, alışveriş, görev) scroll pozisyonunu
    // restore etmek gereksiz; her zaman "0px" kullan.
    body.style.position = "fixed";
    body.style.top = "0px";
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    const preventOuterPageScroll = (event) => {
      const target = event.target;
      if (target?.closest?.(lockConfig.allowedScrollSelector)) return;
      if (target?.closest?.("input, textarea, select, button")) return;
      event.preventDefault();
    };

    document.addEventListener("touchmove", preventOuterPageScroll, { passive: false });

    return () => {
      document.removeEventListener("touchmove", preventOuterPageScroll);
      root.classList.remove(lockConfig.className);
      body.classList.remove(lockConfig.className);
      root.style.overflow = previous.rootOverflow;
      root.style.height = previous.rootHeight;
      body.style.overflow = previous.bodyOverflow;
      body.style.height = previous.bodyHeight;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [aktifSayfa, calendarIntegrationOpen, compactCalendarViewport, compactShoppingViewport, compactTasksViewport]);

  useEffect(() => { menuAcikRef.current   = menuAcik;   }, [menuAcik]);
  useEffect(() => { calendarIntegrationOpenRef.current = calendarIntegrationOpen; }, [calendarIntegrationOpen]);
  useEffect(() => { documentsDetailAcikRef.current = documentsDetailAcik; }, [documentsDetailAcik]);

  /* ── Ayarlar ── */
  const [temaTercihi, setTemaTercihi] = useState(getStoredThemePreference);
  const [sistemTemasi, setSistemTemasi] = useState(getSystemTheme);
  const tema = temaTercihi === SYSTEM_THEME ? sistemTemasi : temaTercihi;

  // Mevcut tema düğmelerinin kullandığı setter. Bu fonksiyon yalnızca kullanıcı
  // bir düğmeye bastığında çağrıldığı için seçim bundan sonra kalıcı hale gelir.
  const setTema = useCallback((yeniTema) => {
    if (yeniTema !== "light" && yeniTema !== "dark") return;
    try { localStorage.setItem("tema", yeniTema); } catch (_) {}
    setTemaTercihi(yeniTema);
  }, []);

  // Henüz uygulama içinden seçim yapılmadıysa işletim sisteminin gündüz/gece
  // değişimini uygulama açıkken de anında takip et.
  useEffect(() => {
    if (temaTercihi !== SYSTEM_THEME || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sistemiUygula = (event) => setSistemTemasi(event.matches ? "dark" : "light");
    setSistemTemasi(media.matches ? "dark" : "light");

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sistemiUygula);
      return () => media.removeEventListener("change", sistemiUygula);
    }

    media.addListener(sistemiUygula);
    return () => media.removeListener(sistemiUygula);
  }, [temaTercihi]);

  useEffect(() => {
    const configureNativeStatusBar = async () => {
      if (!Capacitor.isNativePlatform()) return;

      try {
        // iOS safe-area değişkenlerinin doğru çalışması için WebView viewport'u tam ekran kabul etmeli.
        const viewportMeta = document.querySelector("meta[name='viewport']");
        const viewportContent = viewportMeta?.getAttribute("content") || "";
        if (viewportMeta && !viewportContent.includes("viewport-fit=cover")) {
          viewportMeta.setAttribute(
            "content",
            viewportContent ? `${viewportContent}, viewport-fit=cover` : "width=device-width, initial-scale=1, viewport-fit=cover"
          );
        }

        if (Capacitor.getPlatform() === "android") {
          // ── ANDROID (MADDE 4) ────────────────────────────────────
          // Android'de sistem çubukları BURADAN YÖNETİLMEZ.
          //
          // Uygulama targetSdk 36 hedeflediği için Capacitor StatusBar plugin'inin
          // renk/overlay çağrıları Android tarafından yok sayılıyor; dahası bu
          // çağrılar native tarafta yaptığımız ayarı ezerek çubukların beyaz ya da
          // siyah kalmasına yol açıyordu (ekran görüntüsündeki sorun).
          //
          // Çubukların rengi, ikon parlaklığı ve pencere/WebView zemini artık
          // TAMAMEN native tarafta yönetiliyor:
          //   android/app/src/main/java/.../MainActivity.java  -> applyThemeColors()
          // Tema değiştiğinde ThemePreference plugin'i (aşağıdaki tema useEffect'i)
          // native tarafı anında haberdar eder ve renkler anında güncellenir.
          //
          // Burada bilerek hiçbir StatusBar çağrısı yapılmıyor — çakışmayı önlemek
          // için. iOS akışı aşağıdaki else dalında AYNEN korunuyor.
        } else {
          // ── iOS (mevcut çalışan davranış — DEĞİŞTİRİLMEDİ) ────────
          // overlay: true → status bar şeffaf, WebView tam ekranı kaplar.
          // CSS safe-area-inset-top ile içerik çentik altına iner.
          await StatusBar.setOverlaysWebView({ overlay: true });
          await StatusBar.setBackgroundColor({ color: "#00000000" });
          await StatusBar.setStyle({
            // Capacitor adları metin rengini değil arka planı tarif eder:
            // Style.Light açık zeminde koyu, Style.Dark koyu zeminde açık ikon kullanır.
            style: tema === "light" ? Style.Light : Style.Dark,
          });
        }
      } catch (error) {
        console.warn("Status bar tema ayarlanamadı:", error);
      }
    };

    configureNativeStatusBar();
  }, [tema]);
  const [paraBirimi, setParaBirimiState] = useState(() => localStorage.getItem("paraBirimi") || defaultCurrencyForLang(lang));
  const [bildirimAcik, setBildirimAcik] = useState(() => localStorage.getItem("bildirimAcik") !== "false");
  const [bildirimGun, setBildirimGun]   = useState(() => Number(localStorage.getItem("bildirimGun")) || 3);
  const [varsayilanSayfa, setVarsayilanSayfa] = useState(() => normalizeAndroidPage(localStorage.getItem("varsayilanSayfa") || "ana"));
  const [adminMode, setAdminMode] = useState(false);
  const [alisverisHatirlatici, setAlisverisHatirlatici] = useState(() => localStorage.getItem("alisverisHatirlatici") !== "false");
  const [noFamilyDeleteOpen, setNoFamilyDeleteOpen] = useState(false);
  const [noFamilyDeletePassword, setNoFamilyDeletePassword] = useState("");
  const [noFamilyDeleteLoading, setNoFamilyDeleteLoading] = useState(false);
  const [noFamilyDeleteReasons, setNoFamilyDeleteReasons] = useState([]);
  const [noFamilyDeleteOtherText, setNoFamilyDeleteOtherText] = useState("");
  const [noFamilyDeleteSurveyComplete, setNoFamilyDeleteSurveyComplete] = useState(false);
  const noFamilyOnboardingStartedAtRef = useRef(null);
  const noFamilyDeleteOwnerRef = useRef(null);
  const resetNoFamilyDelete = useCallback(() => {
    setNoFamilyDeleteOpen(false);
    setNoFamilyDeletePassword("");
    setNoFamilyDeleteLoading(false);
    setNoFamilyDeleteReasons([]);
    setNoFamilyDeleteOtherText("");
    setNoFamilyDeleteSurveyComplete(false);
  }, []);

  /* Ayarları localStorage'a yaz */
  useEffect(() => {
    // Çözümlenmiş sistem temasını localStorage'a yazma. Aksi halde yeni kullanıcı
    // ilk açılışta yanlışlıkla kalıcı açık/koyu tercihi yapmış sayılır.
    try {
      if (temaTercihi === SYSTEM_THEME) localStorage.removeItem("tema");
      else localStorage.setItem("tema", temaTercihi);
    } catch (_) {}
    document.body.dataset.tema = tema;

    // MADDE 11: Native <select> açılır listesini işletim sistemi çizer; CSS ile
    // doğrudan boyanamaz. Chrome/Android WebView bu listeyi `color-scheme`
    // değerine göre koyu veya açık çizer. Önceki sürümde bu yalnızca <body>
    // üzerine yazılıyordu; tarayıcılar form kontrolleri için KÖK elemanı
    // (<html>) dikkate aldığından açılır menüler koyu temada beyaz kalıyordu.
    // Artık hem <html> hem <body> üzerine yazıyoruz.
    const scheme = tema === "light" ? "light" : "dark";
    document.documentElement.style.colorScheme = scheme;
    document.body.style.colorScheme = scheme;
    document.documentElement.dataset.tema = tema;

    // MADDE 13: Android native açılış ekranı (splash) CİHAZIN sistem temasını
    // takip eder, uygulama içi tema anahtarını değil. Bu yüzden açık tema
    // kullanan kullanıcı, sistemi koyu olduğunda açılışta koyu splash görüyordu.
    // Seçilen temayı native tarafa kaydediyoruz; MainActivity bir sonraki
    // açılışta gece modunu buna göre ayarlayarak doğru splash'i gösterir.
    // Plugin yoksa (iOS/web) sessizce atlanır — iOS davranışı değişmez.
    try {
      const ThemePreference = window?.Capacitor?.Plugins?.ThemePreference;
      if (ThemePreference && ThemePreference.set) {
        ThemePreference.set({ theme: temaTercihi, resolvedTheme: scheme });
      }
    } catch (_) { /* tema tercihi kaydedilemedi — kritik değil */ }

    /* ═══════════════════════════════════════════════════════════════
       MADDE 1 — KLAVYE ARKA PLANI TEMAYA UYSUN (iOS + ANDROID)

       SORUN:
       Açık temada bile klavye (ve klavyenin arkasındaki şerit) uygulamanın
       KOYU zemin renginde (#1f2852) kalıyordu.

       İKİ AYRI KATMAN VAR, İKİSİ DE DÜZELTİLİYOR:

       1) KLAVYENİN KENDİ GÖRÜNÜMÜ (iOS)
          WKWebView klavyeyi sistem çizer; CSS ile boyanamaz. Doğru yöntem
          Capacitor Keyboard eklentisinin setStyle() çağrısıdır. Ayrıca
          AppDelegate tarafında pencereye overrideUserInterfaceStyle veriliyor
          (aşağıdaki köprü mesajı) — böylece klavye, paylaşım sayfası ve diğer
          native bileşenler de doğru tonda açılır.

       2) KLAVYENİN ARKASINDAKİ ŞERİT (iOS + Android)
          Bu şerit WebView/pencere zeminidir ve native tarafta sabit koyu
          renkti. Tema değişiminde native tarafa bildiriliyor:
            • iOS     → aileNativeBridge { action: "setAppTheme" }
            • Android → ThemePreference plugin (yukarıda) + MainActivity

       Web tarafında (tarayıcı) hiçbiri mevcut değildir; sessizce atlanır.
       Sunucuya hiçbir istek üretmez.
       ═══════════════════════════════════════════════════════════════ */
    try {
      if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
        Keyboard.setStyle({
          style: scheme === "light" ? KeyboardStyle.Light : KeyboardStyle.Dark,
        }).catch(() => { /* eklenti yoksa/desteklemiyorsa yok say */ });
      }
    } catch (_) { /* klavye stili uygulanamadı — kritik değil */ }

    try {
      const bridge = window?.webkit?.messageHandlers?.aileNativeBridge;
      if (bridge && bridge.postMessage) {
        bridge.postMessage({ action: "setAppTheme", theme: temaTercihi, resolvedTheme: scheme });
      }
    } catch (_) { /* iOS köprüsü yok — kritik değil */ }
  }, [tema, temaTercihi]);

  /* MADDE 1: iOS köprüsü uygulama açılışından ~1 sn sonra bağlanıyor.
     Bağlanmadan önce gönderilen tema mesajı kaybolacağı için, ilk kareden
     sonra tema bir kez daha native tarafa bildirilir. Tek seferlik ve
     tamamen yereldir; sunucuya istek yoktur. */
  useEffect(() => {
    if (!(Capacitor.isNativePlatform && Capacitor.isNativePlatform())) return undefined;
    const zamanlayici = setTimeout(() => {
      try {
        const scheme = tema === "light" ? "light" : "dark";
        const bridge = window?.webkit?.messageHandlers?.aileNativeBridge;
        if (bridge && bridge.postMessage) {
          bridge.postMessage({ action: "setAppTheme", theme: temaTercihi, resolvedTheme: scheme });
        }
        Keyboard.setStyle({
          style: scheme === "light" ? KeyboardStyle.Light : KeyboardStyle.Dark,
        }).catch(() => {});
      } catch (_) { /* yok say */ }
    }, 1500);
    return () => clearTimeout(zamanlayici);
  }, [tema, temaTercihi]);
  useEffect(() => { document.body.dataset.tema = tema; }, []); // eslint-disable-line

  /* FAZ 4A — Native arama köprüsü (iOS PushKit + CallKit).
     Uygulama kapalıyken gelen aramanın telefonu çaldırması için VoIP
     token'ının sunucuya kayıtlı olması gerekir. Köprü açılışta bir kez kurulur;
     iOS dışında hiçbir şey yapmaz. */
  useEffect(() => { nativeCallBridgeKur(); }, []);

  /* ═══════════════════════════════════════════════════════════════════
     MADDE 1 (İKİNCİ TUR) — iOS KLAVYE İÇERİK TELAFİSİ (UYGULAMA GENELİ)

     SORUN:
     capacitor.config.ts içinde `Keyboard.resize: 'none'` olduğu için iOS'ta
     klavye açıldığında WebView HİÇ küçülmüyor. Sohbet sayfası bunu kendi
     `visualViewport` akışıyla çözüyor; ancak DİĞER tüm sayfalarda telafi yok.
     Bu yüzden bir metin kutusuna odaklanınca içerik klavyenin ARKASINDA
     kalıyor (kullanıcının bildirdiği davranış).

     ÇÖZÜM:
     `visualViewport` ile klavye yüksekliği okunur ve KÖK `.page` elemanına
     alt boşluk (`--ios-kb-inset`) olarak verilir. Böylece sayfa içeriği
     klavyenin üstünde kalır ve odaklanılan alan görünür olur. Odaklanılan
     eleman gerekirse görünür alana kaydırılır.

     KAPSAM DIŞI:
       • Yalnızca iOS native kabında çalışır (Android'de sorun yok; oraya
         hiç dokunulmaz).
       • Sohbet sayfası (`chatPageActive`) KENDİ akışını kullandığı için bu
         telafi ORADA DEVREYE GİRMEZ — çakışmayı önlemek için.

     Sunucuya hiçbir istek üretmez; tamamen yerel bir yerleşim düzeltmesidir.
     ═══════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!isIOSPlatform()) return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const kok = document.documentElement;
    let rafId = null;

    const odakDuzenlenebilirMi = () => {
      const el = document.activeElement;
      if (!el) return false;
      const etiket = el.tagName;
      return etiket === "INPUT" || etiket === "TEXTAREA" || el.isContentEditable;
    };

    const uygula = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        /* Yalnızca TAM EKRAN SOHBET ODASI kendi klavye akışını kullanır.
           ÖNCEKİ HATA: dışlama `.page.chatPageActive` ile yapılıyordu; ama bu
           sınıf TÜM sohbet bölümüne (oda LİSTESİ sayfası dahil) uygulanıyor.
           Bu yüzden "Yeni Sohbet" gibi modallar bulunduğu sayfada hiç telafi
           almıyor, klavye modalın alt kısmını (butonlar) örtüyordu.
           Artık yalnızca gerçekten açık olan tam ekran oda dışlanıyor. */
        if (document.querySelector(".chatPage.chatRoomFull")) {
          kok.style.setProperty("--ios-kb-inset", "0px");
          return;
        }

        const layoutHeight = window.innerHeight || 0;
        const klavyeYuksekligi = Math.max(0, Math.round(layoutHeight - vv.height - vv.offsetTop));
        const acik = odakDuzenlenebilirMi() && klavyeYuksekligi > 80;

        kok.style.setProperty("--ios-kb-inset", acik ? `${klavyeYuksekligi}px` : "0px");

        // Odaklanılan alanı görünür alana getir (klavyenin arkasında kalmasın).
        if (acik) {
          const el = document.activeElement;
          if (el && typeof el.getBoundingClientRect === "function") {
            const kutu = el.getBoundingClientRect();
            const gorunurAlt = vv.offsetTop + vv.height;
            if (kutu.bottom > gorunurAlt - 12 || kutu.top < vv.offsetTop + 8) {
              try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) {}
            }
          }
        }
      });
    };

    const kapat = () => {
      kok.style.setProperty("--ios-kb-inset", "0px");
    };

    vv.addEventListener("resize", uygula);
    vv.addEventListener("scroll", uygula);
    document.addEventListener("focusin", uygula);
    document.addEventListener("focusout", kapat);
    window.addEventListener("orientationchange", uygula);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      vv.removeEventListener("resize", uygula);
      vv.removeEventListener("scroll", uygula);
      document.removeEventListener("focusin", uygula);
      document.removeEventListener("focusout", kapat);
      window.removeEventListener("orientationchange", uygula);
      kok.style.setProperty("--ios-kb-inset", "0px");
    };
  }, []);

  /* Aile yok ekranı: Keyboard.resize "none" kullanan iOS kabında sayfayı
     yalnızca gerçek klavye yüksekliği kadar küçült ve odaktaki alanı göster.
     Seçici bu ekrana özel olduğu için diğer sayfaların klavye akışı değişmez. */
  useEffect(() => {
    if (!isIOSPlatform()) return undefined;

    let iptalEdildi = false;
    let kaydirmaZamanlayicisi = null;
    const dinleyiciler = [];

    const sayfayiTemizle = () => {
      const sayfa = document.querySelector(".noFamilyPage");
      if (!sayfa) return;
      sayfa.classList.remove("noFamilyKeyboardOpen");
      sayfa.style.removeProperty("--no-family-keyboard-height");
    };

    const odaktakiAlaniGoster = () => {
      window.clearTimeout(kaydirmaZamanlayicisi);
      kaydirmaZamanlayicisi = window.setTimeout(() => {
        const sayfa = document.querySelector(".noFamilyPage");
        const alan = sayfa?.querySelector("input:focus, textarea:focus");
        if (!sayfa || !alan) return;
        try { alan.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) {}
      }, 80);
    };

    const klavyeAcildi = (bilgi) => {
      const sayfa = document.querySelector(".noFamilyPage");
      const yukseklik = Math.max(0, Math.round(Number(bilgi?.keyboardHeight) || 0));
      if (!sayfa || yukseklik <= 0) return;
      sayfa.style.setProperty("--no-family-keyboard-height", `${yukseklik}px`);
      sayfa.classList.add("noFamilyKeyboardOpen");
      odaktakiAlaniGoster();
    };

    const dinleyicileriKur = async () => {
      const acilis = await Keyboard.addListener("keyboardWillShow", klavyeAcildi);
      const kapanis = await Keyboard.addListener("keyboardWillHide", sayfayiTemizle);
      if (iptalEdildi) {
        acilis.remove();
        kapanis.remove();
        return;
      }
      dinleyiciler.push(acilis, kapanis);
    };

    dinleyicileriKur().catch(() => {});

    return () => {
      iptalEdildi = true;
      window.clearTimeout(kaydirmaZamanlayicisi);
      dinleyiciler.forEach((dinleyici) => dinleyici.remove());
      sayfayiTemizle();
    };
  }, []);

  const setParaBirimi = (value) => {
    localStorage.setItem("paraBirimiKullaniciSecimi", "true");
    setParaBirimiState(value);
  };
  useEffect(() => {
    const kullaniciSecimiVar = localStorage.getItem("paraBirimiKullaniciSecimi") === "true";
    if (!kullaniciSecimiVar) {
      setParaBirimiState(defaultCurrencyForLang(lang));
    }
  }, [lang]); // eslint-disable-line
  useEffect(() => { localStorage.setItem("paraBirimi", paraBirimi); }, [paraBirimi]);
  useEffect(() => { localStorage.setItem("bildirimAcik", bildirimAcik); }, [bildirimAcik]);
  useEffect(() => { localStorage.setItem("bildirimGun", bildirimGun); }, [bildirimGun]);
  useEffect(() => {
    const normalized = normalizeAndroidPage(varsayilanSayfa);
    if (normalized !== varsayilanSayfa) setVarsayilanSayfa(normalized);
    localStorage.setItem("varsayilanSayfa", normalized);
  }, [varsayilanSayfa]);
  useEffect(() => { localStorage.setItem("alisverisHatirlatici", alisverisHatirlatici); }, [alisverisHatirlatici]);

  /* ── Navigasyon (programatik) ── */
  const navigateTo = (sayfa) => {
    sayfa = normalizeAndroidPage(sayfa);
    if (sayfa !== "takvim" && calendarIntegrationOpenRef.current) closeCalendarIntegration();
    const permissionKey = APP_PAGE_PERMISSION_KEYS[sayfa];
    if (permissionKey && !pagePermissionCheckRef.current(permissionKey)) {
      if (aktifSayfaRef.current !== "ana") setAktifSayfa("ana");
      return;
    }
    if (sayfa !== aktifSayfaRef.current) {
      trackEngagementPageView({
        familyId,
        pageKey: sayfa,
        language: lang,
        metadata: { source: "navigateTo" }
      });
    }
    if (sayfa === aktifSayfaRef.current) return;
    const geriGidiyor = sayfa === "ana";
    const el = activePageRef.current;

    if (geriGidiyor) {
      // Footer'daki geri butonu da dahil TÜM programatik "ana sayfaya dön" çağrıları
      // artık swipe-to-back ile birebir aynı görsel mekanizmayı kullanıyor: aktif
      // sayfa kaydırılırken altında swipeBgLayer'daki (zaten önceden render edilmiş)
      // ana sayfa gerçek zamanlı görünür oluyor — eskiden bu katman hiç gösterilmeden
      // sadece üstteki sayfa kaybolup boş bir arka plan kalıyordu.
      setSwipeBgMode("ana");
      requestAnimationFrame(() => requestAnimationFrame(() => showBg()));
      snapComplete(0, () => { sayfaEnUsteAl(); setAktifSayfa(sayfa); }, true);
      return;
    }

    // İleri gitme (yeni bir sayfaya geçiş).
    // Ana sayfadan ilk geçişte activePageRef henüz mount edilmediği için
    // animasyonu burada değil, aktif sayfa DOM'a geldikten sonra useLayoutEffect içinde başlatıyoruz.
    const outClass = "nav-go-out";
    const outDur   = el ? 205 : 0;

    if (el) el.classList.add(outClass);

    // Forward navigasyon sırasında swipeBgLayer'ı geçici olarak göster.
    // nav-go-out (205ms) + nav-go-in (235ms) süresince sol tarafta .page gradient
    // görünmesini önler; swipeBgLayer ana sayfayı/önceki içeriği arkaplan olarak tutar.
    const showBgForForwardNav = () => {
      const bgEl = document.querySelector('.swipeBgLayer');
      if (bgEl) bgEl.style.visibility = 'visible';
    };
    const hideBgAfterForwardNav = () => {
      const bgEl = document.querySelector('.swipeBgLayer');
      if (bgEl) bgEl.style.visibility = '';
    };

    showBgForForwardNav();

    const commitForward = () => {
      if (el) {
        el.classList.remove(outClass);
        el.style.transform = "";
        el.style.opacity = "";
      }
      sayfaEnUsteAl();
      pendingPageEnterAnimationRef.current = true;
      setAktifSayfa(sayfa);
    };

    if (outDur > 0) setTimeout(commitForward, outDur);
    else commitForward();

    // nav-go-in animasyonu bittikten sonra bg layer'ı gizle
    const navTotalDur = outDur + 240;
    setTimeout(hideBgAfterForwardNav, navTotalDur);
  };

  useLayoutEffect(() => {
    if (aktifSayfa === "ana" || !pendingPageEnterAnimationRef.current) return undefined;

    pendingPageEnterAnimationRef.current = false;
    const el = activePageRef.current;
    if (!el) return undefined;

    const inClass = "nav-go-in";
    const inDur = 235;
    let timerId;

    el.classList.remove("nav-go-out", "nav-back-out", "nav-back-in", inClass);
    el.style.transform = "";
    el.style.opacity = "";

    // Bazı sayfalar (harcama, belgeler, nePisirsam, activity) kısa içerikli olduğunda
    // nav-go-in animasyonu sırasında sayfanın alt kısmında boşluk görünebilir.
    // Animasyon süresince geçici olarak tam ekran yüksekliği verip animasyon
    // bitince kaldırıyoruz — sayfa içeriği kendi doğal yüksekliğine döner.
    const shortPageKeys = ["harcama", "belgeler", "nePisirsam", "activity", "saglik", "kullaniciYonetimi", "feedback"];
    const needsHeightGuard = shortPageKeys.includes(aktifSayfa);
    if (needsHeightGuard) el.style.minHeight = "100dvh";

    // Safari/WKWebView ilk mount frame'inde class eklemeyi yutabiliyor;
    // reflow ile başlangıç keyframe'i garanti edilir.
    void el.offsetWidth;
    el.classList.add(inClass);
    timerId = window.setTimeout(() => {
      el.classList.remove(inClass);
      if (needsHeightGuard) el.style.minHeight = "";
      sayfaEnUsteAl();
    }, inDur);

    return () => window.clearTimeout(timerId);
  }, [aktifSayfa]);

  const openLegalDocument = (documentKey) => {
    const allowed = ["privacy", "terms", "account-deletion"];
    const nextKey = allowed.includes(documentKey) ? documentKey : "privacy";
    const currentIndex = allowed.indexOf(legalDocumentKey);
    const nextIndex = allowed.indexOf(nextKey);
    setLegalTransitionDirection(nextIndex >= currentIndex ? "left" : "right");
    setLegalDocumentKey(nextKey);
    setAyarlarAcik(false);
    if (aktifSayfaRef.current !== "legal") navigateTo("legal");
  };

  const openFeedbackPage = () => {
    setAyarlarAcik(false);
    if (aktifSayfaRef.current !== "feedback") navigateTo("feedback");
  };

  const changeLegalDocument = (documentKey, direction) => {
    if (documentKey === legalDocumentKey) return;
    setLegalTransitionDirection(direction || "left");
    setLegalDocumentKey(documentKey);
  };

  const navigateFromAssistant = (sayfa) => {
    assistantReturnPageRef.current = sayfa;
    navigateTo(sayfa);
  };

  const assistantBackTarget = () => {
    const current = aktifSayfaRef.current;
    if (assistantReturnPageRef.current && assistantReturnPageRef.current === current) {
      assistantReturnPageRef.current = null;
      return "assistant";
    }
    return "ana";
  };

  /* ── Swipe-to-back ile paylaşılan yardımcılar ──
     Bu fonksiyonlar eskiden NATIVE iOS SWIPE-BACK useEffect'i içinde lokaldi,
     sadece parmak hareketinden erişilebiliyordu. navigateTo() (footer geri
     butonu dahil her programatik "geri" çağrısı) da artık aynı mekanizmayı
     kullanabilsin diye component gövdesine taşındı — swipe'ın kendi davranışı
     hiç değişmedi, sadece bu fonksiyonlar artık iki yerden de çağrılabiliyor. */
    const chatRoomSwipeAktifMi = () => (
      aktifSayfaRef.current === "chat" &&
      (typeof window.__chatRoomCommitBack === "function" || typeof window.__chatRoomBack === "function")
    );

    const getSwipeTarget = () => {
      const wrap = activePageRef.current;
      if (!wrap) return null;

      // Chat oda ekranı zaten tam ekran/fixed çalışıyor. Parent wrapper'a transform verilirse
      // Capacitor WebView'de safe-area/padding hesabı bozulup içerik küçülüyor/aşağı kayıyor.
      // Bu yüzden chat odasında sadece öndeki chatRoomFull katmanını kaydırırız;
      // arka katmanda Aile Sohbeti listesi hazır beklediği için render davranışı diğer sayfalarla aynı kalır.
      if (chatRoomSwipeAktifMi()) {
        return document.querySelector('body > .chatPage.chatRoomFull') || document.querySelector('.chatPage.chatRoomFull') || wrap;
      }

      return wrap;
    };

    const setChatSwipeLayerState = (active) => {
      const wrap = activePageRef.current;
      if (!wrap) return;

      // Chat odasında parent wrapper her zaman gerçek Aile Sohbeti görünümünü korur.
      // Swipe sırasında arka planı değiştirmek, arkadaki listeyi normal sayfadan farklı render ediyordu.
      if (!active || !chatRoomSwipeAktifMi()) {
        wrap.style.background = "";
      }
    };

    const setDrag = (dx) => {
      const el = getSwipeTarget();
      if (!el) return;
      setChatSwipeLayerState(true);
      el.style.transition = "none";
      el.style.transform  = `translateX(${Math.max(0, dx)}px)`;
    };

    const clearDrag = () => {
      const wrap = activePageRef.current;
      const chatFull = document.querySelector('body > .chatPage.chatRoomFull') || document.querySelector('.chatPage.chatRoomFull');

      [wrap, chatFull].filter(Boolean).forEach((el) => {
        el.style.transition = "";
        el.style.transform  = "";
        el.style.opacity = "";
        el.classList?.remove?.('chatRoomSwipe');
      });
      hideSwipeBottomFill();
      setChatSwipeLayerState(false);
    };

    const shadow = (show) => {
      edgeShadowRef.current?.classList.toggle("swipe-shadow-visible", show);
    };

    const getBg = () => document.querySelector('.swipeBgLayer');

    // Swipe sırasında swipePageWrap altında clip görünmesini önleyen overlay
    const showSwipeBottomFill = () => {
      const wrap = activePageRef.current;
      if (!wrap || wrap.querySelector('.swipeBottomFill')) return;
      const fill = document.createElement('div');
      fill.className = 'swipeBottomFill';
      fill.style.cssText = 'position:absolute;left:0;right:0;bottom:-100vh;height:100vh;background:var(--bg-base);pointer-events:none;z-index:0;';
      wrap.appendChild(fill);
    };
    const hideSwipeBottomFill = () => {
      const wrap = activePageRef.current;
      if (wrap) wrap.querySelector('.swipeBottomFill')?.remove();
    };

    const showBg = () => {
      const b = getBg();
      if (b) b.style.visibility = 'visible';
    };
    const hideBg = () => {
      const b = getBg();
      if (b) b.style.visibility = '';
    };

    const snapComplete = (dx, onDone, mode = false) => {
      const el = getSwipeTarget();
      const screenW = window.innerWidth; const remaining = screenW - dx;
      const dur = Math.min(260, Math.max(120, remaining * 0.55));
      if (el) {
        el.style.transition = `transform ${dur}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
        el.style.transform  = `translateX(${screenW}px)`;
      }
      setTimeout(() => {
        if (mode === "sekme") {
          if (el) { el.style.transition = 'none'; el.style.opacity = '0'; el.style.transform = ''; }
          window.scrollTo(0, 0);
          onDone();
          requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => {
            if (el) { el.style.opacity = ''; el.style.transition = ''; }
            hideSwipeBottomFill();
            setChatSwipeLayerState(false);
            hideBg();
            setSwipeBgMode('ana');
          })));
        } else if (mode === true) {
          if (el) { el.style.transition = 'none'; el.style.opacity = '0'; el.style.transform = ''; }
          window.scrollTo(0, 0);
          onDone();
          requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => {
            if (el) { el.style.opacity = ''; el.style.transition = ''; }
            hideSwipeBottomFill();
            setChatSwipeLayerState(false);
            hideBg();
            setSwipeBgMode('ana');
          })));
        } else {
          if (el) { el.style.transition = 'none'; el.style.opacity = '0'; }
          window.scrollTo(0, 0);
          onDone();
          requestAnimationFrame(() => requestAnimationFrame(() => {
            hideBg();
            clearDrag();
          }));
        }
      }, dur + 10);
    };

    const snapCancel = (dx) => {
      const el = getSwipeTarget();
      const dur = Math.min(300, Math.max(150, dx * 0.8));
      if (el) {
        el.style.transition = `transform ${dur}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
        el.style.transform  = "translateX(0)";
      }
      // Bg'yi hemen gizle — sayfa geri dönerken arka plan görünmemeli
      hideBg();
      setTimeout(() => {
        clearDrag();
      }, dur + 10);
    };

  const runForwardPageTransition = (commitFn) => {
    const inClass = "nav-go-in";
    const inDur = 225;

    sayfaEnUsteAl();
    commitFn?.();

    requestAnimationFrame(() => {
      const newEl = activePageRef.current;
      if (newEl) {
        newEl.classList.remove("nav-go-out", "nav-back-out", "nav-back-in");
        newEl.style.transform = "";
        newEl.style.opacity = "";
        newEl.classList.add(inClass);
        setTimeout(() => newEl.classList.remove(inClass), inDur);
      }
      sayfaEnUsteAl();
    });
  };

  // Alt sayfa geri hareketi yardımcısı (buton tıklaması için) — artık swipe-to-back
  // ile birebir aynı mekanizmayı (showBg + snapComplete) kullanıyor, böylece footer
  // geri butonuna basıldığında da altta gerçek bir önceki sayfa (liste görünümü)
  // görünür oluyor, eskiden olduğu gibi boş kalmıyor.
  const _subpageBack = (guardPage, commitFn, setDetailFn) => {
    if (aktifSayfaRef.current !== guardPage) return;
    setSwipeBgMode(guardPage);
    requestAnimationFrame(() => requestAnimationFrame(() => showBg()));
    snapComplete(0, () => {
      commitFn?.();
      sayfaEnUsteAl();
      setDetailFn(false);
    }, true);
  };

  const documentsDetailBackWithGlobalAnimation = () =>
    _subpageBack("belgeler", () => window.__documentsDetailCommitBack?.(), setDocumentsDetailAcik);

  const healthDetailBackWithGlobalAnimation = () =>
    _subpageBack("saglik", () => window.__healthDetailCommitBack?.(), setHealthDetailAcik);

  const childrenDetailBackWithGlobalAnimation = () =>
    _subpageBack("cocuk", () => window.__childrenDetailCommitBack?.(), setChildrenDetailAcik);

  // Chat geri butonu: swipe-to-back ile birebir aynı.
  // _subpageBack kullanamayız çünkü chat'te wrap değil chatRoomFull kayar ve
  // wrap.background şeffaf yapılması gerekir. Bunları yapan setChatSwipeLayerState
  // useEffect içinde — onu DOM üzerinden manuel tetikliyoruz.
  const chatRoomBackWithGlobalAnimation = () => {
    if (aktifSayfaRef.current !== "chat") return;
    const wrap = activePageRef.current;
    const chatEl = document.querySelector("body > .chatPage.chatRoomFull") || document.querySelector(".chatPage.chatRoomFull");
    if (!chatEl) return;
    // Arkadaki gerçek Aile Sohbeti listesi ve Header aynı wrapper içinde mount durumda.
    // Wrapper zemini değiştirilmez; yalnızca ön oda katmanı kaydırılır.
    const screenW = window.innerWidth;
    const dur = Math.min(260, Math.max(120, screenW * 0.55));
    chatEl.style.transition = `transform ${dur}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
    chatEl.style.transform = `translateX(${screenW}px)`;
    setTimeout(() => {
      chatEl.style.transition = "none";
      chatEl.style.opacity = "0";
      chatEl.style.transform = "";
      window.scrollTo(0, 0);
      const commitFn = window.__chatRoomCommitBack || window.__chatRoomBack;
      if (typeof commitFn === "function") commitFn();
      setChatRoomAcik(false);
      sayfaEnUsteAl();
      requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        chatEl.style.opacity = "";
        chatEl.style.transition = "";
        if (wrap) wrap.style.background = "";
        setSwipeBgMode("ana");
      })));
    }, dur + 10);
  };

  /* ── Auth hook ── */
  const auth_ = useAuth();
  const { user, profil, setProfil, authKontrolEdiliyor, emailDogrulandi } = auth_;
  const authIdentity = String(user?.uid || user?.firebase_uid || user?.id || "");
  useEffect(() => {
    if (noFamilyDeleteOwnerRef.current === authIdentity) return;
    noFamilyDeleteOwnerRef.current = authIdentity;
    noFamilyOnboardingStartedAtRef.current = null;
    resetNoFamilyDelete();
  }, [authIdentity, resetNoFamilyDelete]);
  useUserTimezone(user);

  /* ── Bildirim hook (navigateTo + user/familyId gerekirken geçici) ── */
  const notifs = useNotifications({ navigateTo });

  /* ── Aile hook ── */
  const family_ = useFamily({
    user, profil, setProfil,
    setAktifSayfa: navigateTo,
  });
  const {
    familyId: loadedFamilyId,
    familyContextUserUid,
    loadingFamily,
    setLoadingFamily,
    familyResolved,
    uyelerRef,
  } = family_;
  // Bir önceki hesaptan kalan React state'i yeni oturumun listener'larını veya
  // native konum servisini kısa süreliğine dahi başlatmamalıdır.
  const familyId = authIdentity && familyContextUserUid === authIdentity
    ? loadedFamilyId
    : null;
  const callFamilySwitchRef = useRef(family_.switchFamily);
  callFamilySwitchRef.current = family_.switchFamily;

  /* FAZ 4A — VoIP token'ını aktif aileye bağla.
     DİKKAT: Bu efekt `familyId` TANIMLANDIKTAN SONRA durmalı; yukarıya
     taşınırsa "tanımlanmadan kullanım" hatası verir. */
  useEffect(() => { if (familyId) familyIdAta(familyId); }, [familyId]);

  /* ═══════════════════════════════════════════════════════════════════════
     MERKEZİ ARAMA YÖNETİCİSİ

     Arama durumu artık SAYFA seviyesinde değil, UYGULAMA seviyesinde yaşıyor
     (`callManager`). Böylece kilit ekranından cevaplandığında hangi sayfa açık
     olursa olsun aramaya katılınır, reddetme sunucuya bildirilir ve karşı taraf
     asılı kalmaz.

     `odayaGit`, cevaplanan aramanın odasını arka planda hazır tutmak için
     bildirimlerin kullandığı derin bağlantı mekanizmasını kullanır. Kullanıcı
     arama ekranını kapattığında doğrudan o sohbete düşer.
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    callManager.kur({
      familyHazirla: (targetFamilyId) => callFamilySwitchRef.current(targetFamilyId, {
        reason: "call-answer",
      }),
      odayaGit: (roomId) => {
        try {
          notifs.setBildirimChatRoomId(String(roomId));
          navigateTo("chat");
        } catch (_) { /* navigasyon başarısız olsa da arama devam eder */ }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    callManager.kimlikAta({ familyId, uid: user?.uid });
  }, [familyId, user?.uid]);

  /* Android: bildirimden "Reddet" uygulamayı açmadan sunucuya bilgi
     gönderebilsin diye oturum bilgisini native tarafa yaz. */
  useEffect(() => {
    const nativeOturumuYaz = (tokenOverride = null) => {
      if (!user) {
        oturumBilgisiniNativeYaz("", "", {});
        return;
      }
      try {
        const token = tokenOverride ?? getAccessToken();
        const refreshToken = getRefreshToken();
        const apiBase = (process.env.REACT_APP_API_BASE_URL || "https://api.aileyonetimi.com").replace(/\/+$/, "");
        oturumBilgisiniNativeYaz(token, apiBase, {
          refreshToken,
          familyId: familyId ? String(familyId) : "",
          uid: user?.uid ? String(user.uid) : "",
        });
        syncNativeBackgroundLocationAuth({ accessToken: token, refreshToken });
      } catch (_) { /* yoksay */ }
    };
    nativeOturumuYaz();
    const tokenDegisti = (e) => {
      const accessToken = e?.detail?.accessToken || "";
      const refreshToken = e?.detail?.refreshToken || getRefreshToken();
      if (!user) return;
      const apiBase = (process.env.REACT_APP_API_BASE_URL || "https://api.aileyonetimi.com").replace(/\/+$/, "");
      oturumBilgisiniNativeYaz(accessToken, apiBase, {
        refreshToken,
        familyId: familyId ? String(familyId) : "",
        uid: user?.uid ? String(user.uid) : "",
      });
      syncNativeBackgroundLocationAuth({ accessToken, refreshToken });
    };
    window.addEventListener("fp:accessTokenChanged", tokenDegisti);
    return () => window.removeEventListener("fp:accessTokenChanged", tokenDegisti);
  }, [user, familyId]);



  /* ── Veri hook ── */
  const appData = useAppData({
    familyId, user,
    bildirimGoster: notifs.bildirimGoster,
    bildirimHerkeseYazService,
    bildirimEkleService,
    uyelerRef,
    aktifSayfaRef,
  });

  /* Manuel girişte mevcut PremiumStartupSplash aynen korunur. Animasyon bileşenine
     ve 2 saniyelik startup zamanlayıcısına dokunulmaz. Katman yalnızca yeni hesabın
     profil + aile + temel dashboard state'i gerçekten hazır olana kadar kaldırılmaz. */
  useEffect(() => {
    if (!loginStartupVisible || !loginHydrationUid) return;

    const currentUid = String(user?.uid || user?.firebase_uid || user?.id || "").trim();
    if (!currentUid || currentUid !== loginHydrationUid) return;

    const resolvedFamilyId = familyId || null;

    if (family_.familyResolved && !resolvedFamilyId && profil) {
      setLoginStartupReady(true);
      return;
    }

    if (
      profil &&
      resolvedFamilyId &&
      appData.initialDataUserUid === currentUid &&
      appData.initialDataFamilyId === resolvedFamilyId &&
      !appData.initialDataLoading
    ) {
      let raf2 = null;
      const raf1 = window.requestAnimationFrame(() => {
        raf2 = window.requestAnimationFrame(() => {
          setLoginStartupReady(true);
        });
      });
      return () => {
        window.cancelAnimationFrame(raf1);
        if (raf2) window.cancelAnimationFrame(raf2);
      };
    }
  }, [
    loginStartupVisible,
    loginHydrationUid,
    user,
    profil,
    familyId,
    family_.familyResolved,
    appData.initialDataUserUid,
    appData.initialDataFamilyId,
    appData.initialDataLoading,
  ]);

  /* Aile değişiminde mevcut tam ekran logo splash'i iki saniye boyunca hedef
     ailenin ana ekranını arkada hazırlar. Veri daha erken hazır olsa bile splash
     süresini PremiumStartupSplash korur; iki saniye sonunda hızlı fade başlar. */
  useEffect(() => {
    if (!aileGecisSplashVisible) return undefined;

    const hedef = String(aileGecisHedefId || "").trim();
    const aktif = String(familyId || "").trim();

    const hedefeUlasildi = hedef && aktif === hedef;
    const veriHazir =
      hedefeUlasildi &&
      appData.initialDataFamilyId === aktif &&
      !appData.initialDataLoading;

    if (veriHazir) {
      setAileGecisCikabilir(true);
    }
    return undefined;
  }, [
    aileGecisSplashVisible,
    aileGecisHedefId,
    familyId,
    appData.initialDataFamilyId,
    appData.initialDataLoading,
  ]);

  // Ağ veya önbellek yanıtı gecikse bile geçiş iki saniyede fade'e hazır olur;
  // bileşenin kendi minimum süresi erken kapanmayı ayrıca engeller.
  useEffect(() => {
    if (!aileGecisSplashVisible) return undefined;
    const timer = window.setTimeout(() => setAileGecisCikabilir(true), 2000);
    return () => window.clearTimeout(timer);
  }, [aileGecisSplashVisible, aileGecisRunKey]);
  /* ── Global native konum takibi ──────────────────────────────────────
     Arka plan takibi HaritaPage yaşam döngüsüne bağlı olamaz. Kullanıcı
     uygulamayı ana sayfada açıp arka plana aldığında JavaScript interval'ları
     işletim sistemi tarafından askıya alınır; watcher bu süreçte restore
     edilmezse yol boyunca konum yazılmaz ve tüm bölge geçişleri bir sonraki
     foreground konumunda aynı anda oluşur.

     Bu effect sunucudaki kesin paylaşım durumunu izler:
       • iOS: bağımsız CLLocationManager'a kimlik + aktiflik bağlamını verir.
       • Android: aktif BackgroundGeolocation watcher yoksa sessizce kurar.
     Hiçbir izin popup'ı açmaz ve mevcut watcher'ı gereksiz yere yenilemez. */
  const globalLocationSharingState = user?.uid && appData.locationsFamilyId === familyId
    ? appData.locations?.[user.uid]?.paylasimAcik
    : undefined;
  useEffect(() => {
    if (!isNativeLocationAvailable()) return undefined;
    const nextContextKey = user?.uid && familyId ? `${user.uid}:${familyId}` : "";
    if (globalLocationSharingState === undefined) {
      // Cold startta ref boştur; native kalıcı kuyruk ve mevcut takip kesilmez.
      // Ref doluyken anahtar değişmişse ise eski kullanıcı/aile adına tek bir
      // gecikmiş yazıma dahi izin vermeden yeni SSE bağlamını bekleriz.
      if (globalNativeLocationContextRef.current &&
          globalNativeLocationContextRef.current !== nextContextKey) {
        globalNativeLocationContextRef.current = "";
        globalLocationImmediateWriteRef.current = "";
        globalNativeRegionsSyncRef.current = "";
        setNativeBackgroundLocationSharingEnabled(false);
        stopNativeLocationSharing().catch(() => {});
      }
      return undefined;
    }
    if (!user?.uid || !familyId) return undefined;

    let iptal = false;

    if (globalLocationSharingState !== true) {
      globalNativeLocationContextRef.current = "";
      globalLocationImmediateWriteRef.current = "";
      globalNativeRegionsSyncRef.current = "";
      setNativeBackgroundLocationSharingEnabled(false);
      stopNativeLocationSharing().catch(() => {});
      return () => { iptal = true; };
    }

    globalNativeLocationContextRef.current = nextContextKey;

    const onLocation = async (loc) => {
      if (globalLocationSharingState !== true) return;
      await konumGuncelleService({
        familyId,
        uid          : user.uid,
        lat          : loc.lat,
        lng          : loc.lng,
        accuracy     : loc.accuracy ?? null,
        speed        : loc.speed ?? null,
        heading      : loc.heading ?? null,
        source       : "native-background-global",
        paylasimAcik : true,
        savePoint    : true,
      });
    };

    setBackgroundLocationContext({
      familyId,
      uid       : user.uid,
      tamAd     : profil ? `${profil.ad || ""} ${profil.soyad || ""}`.trim() : "",
      onLocation,
    });
    setNativeBackgroundLocationSharingEnabled(true);

    // Harita sayfası hiç açılmasa bile mevcut güvenli alanları native katmana
    // bir kez kaydet. Bu çağrı polling değildir; aile/paylaşım bağlamı
    // değişmedikçe tekrarlanmaz. Hata halinde konum paylaşımı çalışmaya devam
    // eder ve backend geofence kontrolü güvenli fallback olarak kalır.
    const nativeRegionSyncKey = `${user.uid}:${familyId}`;
    if (globalNativeRegionsSyncRef.current !== nativeRegionSyncKey) {
      globalNativeRegionsSyncRef.current = nativeRegionSyncKey;
      nativeBolgeListesiGetirService(familyId).then((zones) => {
        if (iptal || globalLocationSharingState !== true) return;
        setBackgroundLocationContext({
          familyId,
          uid  : user.uid,
          tamAd: profil ? `${profil.ad || ""} ${profil.soyad || ""}`.trim() : "",
          zones,
        });
      }).catch(() => {
        if (globalNativeRegionsSyncRef.current === nativeRegionSyncKey) {
          globalNativeRegionsSyncRef.current = "";
        }
      });
    }

    // iOS arka plan yazımını AppDelegate içindeki bağımsız CLLocationManager
    // yapar. Android'de ise native plugin watcher'ı uygulama sürecinde sessizce
    // restore edilmelidir.
    if (!isIOSPlatform()) {
      (async () => {
        const izinVar = await hasNativeLocationPermission();
        if (iptal || !izinVar || isNativeLocationSharingRunning()) return;
        await startNativeLocationSharing({
          onLocation,
          requestPermission: false,
          tryAlways: false,
        });
      })().catch(() => {});
    }

    return () => { iptal = true; };
  }, [familyId, globalLocationSharingState, profil?.ad, profil?.soyad, user?.uid]); // eslint-disable-line

  // Her uygulama oturumunda/aile bağlamında ilk canlı konumu bir kez hemen yaz.
  // Devamındaki güncellemeler hareket tabanlı native watcher'dan gelir; burada
  // periyodik GET ya da timer yoktur.
  useEffect(() => {
    // iOS AppDelegate foreground/cold-start sırasında kuyruğu flush eder ve
    // CLLocationManager'ın ilk geçerli ölçümünü zaten yazar. WebView'den ikinci
    // bir app-open POST göndermek aynı fiziksel noktayı çift kaydediyordu.
    if (isIOSPlatform()) return undefined;
    if (!user?.uid || !familyId || globalLocationSharingState !== true) return undefined;
    const immediateWriteKey = `${user.uid}:${familyId}`;
    if (globalLocationImmediateWriteRef.current === immediateWriteKey) return undefined;
    globalLocationImmediateWriteRef.current = immediateWriteKey;
    let iptal = false;

    nativeGetLocationOnce({
      requestPermission: false,
      tryAlways        : false,
      timeout          : 10000,
      maximumAge       : 15000,
    }).then((loc) => {
      if (iptal || !loc?.lat || !loc?.lng) return undefined;
      return konumGuncelleService({
        familyId,
        uid          : user.uid,
        lat          : loc.lat,
        lng          : loc.lng,
        accuracy     : loc.accuracy ?? null,
        speed        : loc.speed ?? null,
        heading      : loc.heading ?? null,
        source       : "app-open-global",
        paylasimAcik : true,
      });
    }).catch(() => {
      // İzin/konum geçici olarak hazır değilse native watcher ilk geçerli
      // hareket güncellemesini gönderir; burada polling başlatılmaz.
    });

    return () => { iptal = true; };
  }, [familyId, globalLocationSharingState, user?.uid]);

  // apiClient access token'ı yenilediği anda iOS AppDelegate UserDefaults
  // köprüsünü de güncelle. Böylece Harita sayfası hiç açılmasa dahi native
  // arka plan servisi güncel access/refresh token ile devam eder.
  useEffect(() => {
    if (!user?.uid || !familyId || globalLocationSharingState !== true) return undefined;
    const tokenDegisti = () => {
      setBackgroundLocationContext({
        familyId,
        uid  : user.uid,
        tamAd: profil ? `${profil.ad || ""} ${profil.soyad || ""}`.trim() : "",
      });
    };
    window.addEventListener("fp:accessTokenChanged", tokenDegisti);
    return () => window.removeEventListener("fp:accessTokenChanged", tokenDegisti);
  }, [familyId, globalLocationSharingState, profil?.ad, profil?.soyad, user?.uid]);


  /* ── Yetki hook ── */
  const currentFamilyMembership = useMemo(() => {
    const currentUid = String(user?.uid || user?.firebase_uid || user?.id || "").trim();
    if (!currentUid || family_.membersLoading) return null;
    return (family_.uyeler || []).find((member) => {
      const memberUid = String(member?.uid || member?.firebaseUid || member?.userId || member?.id || "").trim();
      return memberUid === currentUid;
    }) || null;
  }, [family_.membersLoading, family_.uyeler, user?.firebase_uid, user?.id, user?.uid]);
  const permissionProfile = useMemo(() => currentFamilyMembership ? {
    ...(profil || {}),
    aileYetkisi: currentFamilyMembership.aileYetkisi || currentFamilyMembership.role || profil?.aileYetkisi,
    permissions: currentFamilyMembership.permissions || profil?.permissions,
  } : profil, [currentFamilyMembership, profil]);
  const { yoneticiMi, permissions, sayfaIzniVarMi, islemIzniVarMi, silebilirMi } =
    usePermissions({ profil: permissionProfile, user, ownerUid: family_.ownerUid });
  pagePermissionCheckRef.current = sayfaIzniVarMi;
  const isSuperAdmin = user?.isSuperAdmin === true || profil?.isSuperAdmin === true;

  useEffect(() => {
    const permissionKey = APP_PAGE_PERMISSION_KEYS[aktifSayfa];
    if (permissionKey && !sayfaIzniVarMi(permissionKey)) setAktifSayfa("ana");
  }, [aktifSayfa, permissions]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isSuperAdmin && adminMode) {
      setAdminMode(false);
    }
  }, [isSuperAdmin, adminMode]);

  /* ── Premium hook ── */
  const premium = usePremium({ user, familyId });

  /* Premium popup state */
  const [premiumPopup, setPremiumPopup] = useState({
    visible: false, mode: "locked",
    featureTitle: "", featureDesc: "",
    limitLabel: "", limitCurrent: 0, limitMax: 0,
    benefits: null,
    plusBenefits: null,
  });
  const showPremiumLock = (featureTitle, featureDesc, benefits, plusBenefits) => {
    setPremiumPopup({ visible: true, mode: "locked", featureTitle, featureDesc, benefits: benefits || null, plusBenefits: plusBenefits || null, limitLabel:"", limitCurrent:0, limitMax:0 });
  };
  const showPremiumLimit = (limitLabel, limitCurrent, limitMax, benefits, customDesc, plusBenefits) => {
    setPremiumPopup({
      visible: true,
      mode: "limit",
      featureTitle: t("premium.limitReached", { label: limitLabel }),
      featureDesc: customDesc || t("premium.limitDescGeneric", { max: limitMax, label: String(limitLabel).toLowerCase() }),
      limitLabel,
      limitCurrent,
      limitMax,
      benefits: benefits || null,
      plusBenefits: plusBenefits || null,
    });
  };
  const closePremiumPopup = () => setPremiumPopup((p) => ({ ...p, visible: false }));

  /* ── Popup/overlay açıkken native status bar'ı kararttır ── */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // ANDROID: status bar overlay:false ile solid uygulama rengine sabitlendiği
    // için burada tekrar renk değiştirmiyoruz. iOS'ta status bar şeffaf (overlay)
    // olduğundan, popup açıldığında arkasını hafif karartmak görsel tutarlılık
    // sağlar — bu davranış yalnızca iOS'a özeldir.
    if (Capacitor.getPlatform() === "android") return;
    const overlayOpen = menuAcik || ayarlarAcik || premiumPopup.visible;
    if (overlayOpen) {
      StatusBar.setBackgroundColor({ color: tema === 'light' ? '#1a1a2ef0' : '#00000099' }).catch(() => {});
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    } else {
      StatusBar.setBackgroundColor({ color: '#00000000' }).catch(() => {});
      StatusBar.setStyle({ style: tema === 'light' ? Style.Light : Style.Dark }).catch(() => {});
    }
  }, [menuAcik, ayarlarAcik, premiumPopup.visible, tema]);

  const kullaniciAileSayisi = () => {
    const familiesCount = Array.isArray(profil?.families) ? profil.families.length : 0;
    const legacyCount = profil?.familyId ? 1 : 0;
    const activeCount = familyId ? 1 : 0;
    return Math.max(familiesCount, legacyCount, activeCount);
  };

  const aileLimitKontrolEt = async (baslik) => {
    const mevcutAileSayisi = kullaniciAileSayisi();
    const kontrol = premium.checkLimit("kullaniciAile", mevcutAileSayisi);
    if (!kontrol.allowed) {
      showPremiumLimit(
        baslik,
        mevcutAileSayisi,
        kontrol.limit,
        [t("premium.benefit.multiFamily"), t("premium.benefit.separatePlanning"), t("premium.benefit.quickSwitch")],
        t("familySwitcher.limitMessage", { limit: kontrol.limit }),
        [t("familySwitcher.plus.multiFamily"), t("familySwitcher.plus.unlimitedMembers"), t("familySwitcher.plus.unlimitedRecords")]
      );
      return false;
    }
    return true;
  };

  /* Sayfa girişinde limit kontrolü */
  useEffect(() => {
    if (!aktifSayfa || premium.loading) return;
    if (premium.isPremium) return;

    const bekleyen     = appData.urunler.length; // toplam (alınan + bekleyen)
    const aktifGorev   = appData.gorevler.filter(g => !g.tamamlandi).length;
    const belgeSayisi  = appData.belgeler.length;
    const now          = new Date();
    const buAyFinans   = appData.finanslar.filter(f => {
      const d = new Date(f.tarih || 0);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;

    if (aktifSayfa === "alisveris") {
      const k = premium.checkLimit("alisverisOge", bekleyen);
      if (!k.allowed) showPremiumLimit(t("premium.limit.shoppingItems"), bekleyen, k.limit, [t("premium.benefit.unlimitedShopping"), t("premium.benefit.shoppingCategories"), t("premium.benefit.shoppingHistory")], undefined, [t("premium.plus.benefit.shoppingSync"), t("premium.plus.benefit.shoppingAssign"), t("premium.plus.benefit.shoppingBudget")]);
    } else if (aktifSayfa === "gorev") {
      const k = premium.checkLimit("aktifGorev", aktifGorev);
      if (!k.allowed) showPremiumLimit(t("premium.limit.activeTasks"), aktifGorev, k.limit, [t("premium.benefit.unlimitedTasks"), t("premium.benefit.taskPriority"), t("premium.benefit.taskReminders")], undefined, [t("premium.plus.benefit.taskAssign"), t("premium.plus.benefit.taskRepeat"), t("premium.plus.benefit.taskStats")]);
    } else if (aktifSayfa === "belgeler") {
      const k = premium.checkLimit("belgeSayisi", belgeSayisi);
      if (!k.allowed) showPremiumLimit(t("premium.limit.documents"), belgeSayisi, k.limit, [t("premium.benefit.unlimitedDocuments"), "5 GB storage", t("premium.benefit.personalSharedDocs"), t("premium.benefit.expiryTracking")], undefined, [t("premium.plus.benefit.docsUnlimited"), t("premium.plus.benefit.docsVersioning"), t("premium.plus.benefit.docsTeam")]);
    } else if (aktifSayfa === "finans") {
      const k = premium.checkLimit("aylikFinans", buAyFinans);
      if (!k.allowed) showPremiumLimit(t("premium.limit.financeRecords"), buAyFinans, k.limit, [t("premium.benefit.unlimitedMonthlyRecords"), t("premium.benefit.reportsCharts"), t("premium.benefit.savingsGoals"), t("premium.benefit.installmentSubscriptions")], undefined, [t("premium.plus.benefit.financeMulti"), t("premium.plus.benefit.financeAdvanced"), t("premium.plus.benefit.financeExport")]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktifSayfa, premium.loading, premium.isPremium, t]);


  /* ── Auth takibi başlat ── */
  useEffect(() => {
    const resetAuthenticatedClientState = () => {
      setLoginStartupVisible(false);
      setLoginStartupReady(false);
      setLoginStartupRequiresReady(false);
      setLoginHydrationUid("");
      family_.aileSifirla();
      appData.setUrunler([]);
      appData.setGorevler([]);
      appData.setFinanslar([]);
      appData.setHarcamalar([]);
      appData.setTakvimEtkinlikleri([]);
      appData.setBelgeler([]);
      appData.setHealthRecords([]);
      appData.setManualChildren([]);
      appData.setChildRecords([]);
      appData.setLocations({});
      appData.setLocationsFamilyId(null);
      appData.setMapMetricCounts({ places: 0, geofences: 0, safeZones: 0, locationPoints: 0 });
      appData.setOkunmamisChat(0);
      appData.setOkunmamisChatOda(0);
      appData.setToplamSohbet(0);
      notifs.setMerkeziBildirimler([]);
      notifs.setMerkeziChatBildirimler([]);
      notifs.setBildirimPopupAcik(false);
    };

    window.addEventListener("fp:sessionEnding", resetAuthenticatedClientState);
    const stopAuthWatch = auth_.startAuthWatch(
      async (currentUser, userData, userExists) => {
        // Cache'den aile verisi var mı kontrol et
        // Varsa loadingFamily gösterme — kullanıcı direkt ana sayfayı görsün
        // Yoksa (ilk giriş) kısa loading göster
        const familyCache = (() => {
          try {
            const r = localStorage.getItem("fp:familyCache");
            const cache = r ? JSON.parse(r) : null;
            const currentUid = String(currentUser?.uid || currentUser?.firebase_uid || currentUser?.id || "").trim();
            return cache && String(cache?.userUid || "").trim() === currentUid ? cache : null;
          } catch { return null; }
        })();
        if (!familyCache) setLoadingFamily(true);
        // Aile bilgisini yükle (cache varsa offline fix içinde hemen state'e yazar)
        await family_.aileBilgisiYukle(currentUser, userData || null);
      },
      resetAuthenticatedClientState,
      /* onLangSync: Firebase'den okunan dil bilgisini Context'e ilet */
      (lang) => setLang(lang)
    );
    return () => {
      window.removeEventListener("fp:sessionEnding", resetAuthenticatedClientState);
      if (typeof stopAuthWatch === "function") stopAuthWatch();
    };
  }, []); // eslint-disable-line

  /* ── uyelerAcik localStorage senkronizasyonu ── */
  useEffect(() => { localStorage.setItem("uyelerAcik", JSON.stringify(family_.uyelerAcik)); }, [family_.uyelerAcik]);

  /* ── Üyeleri dinle ── */
  useEffect(() => {
    if (!familyId) return;
    return family_.uyelerDinle(familyId);
  }, [familyId]); // eslint-disable-line

  /* ── Veri listener'larını başlat ── */
  useEffect(() => {
    if (!familyId || !user?.uid) return;
    return appData.veriDinlemeBaslat(user?.uid, familyId);
  }, [familyId, user?.uid]); // eslint-disable-line

  /* ── Merkezi bildirimleri dinle ── */
  useEffect(() => {
    if (!user?.uid || !familyId) return;
    // Yalnızca aktif ailenin bildirimleri dinlenir. Diğer aileler kullanıcı
    // aile değiştirdiğinde familyId güncellenince otomatik olarak devreye girer.
    return notifs.bildirimlereDinle(familyId, user?.uid, profil?.families || []);
  }, [user?.uid, familyId]); // eslint-disable-line

  /* ── İlk aile sonrası uygulama rehberi (Aşama 1) ── */
  const [appTourActive, setAppTourActive] = useState(false);
  const [appTourPending, setAppTourPending] = useState(false);

  /* ── Doğum günü deneyimi ──
     Backend claim'i aynı yerel günde tüm cihazlarda yalnızca bir kez tam ekran
     kutlama gösterir. Bu kontrol bitmeden günlük özet açılmaz; overlay'ler yarışmaz. */
  const [birthdayCelebration, setBirthdayCelebration] = useState(null);
  const [birthdayDetailId, setBirthdayDetailId] = useState("");
  const [birthdayExperienceReady, setBirthdayExperienceReady] = useState(false);
  const [birthdayOwnCelebrationShown, setBirthdayOwnCelebrationShown] = useState(false);
  const birthdayClaimRef = useRef({ key: "", running: false, token: null });

  useEffect(() => {
    birthdayClaimRef.current = { key: "", running: false, token: null };
    setBirthdayCelebration(null);
    setBirthdayDetailId("");
    setBirthdayExperienceReady(false);
    setBirthdayOwnCelebrationShown(false);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !familyId) return;
    if (premiumStartupVisible || family_.basariMesaji || appTourPending || appTourActive) return;
    if (appData.initialDataLoading || family_.membersLoading) return;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const claimKey = `${user.uid}:${today}`;
    if (birthdayClaimRef.current.key === claimKey || birthdayClaimRef.current.running) return;
    const attemptToken = Symbol(claimKey);
    birthdayClaimRef.current = { key: claimKey, running: true, token: attemptToken };
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const result = await claimBirthdayExperience();
        if (!cancelled && result?.eligible) {
          setBirthdayOwnCelebrationShown(true);
          if (result?.showCelebration) setBirthdayCelebration(result);
        }
      } catch (error) {
        console.warn("[BirthdayExperience] Kutlama kontrolü atlandı:", error?.message || error);
      } finally {
        if (birthdayClaimRef.current.token === attemptToken) {
          birthdayClaimRef.current = { key: claimKey, running: false, token: attemptToken };
          if (!cancelled) setBirthdayExperienceReady(true);
        }
      }
    }, 260);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (birthdayClaimRef.current.token === attemptToken && birthdayClaimRef.current.running) {
        birthdayClaimRef.current = { key: "", running: false, token: null };
      }
    };
  }, [user?.uid, familyId, premiumStartupVisible, family_.basariMesaji, family_.membersLoading, appTourPending, appTourActive, appData.initialDataLoading]);

  const openBirthdayDetail = useCallback(async (birthday = {}) => {
    const directId = birthday.occurrenceId || (String(birthday.id || "").match(/^[0-9a-f-]{36}$/i) ? birthday.id : "");
    if (directId) {
      setBirthdayDetailId(String(directId));
      return;
    }
    if (!familyId) return;
    try {
      const resolved = await resolveBirthdayOccurrence(familyId, birthday);
      if (resolved?.occurrenceId) {
        setBirthdayDetailId(String(resolved.occurrenceId));
        return;
      }
      // Eski backend sürümüyle kısa dağıtım penceresinde geriye uyumluluk.
      const upcoming = await getUpcomingBirthdays(familyId, 370);
      const match = findBirthdayOccurrence(upcoming, birthday);
      if (match?.id) {
        setBirthdayDetailId(match.id);
        return;
      }
      await appAlert(lang === "en"
        ? "This birthday celebration could not be matched. Refresh the page and try again."
        : "Bu doğum günü kutlaması eşleştirilemedi. Sayfayı yenileyip tekrar deneyin.");
    } catch (error) {
      console.warn("[BirthdayExperience] Doğum günü detayı açılamadı:", error?.message || error);
      await appAlert(error?.message || (lang === "en"
        ? "Birthday details could not be opened."
        : "Doğum günü detayı açılamadı."));
    }
  }, [familyId, lang]);

  useEffect(() => {
    const openFromNotification = (event) => {
      window.setTimeout(() => openBirthdayDetail(event?.detail || {}), 140);
    };
    window.addEventListener("fp:openBirthday", openFromNotification);
    return () => window.removeEventListener("fp:openBirthday", openFromNotification);
  }, [openBirthdayDetail]);
  const noFamilyTourSourceRef = useRef(false);

  const appTourBitir = () => {
    setAppTourActive(false);
    setAppTourPending(false);
  };

  const hosgeldinSonrasiDevamEt = () => {
    // Rehber yalnızca "Aile grubun yok" ekranındaki oluştur/katıl akışından gelir.
    // Aile seçici modalındaki aile değiştirme, oluşturma veya katılma işlemleri rehberi tetiklemez.
    const shouldStartTour = noFamilyTourSourceRef.current === true;
    noFamilyTourSourceRef.current = false;

    if (shouldStartTour) setAppTourPending(true);
    family_.setBasariMesaji("");
    if (shouldStartTour) {
      window.requestAnimationFrame(() => window.setTimeout(() => {
        setAppTourActive(true);
        setAppTourPending(false);
      }, 260));
    }
  };

  /* ── Günlük giriş hatırlatıcısı ── */
  const [gunlukPopupAcik, setGunlukPopupAcik] = useState(false);
  const [konfetiAktif, setKonfetiAktif]       = useState(false);
  const [gunlukMaddeler, setGunlukMaddeler]   = useState([]);
  // Aynı uygulama oturumunda render/aile değişimi popup kontrolünü tekrar başlatmasın.
  const gunlukPopupAttemptRef = useRef({ uid: '', date: '', attempted: false });

  // Hesap veya aktif aile değiştiğinde önceki hesaptan açık popup/içerik taşınmasın.
  useEffect(() => {
    setGunlukPopupAcik(false);
    setGunlukMaddeler([]);
  }, [user?.uid, familyId]);

  useEffect(() => {
    if (!user?.uid || !familyId) return;
    if (!birthdayExperienceReady || birthdayCelebration || birthdayDetailId) return;
    if (appData.initialDataLoading || appData.initialDataFamilyId !== familyId) return;
    if (appData.popupDataLoading || appData.popupDataFamilyId !== familyId) return;
    // Aile üyeleri henüz hiç çözülmediyse doğum günü/kişi filtreleri eksik hesaplanır.
    // Geçerli cache veya members isteği sonucu geldikten sonra devam et.
    if (family_.membersLoading) return;
    // Hoşgeldin popup ve ilk kullanım rehberi tamamlanmadan günlük popup gösterilmez.
    if (family_.basariMesaji || appTourPending || appTourActive) return;

    // Yerel takvim gününü kullan; UTC dönüşümü gece saatlerinde günü kaydırmasın.
    const localDateKey = (date = new Date()) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };
    const bugun = localDateKey();
    // Her kullanıcı için günde yalnızca bir kez. Aynı telefonda hesap değiştirildiğinde
    // diğer kullanıcının günlük popup hakkı etkilenmez.
    const storageKey = `dailyReminder_${String(user.uid)}_${bugun}`;
    const attemptKeyMatches =
      gunlukPopupAttemptRef.current.uid === String(user.uid) &&
      gunlukPopupAttemptRef.current.date === bugun;
    if (attemptKeyMatches && gunlukPopupAttemptRef.current.attempted) return;
    if (localStorage.getItem(storageKey)) {
      gunlukPopupAttemptRef.current = { uid: String(user.uid), date: bugun, attempted: true };
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const uid      = user.uid;
      const isAdmin  = yoneticiMi;
      const bugunDate = new Date();
      const yarinDate = new Date(bugunDate); yarinDate.setDate(yarinDate.getDate() + 1);
      const yediDate = new Date(bugunDate); yediDate.setDate(yediDate.getDate() + 7);
      const bugunStr = localDateKey(bugunDate);
      const yarin    = localDateKey(yarinDate);
      const yedi     = localDateKey(yediDate);

      const dateOnly = (value) => {
        if (!value) return "";
        const raw = String(value).trim();
        const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
        if (iso) return iso[1];
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? "" : localDateKey(parsed);
      };

      const kalanGunHesapla = (tarih) => {
        const normalized = dateOnly(tarih);
        if (!normalized) return 9999;
        const [y, m, d] = normalized.split("-").map(Number);
        const [by, bm, bd] = bugunStr.split("-").map(Number);
        return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(by, bm - 1, bd)) / 86400000);
      };

      const zamanEtiketi = (tarih, saat) => {
        const kalan = kalanGunHesapla(tarih);
        if (kalan < 0)  return { label: t("home.reminder.overdue", { days: Math.abs(kalan) }), oncelik: 0, gecikmisMi: true };
        if (kalan === 0) return { label: saat ? t("home.reminder.todayAt", { time: saat }) : t("home.reminder.today"), oncelik: 1, gecikmisMi: false };
        if (kalan === 1) return { label: saat ? t("home.reminder.todayAt", { time: saat }).replace(t("home.reminder.today"), t("home.reminder.tomorrow")) : t("home.reminder.tomorrow"), oncelik: 2, gecikmisMi: false };
        return { label: t("home.reminder.daysLeft", { days: kalan }), oncelik: 3, gecikmisMi: false };
      };

      const maddeler = [];

      // ── GÖREVLER ─────────────────────────────────────────────
      // Kullanıcıya atanan VEYA admin ise tüm görevler
      (appData.gorevler || []).forEach((g) => {
        const gorevTarih = dateOnly(g.sonTarih || g.dueDate || g.son_tarih);
        if (g.tamamlandi || !gorevTarih) return;
        if (gorevTarih > yedi) return;
        if (!isAdmin && g.atananUid && g.atananUid !== uid) return; // başkasına atanmış
        const { label, oncelik, gecikmisMi } = zamanEtiketi(gorevTarih, "");
        const atananAd = g.atananAd || "";
        maddeler.push({
          id: `t_${g.id}`, tip: "task",
          baslik: g.ad || g.baslik || g.title || t("daily.popup.task"),
          altBilgi: isAdmin && atananAd ? atananAd : "",
          zaman: label, oncelik, gecikmisMi,
          tarihStr: gorevTarih, sayfa: "gorev",
        });
      });

      // ── TAKVİM ETKİNLİKLERİ ──────────────────────────────────
      // ilgiliKisiUid yoksa → herkesi ilgilendirir
      // ilgiliKisiUid varsa → sadece o kişi + admin
      (appData.takvimEtkinlikleri || []).forEach((e) => {
        const etkinlikTarih = dateOnly(e.tarih || e.date || e.startDate);
        if (!etkinlikTarih || etkinlikTarih < bugunStr || etkinlikTarih > yedi) return;
        const herkese = !e.ilgiliKisiUid;
        if (!herkese && !isAdmin && e.ilgiliKisiUid !== uid) return;
        const { label, oncelik } = zamanEtiketi(etkinlikTarih, e.saat || e.time);
        const kisiAd = e.ilgiliKisiAd || "";
        maddeler.push({
          id: `e_${e.id}`, tip: "event",
          baslik: e.baslik || e.title || t("daily.popup.event"),
          altBilgi: kisiAd,
          zaman: label, oncelik, gecikmisMi: false,
          tarihStr: etkinlikTarih, sayfa: "takvim",
        });
      });

      // ── SAĞLIK: RANDEVU ───────────────────────────────────────
      // memberUid varsa → o kişi + admin; yoksa herkes
      if (!areAndroidHealthFeaturesDisabled()) (appData.healthRecords || []).forEach((r) => {
        if (r.type !== "appointment") return;
        const randevuTarih = dateOnly(r.date || r.tarih);
        if (!randevuTarih || (randevuTarih < bugunStr || randevuTarih > yarin)) return; // sadece bugün + yarın
        const herkese = !r.memberUid;
        if (!herkese && !isAdmin && r.memberUid !== uid) return;
        const { label, oncelik } = zamanEtiketi(randevuTarih, r.time || r.saat);
        const uyeAdi = r.memberName || r.uyeAd || "";
        maddeler.push({
          id: `a_${r.id || r.date}`, tip: "appointment",
          baslik: r.doctorName || r.department || r.doctor || t("daily.popup.appointment"),
          altBilgi: uyeAdi,
          zaman: label, oncelik, gecikmisMi: false,
          tarihStr: randevuTarih, sayfa: "saglik",
        });
      });

      // ── SAĞLIK: İLAÇ ─────────────────────────────────────────
      // memberUid varsa → o kişi + admin
      if (!areAndroidHealthFeaturesDisabled()) (appData.healthRecords || []).forEach((r) => {
        if (r.type !== "medicine") return;
        if (!r.startDate || r.startDate > bugunStr) return;
        if (r.endDate && r.endDate < bugunStr) return;
        if (!isAdmin && r.memberUid && r.memberUid !== uid) return;
        const saatler = Array.isArray(r.times) && r.times.length > 0 ? r.times.join(", ") : "";
        const uyeAdi = r.memberName || r.uyeAd || "";
        maddeler.push({
          id: `m_${r.id}`, tip: "medicine",
          baslik: r.name || r.ad || t("daily.popup.medicine"),
          altBilgi: uyeAdi,
          zaman: saatler ? `⏰ ${saatler}` : t("home.reminder.today"),
          oncelik: 1, gecikmisMi: false,
          tarihStr: bugunStr, sayfa: "saglik",
        });
      });

      // ── DOĞUM GÜNLERİ ────────────────────────────────────────
      // Tüm aile üyelerine gösterilir
      (family_.uyeler || []).forEach((u) => {
        if (!u.dogumTarihi) return;
        if (birthdayOwnCelebrationShown && String(u.uid || "") === String(uid || "")) return;
        const normalizedBirthDate = dateOnly(u.dogumTarihi);
        if (!normalizedBirthDate) return;
        const [, mm, dd] = normalizedBirthDate.split("-");
        const buYil = new Date().getFullYear();
        const leapDay = mm === "02" && dd === "29";
        const leapYear = buYil % 4 === 0 && (buYil % 100 !== 0 || buYil % 400 === 0);
        const birthdayThisYear = `${buYil}-${mm}-${leapDay && !leapYear ? "28" : dd}`;
        if (birthdayThisYear === bugunStr) {
          const ad = `${u.ad || ""} ${u.soyad || ""}`.trim();
          maddeler.push({
            id: `b_${u.uid}`, tip: "birthday",
            baslik: ad, altBilgi: "",
            zaman: t("daily.popup.today"), oncelik: 1, gecikmisMi: false,
            tarihStr: bugunStr, sayfa: "takvim", celebrantUid: u.uid,
          });
        }
      });

      // Başlığı/tipi olmayan bozuk kayıtlar popup içinde boş satır üretmesin.
      const gecerliMaddeler = maddeler.filter((madde) =>
        madde &&
        typeof madde.tip === "string" &&
        typeof madde.baslik === "string" &&
        madde.baslik.trim().length > 0
      );

      // Veri gerçekten boşsa popup açılmaz ve "gösterildi" anahtarı yazılmaz.
      // İlk yükleme sırasında listeler henüz boş geldiyse ilgili state değiştiğinde
      // effect tekrar çalışır ve dolu içerik hazır olduğunda popup açılır.
      // İlk açılış kontrolü bu uygulama oturumu için tamamlandı. Aktif aile sonradan
      // değişse bile günlük popup yeniden değerlendirilmez.
      gunlukPopupAttemptRef.current = { uid: String(uid), date: bugunStr, attempted: true };

      if (gecerliMaddeler.length === 0) {
        setGunlukMaddeler([]);
        setGunlukPopupAcik(false);
        return;
      }

      // Önce öncelik (gecikmiş→bugün→yarın→ilerisi), sonra tip, sonra alfabetik
      gecerliMaddeler.sort((a, b) =>
        a.oncelik - b.oncelik ||
        a.tip.localeCompare(b.tip) ||
        a.baslik.localeCompare(b.baslik, "tr")
      );

      let backendClaimed = true;
      try {
        const claim = await apiRequest('/auth/daily-reminder/claim', {
          method: 'POST',
          body: JSON.stringify({
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Istanbul',
          }),
        });
        backendClaimed = claim?.claimed === true;
        // Backend aynı gün daha önce gösterildiğini söylüyorsa cihaz anahtarını da eşitle.
        if (!backendClaimed) localStorage.setItem(storageKey, '1');
      } catch (_) {
        // Geçici backend/ağ hatasında mevcut cihaz davranışı korunur. POST otomatik retry
        // edilmez; çift claim/yan etki riski oluşturulmaz.
        backendClaimed = true;
      }

      if (cancelled || String(user?.uid || '') !== String(uid)) return;
      if (!backendClaimed) {
        setGunlukMaddeler([]);
        setGunlukPopupAcik(false);
        return;
      }

      setGunlukMaddeler(gecerliMaddeler.slice(0, 15));
      setGunlukPopupAcik(true);
      localStorage.setItem(storageKey, '1');
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, familyId, yoneticiMi, family_.basariMesaji, family_.membersLoading, appTourPending, appTourActive, appData.initialDataLoading, appData.initialDataFamilyId, appData.popupDataLoading, appData.popupDataFamilyId, appData.gorevler?.length, appData.takvimEtkinlikleri?.length, appData.healthRecords?.length, family_.uyeler?.length, birthdayExperienceReady, birthdayCelebration, birthdayDetailId, birthdayOwnCelebrationShown]);

  /* ── App Store / Google Play değerlendirme uygunluğu ──
     Backend gerçek hesap yaşı, farklı kullanım günleri, anlamlı işlem puanı,
     sürüm ve cooldown koşullarını değerlendirir. Native istek yalnızca yeni
     bir değer anından sonra, ekran sakinleştiğinde yapılır. */
  useEffect(() => {
    if (!user?.uid) return undefined;
    const recordVisibleDay = () => {
      if (!document.hidden) recordAppUsageDay(user.uid);
    };
    recordVisibleDay();
    void refreshAppReviewEligibility(user.uid).catch(() => {});
    document.addEventListener("visibilitychange", recordVisibleDay);
    return () => document.removeEventListener("visibilitychange", recordVisibleDay);
  }, [user?.uid]);

  useEffect(() => subscribeAppReviewUsage((event) => {
    if (event?.detail?.trigger && event?.detail?.eligibility?.eligible) {
      reviewCandidateTriggerRef.current = event.detail.trigger;
    }
    setReviewEligibilityTick((value) => value + 1);
  }), []);

  useEffect(() => {
    const markInteraction = () => { lastReviewInteractionAtRef.current = Date.now(); };
    window.addEventListener("pointerdown", markInteraction, { passive: true });
    window.addEventListener("touchstart", markInteraction, { passive: true });
    window.addEventListener("keydown", markInteraction);
    return () => {
      window.removeEventListener("pointerdown", markInteraction);
      window.removeEventListener("touchstart", markInteraction);
      window.removeEventListener("keydown", markInteraction);
    };
  }, []);

  useEffect(() => {
    const safeReviewPages = new Set(["ana", "gorev", "alisveris", "takvim", "belgeler", "finans"]);
    if (!user?.uid || !familyId || !safeReviewPages.has(aktifSayfa)) return undefined;
    if (premiumStartupVisible || loginStartupVisible || startupSplashVisible) return undefined;
    if (menuAcik || ayarlarAcik || premiumPopup.visible || notifs.bildirimPopupAcik) return undefined;
    if (gunlukPopupAcik || birthdayCelebration || birthdayDetailId || appTourPending || appTourActive || family_.basariMesaji) return undefined;
    if (noFamilyDeleteOpen || adminMode || reviewRequestInFlightRef.current) return undefined;
    if (appData.initialDataLoading || appData.popupDataLoading || family_.membersLoading) return undefined;
    if (!reviewCandidateTriggerRef.current) return undefined;
    if (!getAppReviewEligibility(user.uid).eligible) return undefined;

    let cancelled = false;
    let idleId = null;
    let retryId = null;
    let retryCount = 0;
    const runCandidate = () => {
      const runWhenIdle = () => {
        if (cancelled || reviewRequestInFlightRef.current) return;
        if (aktifSayfa === "ana" && !gunlukPopupAttemptRef.current.attempted) return;
        const focused = document.activeElement;
        const blocked =
          Date.now() - lastReviewInteractionAtRef.current < 1800 ||
          (focused && ["INPUT", "TEXTAREA", "SELECT"].includes(focused.tagName)) ||
          Boolean(document.querySelector(".modalOverlay, .profilPanelOverlay, .appDialogOverlay, .premOverlay, .bildirimPopupOverlay"));
        if (blocked) {
          if (retryCount < 10) {
            retryCount += 1;
            retryId = window.setTimeout(runCandidate, 1200);
          }
          return;
        }
        reviewRequestInFlightRef.current = true;
        const trigger = reviewCandidateTriggerRef.current;
        reviewCandidateTriggerRef.current = "";
        void requestNativeAppReview(user.uid, trigger)
          .finally(() => { reviewRequestInFlightRef.current = false; });
      };
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(runWhenIdle, { timeout: 1800 });
      } else {
        runWhenIdle();
      }
    };
    const timerId = window.setTimeout(runCandidate, 2000);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
      if (retryId !== null) window.clearTimeout(retryId);
      if (idleId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [user?.uid, familyId, aktifSayfa, premiumStartupVisible, loginStartupVisible, startupSplashVisible, menuAcik, ayarlarAcik, premiumPopup.visible, notifs.bildirimPopupAcik, gunlukPopupAcik, birthdayCelebration, birthdayDetailId, appTourPending, appTourActive, family_.basariMesaji, noFamilyDeleteOpen, adminMode, appData.initialDataLoading, appData.popupDataLoading, family_.membersLoading, reviewEligibilityTick]);

  /* ── Hoşgeldin konfeti — aile oluşturma / aileye katılma ── */
  useEffect(() => {
    if (!family_.basariMesaji) return;
    setKonfetiAktif(true);
  }, [family_.basariMesaji]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── PWA bildirim aboneliğini otomatik hazırla ── */
useEffect(() => {
  if (!user?.uid || !familyId) return;

  let iptal = false;

  const timer = setTimeout(() => {
    const otomatikBildirimHazirla = async () => {
      try {
        // silent:true → izin zaten varsa sessizce tüm ailelere kaydeder,
        // yoksa popup çıkarmaz ve açılışı bekletmez.
        // lang bağımlılığı: kullanıcı dili değiştirdiğinde subscription'ı backend'e
        // güncel locale ile tekrar kaydeder, yoksa push_devices.locale eski dilde kalır.
        await requestAndSaveNotificationToken({ user, familyId, silent: true });
      } catch (error) {
        if (!iptal) {
          console.warn("PWA bildirim otomatik hazırlanamadı:", error?.message || error);
        }
      }
    };

    otomatikBildirimHazirla();
  }, 2500);

  return () => {
    iptal = true;
    clearTimeout(timer);
  };
  // profil.families değiştiğinde (yeni aile eklendi) VEYA dil değiştiğinde tekrar çalıştır
}, [user, familyId, profil?.families?.length, lang]);

    /* ── Native push token kaydı (Capacitor iOS) ──
       NOT: Native push kaydı artık tek kaynaktan yönetiliyor: useNativePush hook'u
       (aşağıda, mesaj dinleyicisiyle birlikte çağrılıyor). Eskiden burada ayrıca
       registerNativePush() de çağrılıyordu; bu, aynı cihaz için farklı device_id
       ile İKİNCİ bir push_devices kaydı oluşturuyordu (ios_${uid} vs random UUID).
       Sonuç: aynı bildirim bazen iki kez, bazen eski/güncel olmayan bir kayda
       gidiyordu ve dil her zaman doğru yansımıyordu. useNativePush hook'u zaten
       lang değişimini izliyor ve duplicate-call koruması var, bu yüzden burada
       ikinci bir kayıt mekanizmasına gerek yok. */

  /* ══════════════════════════════════════════════════════════════
     NATIVE iOS SWIPE-BACK + SAĞ KENAR MENU
     ══════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const swipeEngelliMi = (target) => !!(
      target?.closest?.(".noSwipeBack") ||
      target?.closest?.("[data-no-swipe-back='true']") ||
      target?.closest?.("input") ||
      target?.closest?.("textarea") ||
      target?.closest?.("select") ||
      target?.closest?.("[role='slider']") ||
      target?.closest?.(".swipeArea") ||
      target?.closest?.(".swiper") ||
      target?.closest?.(".mapboxgl-map") ||
      target?.closest?.(".leaflet-container") ||
      target?.closest?.(".pac-container")
    );

    let startX = 0, startY = 0, startTime = 0;
    let trackingBack = false, trackingMenu = false;
    let gestureActive = false, dirLocked = false;
    let screenW = window.innerWidth;


    const onStart = (e) => {
      screenW = window.innerWidth;
      // Swipe aktifken yeni dokunuş gelirse (sağdan parmak) iptal et
      if (gestureActive) {
        gestureActive = false;
        trackingBack = false;
        shadow(false);
        snapCancel(0);
        return;
      }
      if (swipeEngelliMi(e.target) || document.body.classList.contains("map-open")) {
        trackingBack = trackingMenu = false; return;
      }
      const t = e.touches[0];
      startX = t.clientX; startY = t.clientY; startTime = e.timeStamp;
      gestureActive = false; dirLocked = false;
      trackingBack = startX < screenW * 0.12 && aktifSayfaRef.current !== "ana" && !menuAcikRef.current;
      trackingMenu = startX > screenW * 0.93 && !menuAcikRef.current;

      // Bg modunu belirle: iç içe sayfada mıyız?
      if (trackingBack) {
        const s = aktifSayfaRef.current;
        const isSubpage = (
          (s === "chat"     && (typeof window.__chatRoomCommitBack === "function" || typeof window.__chatRoomBack === "function")) ||
          (s === "takvim"   && calendarIntegrationOpenRef.current) ||
          (s === "finans"   && typeof window.__financeSubpageBack === "function") ||
          (s === "saglik"   && typeof window.__healthDetailCommitBack === "function") ||
          (s === "cocuk"    && typeof window.__childrenDetailCommitBack === "function") ||
          (s === "belgeler" && typeof window.__documentsDetailCommitBack === "function")
        );
        swipeBgModeRef.current = isSubpage ? s : "ana";
      }
    };

    const onMove = (e) => {
      if (!trackingBack) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (!dirLocked) {
        if (Math.abs(dx) < 5 && dy < 5) return;
        if (dy > Math.abs(dx) * 1.4) { trackingBack = false; return; }
        dirLocked = true;
      }
      if (dx <= 0) return;
      // Yatay gesture onaylandı — dikey scroll'u engelle
      e.preventDefault();
      if (!gestureActive) {
        gestureActive = true;
        if (swipeBgModeRef.current !== "chat") showSwipeBottomFill();
        shadow(true);
        // Mode'u yaz → React render → tarayıcı boyar → BG göster
        // 2 rAF: ilk frame React state işler, ikinci frame DOM boyar
        setSwipeBgMode(swipeBgModeRef.current);
        // Chat odasında gerçek liste/header aynı aktif wrapper altında hazırdır.
        // Harici kopyayı göstermek iki farklı layout'un üst üste binmesine neden olur.
        if (swipeBgModeRef.current !== "chat") {
          requestAnimationFrame(() => requestAnimationFrame(() => showBg()));
        }
      }
      setDrag(dx);
    };

    const onEnd = (e) => {
      if (trackingMenu) {
        trackingMenu = false;
        const t = e.changedTouches[0];
        if (t.clientX - startX < -55 && Math.abs(t.clientY - startY) < 80) setMenuAcik(true);
        return;
      }
      if (!trackingBack) return;
      trackingBack = false;
      shadow(false);
      if (!gestureActive) return;
      gestureActive = false;

      const t   = e.changedTouches[0];
      const dx  = t.clientX - startX;
      const dy  = Math.abs(t.clientY - startY);
      const vel = dx / (e.timeStamp - startTime);
      const confirmed = (dx > screenW * 0.35 || (vel > 0.5 && dx > 40)) && dy < 120;

      if (confirmed) {
        const s = aktifSayfaRef.current;
        if (s === "finans" && typeof window.__financeSubpageBack === "function") {
          // Finans alt sekme → genel'e dön
          // __financeSubpageBack'i şu an yakala — timeout sırasında null olabilir
          const _finBack = window.__financeSubpageBack;
          snapComplete(dx, () => { if (typeof _finBack === "function") _finBack(); }, "sekme");
        } else if (s === "takvim" && calendarIntegrationOpenRef.current) {
          snapComplete(dx, () => { closeCalendarIntegration(); sayfaEnUsteAl(); }, true);
        } else if (s === "chat" && (typeof window.__chatRoomCommitBack === "function" || typeof window.__chatRoomBack === "function")) {
          const _chatCommitBack = window.__chatRoomCommitBack || window.__chatRoomBack;
          snapComplete(dx, () => {
            if (typeof _chatCommitBack === "function") _chatCommitBack();
            setChatRoomAcik(false);
            sayfaEnUsteAl();
          }, true);
        } else if (s === "saglik" && typeof window.__healthDetailCommitBack === "function") {
          snapComplete(dx, () => { window.__healthDetailCommitBack(); sayfaEnUsteAl(); setHealthDetailAcik(false); }, true);
        } else if (s === "cocuk" && typeof window.__childrenDetailCommitBack === "function") {
          snapComplete(dx, () => { window.__childrenDetailCommitBack(); sayfaEnUsteAl(); setChildrenDetailAcik(false); }, true);
        } else if (s === "belgeler" && typeof window.__documentsDetailCommitBack === "function") {
          snapComplete(dx, () => { window.__documentsDetailCommitBack(); sayfaEnUsteAl(); setDocumentsDetailAcik(false); }, true);
        } else {
          const backTarget = assistantBackTarget();
          snapComplete(dx, () => { sayfaEnUsteAl(); setAktifSayfa(backTarget); });
        }
      } else {
        snapCancel(dx);
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove",  onMove,  { passive: false });
    document.addEventListener("touchend",   onEnd,   { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove",  onMove);
      document.removeEventListener("touchend",   onEnd);
    };
  }, []); // eslint-disable-line

  /* ── Push URL yönlendirmesi ── */
  useEffect(() => {
    if (!user || !familyId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("pushPage")) notifs.pushUrlYonlendir(window.location.href, familyId, user?.uid);
  }, [user, familyId]); // eslint-disable-line

  useEffect(() => {
  if (!user?.uid || !familyId) return;

  const mesajDinle = (event) => {
    if (event.data?.type !== "FP_PUSH_NAVIGATE") return;

    notifs.pushUrlYonlendir(
      event.data.url,
      familyId,
      user.uid
    );
  };

  navigator.serviceWorker?.addEventListener?.("message", mesajDinle);

  return () => {
    navigator.serviceWorker?.removeEventListener?.("message", mesajDinle);
  };
}, [user?.uid, familyId]); // eslint-disable-line
    
  const switchFamilyWithExistingUx = async (targetFamilyId, options = {}) => {
    const startupTransitionActive = startupSplashVisible || loginStartupVisible;
    if (targetFamilyId
        && String(targetFamilyId) !== String(familyId || "")
        && !startupTransitionActive) {
      setAileGecisCikabilir(false);
      setAileGecisIcerikGoster(false);
      setAileGecisHedefId(String(targetFamilyId));
      setAileGecisRunKey((k) => k + 1);
      setAileGecisSplashVisible(true);
      appData.hydrateDashboardCache(
        String(targetFamilyId),
        String(user?.uid || user?.firebase_uid || user?.id || "").trim()
      );
    }
    const snapshot = await family_.switchFamily(targetFamilyId, options);
    if (snapshot) {
      pagePermissionCheckRef.current = (pageKey) => familySnapshotAllowsPage(snapshot, pageKey);
    }
    if (snapshot?.familyId
        && String(snapshot.familyId) !== String(targetFamilyId || "")
        && !startupTransitionActive) {
      setAileGecisHedefId(String(snapshot.familyId));
      appData.hydrateDashboardCache(
        String(snapshot.familyId),
        String(user?.uid || user?.firebase_uid || user?.id || "").trim()
      );
    }
    return snapshot;
  };

    /* ── Native push listener'ları (Capacitor iOS) ── */
      useNativePush({
        user,
        familyId,
        lang,
        pushUrlYonlendir: notifs.pushUrlYonlendir,
        bildirimGoster:   notifs.bildirimGoster,
        switchFamily: switchFamilyWithExistingUx,
        navigationReady: !!(
          (user?.uid || user?.firebase_uid || user?.id)
          && familyId
          && familyResolved
          && !loadingFamily
          && !authKontrolEdiliyor
          && !startupSplashVisible
          && !loginStartupVisible
          && !aileGecisSplashVisible
        ),
      });

  const notificationTikla = async (notif) => {
    const targetFamilyId = notif?.familyId || notif?.familyFirebaseId
      || notif?.data?.familyId || notif?.data?.familyFirebaseId || null;
    let navigationFamilyId = familyId;
    if (targetFamilyId && String(targetFamilyId) !== String(familyId || "")) {
      const snapshot = await switchFamilyWithExistingUx(targetFamilyId, { reason: "notification" });
      navigationFamilyId = snapshot.familyId;
    }
    notifs.bildirimdenGit(notif, navigationFamilyId, user?.uid);
  };

  /* ── Yardımcı değişkenler ── */
  const tamAd = `${profil?.ad || ""} ${profil?.soyad || ""}`.trim();

  const refreshExternalCalendarEvents = useCallback(async () => {
    const preferences = getCalendarIntegrationPreferences();
    if (!preferences.enabled || !preferences.selectedCalendarIds.length) {
      setExternalCalendarEvents([]);
      return [];
    }
    setExternalCalendarRefreshing(true);
    const start = new Date();
    start.setFullYear(start.getFullYear() - 1);
    const end = new Date();
    end.setFullYear(end.getFullYear() + 2);
    try {
      const events = await readDeviceCalendarEvents({
        calendarIds: preferences.selectedCalendarIds,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });
      setExternalCalendarEvents(events);
      saveCalendarIntegrationPreferences({ ...preferences, lastSyncedAt: new Date().toISOString() });
      return events;
    } catch (error) {
      setExternalCalendarEvents([]);
      if (!["CALENDAR_NATIVE_ONLY", "CALENDAR_BRIDGE_NOT_READY"].includes(error?.code)) {
        console.warn("[CalendarIntegration] Cihaz etkinlikleri okunamadı:", error?.code || error?.message);
      }
      return [];
    } finally {
      setExternalCalendarRefreshing(false);
    }
  }, []);

  const importedExternalKeys = useMemo(() => new Set(
    (appData.takvimEtkinlikleri || []).map((event) => event?.externalImportKey).filter(Boolean)
  ), [appData.takvimEtkinlikleri]);
  const importableExternalEvents = useMemo(() => externalCalendarEvents.filter(
    (event) => event?.externalImportKey && !importedExternalKeys.has(event.externalImportKey)
  ), [externalCalendarEvents, importedExternalKeys]);

  const importAllExternalCalendarEvents = useCallback(async () => {
    if (externalCalendarImporting || !importableExternalEvents.length) return;
    if (!permissions?.actions?.addCalendarEvent) {
      await appAlert(lang === "en" ? "You do not have permission to add family calendar events." : "Aile takvimine etkinlik ekleme yetkiniz yok.");
      return;
    }
    const confirmed = await appConfirm(
      lang === "en"
        ? `${importableExternalEvents.length} device events will be copied to the family calendar. Family members will receive one summary notification. Continue?`
        : `${importableExternalEvents.length} cihaz etkinliği aile takvimine aktarılacak. Aile üyelerine yalnızca tek bir özet bildirim gönderilecek. Devam edilsin mi?`,
      { title: lang === "en" ? "Copy all events" : "Tüm etkinlikleri aktar", confirmText: lang === "en" ? "Copy all" : "Tümünü aktar" }
    );
    if (!confirmed) return;
    setExternalCalendarImporting(true);
    try {
      const result = await takvimHariciEtkinlikleriTopluAktarService({ familyId, events: importableExternalEvents, tamAd, user });
      const importedCount = Number(result?.importedCount || 0);
      if (importedCount > 0) {
        await bildirimTakvimTopluAktarimService({
          familyId, ekleyenUid: user?.uid, tamAd, count: importedCount,
          idempotencyKey: `calendar_bulk_import_${result.batchId || Date.now()}`,
        }).catch((error) => console.warn("[CalendarIntegration] Özet bildirim gönderilemedi:", error?.message));
      }
      await appAlert(lang === "en"
        ? `${importedCount} events were copied. ${Number(result?.duplicateCount || 0)} existing events were skipped.`
        : `${importedCount} etkinlik aktarıldı. Daha önce aktarılmış ${Number(result?.duplicateCount || 0)} etkinlik atlandı.`);
    } catch (error) {
      await appAlert(error?.message || (lang === "en" ? "Events could not be copied." : "Etkinlikler aktarılamadı."));
    } finally {
      setExternalCalendarImporting(false);
    }
  }, [externalCalendarImporting, importableExternalEvents, permissions, lang, familyId, tamAd, user]);

  useEffect(() => {
    refreshExternalCalendarEvents();
  }, [calendarIntegrationRevision, aktifSayfa, refreshExternalCalendarEvents, user?.uid]);

  useEffect(() => {
    let handle;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive && aktifSayfaRef.current === "takvim" && !calendarIntegrationOpenRef.current) {
        setCalendarIntegrationRevision((value) => value + 1);
      }
    }).then((listener) => { handle = listener; }).catch(() => {});
    return () => { handle?.remove?.(); };
  }, [refreshExternalCalendarEvents]);

  const paraFormatla = (sayi) => {
    const formatted = formatMoneyDisplayNumber(sayi, 2);
    if (paraBirimi === "USD") return "$" + formatted;
    if (paraBirimi === "EUR") return "€" + formatted;
    if (paraBirimi === "GBP") return "£" + formatted;
    return formatted + " ₺";
  };

  /* ── Sayfa başlıkları ── */
  const sayfaBasliklari = {
    ana: family_.familyName,
    alisveris: t("nav.shopping"),
    takvim: t("nav.calendar"),
    finans: t("nav.finance"),
    gorev: t("nav.tasks"),
    harcama: t("nav.sharedExpenses"),
    saglik: t("nav.health"),
    harita: t("nav.map"),
    cocuk: t("nav.children"),
    chat: t("nav.chat"),
    belgeler: t("nav.documents"),
    nePisirsam: t("nav.cooking"),
    activity: t("nav.activity"),
    assistant: t("nav.assistant"),
    kullaniciYonetimi: t("nav.management"),
    adminDashboard: "Admin",
    legal: t("settings.privacy.legalDocuments"),
    feedback: t("feedback.navTitle"),
  };

  /* ═══════════ ALIŞVERİŞ STATE & FONKSİYONLAR ═══════════ */

  const [alisverisFormAcik, setAlisverisFormAcik] = useState(false);
  const [urunAdi, setUrunAdi]         = useState("");
  const [urunMiktar, setUrunMiktar]   = useState("1");
  const [urunBirim, setUrunBirim]     = useState("kg");
  const [urunKategori, setUrunKategori] = useState("Market");
  const [seciliKategori, setSeciliKategori] = useState("Tümü");

  const urunEkle = async () => {
    if (!urunAdi.trim() || !urunMiktar || !familyId) return;
    const bekleyenSayisi = appData.urunler.length; // toplam (alınan + bekleyen)
    const limitKontrol = premium.checkLimit("alisverisOge", bekleyenSayisi);
    if (!limitKontrol.allowed) {
      showPremiumLimit(t("premium.limit.shoppingItems"), bekleyenSayisi, limitKontrol.limit, [t("premium.benefit.unlimitedShopping"), t("premium.benefit.shoppingCategories"), t("premium.benefit.shoppingHistory")], undefined, [t("premium.plus.benefit.shoppingSync"), t("premium.plus.benefit.shoppingAssign"), t("premium.plus.benefit.shoppingBudget")]);
      return;
    }
    const yeniUrunId = await urunEkleService({ familyId, urunAdi, urunMiktar, urunBirim, urunKategori, tamAd, user, profilAvatar: profil?.avatar });
    trackCoreAction("shopping_item_added");
    // Bildirim useAppData.js polling listener'ında gönderiliyor (çift bildirim önlenir)
    setUrunAdi(""); setUrunMiktar("1"); setUrunBirim("kg");
    return yeniUrunId;
  };

  const tarifMalzemeleriniAlisveriseEkle = async (urunler = []) => {
    const hazirUrunler = (Array.isArray(urunler) ? urunler : []).filter((urun) => urun?.ad).slice(0, 60);
    if (!familyId || !user || hazirUrunler.length === 0) return { count: 0, ids: [], error: "empty" };
    if (permissions?.actions?.addShopping !== true || !sayfaIzniVarMi("shopping")) {
      return { count: 0, ids: [], error: "permission" };
    }

    const mevcutUrunSayisi = appData.urunler.length;
    const limitKontrol = premium.checkLimit("alisverisOge", mevcutUrunSayisi);
    const kalanHak = Number.isFinite(limitKontrol.remaining) ? Math.max(0, limitKontrol.remaining) : Infinity;
    if (!limitKontrol.allowed || kalanHak < hazirUrunler.length) {
      showPremiumLimit(
        t("premium.limit.shoppingItems"),
        mevcutUrunSayisi,
        limitKontrol.limit,
        [t("premium.benefit.unlimitedShopping"), t("premium.benefit.shoppingCategories"), t("premium.benefit.shoppingHistory")],
        undefined,
        [t("premium.plus.benefit.shoppingSync"), t("premium.plus.benefit.shoppingAssign"), t("premium.plus.benefit.shoppingBudget")]
      );
      return { count: 0, ids: [], error: "limit" };
    }

    return urunleriTopluEkleService({
      familyId,
      urunler: hazirUrunler,
      tamAd,
      profilAvatar: profil?.avatar,
      user,
      source: "cooking",
    });
  };

  const urunSil = async (urun) => {
    if (!silebilirMi(urun)) return appAlert(t("common.noDeletePermission"));
    await urunSilService({ familyId, urunId: urun.id, urun, user, tamAd, profilAvatar: profil?.avatar });
  };

  const alindiDegistir = async (urun) => {
    await alindiDegistirService({ familyId, urun, user, tamAd, profilAvatar: profil?.avatar });
    const yeniDurumAlindi = !urun.alindi;
    if (!yeniDurumAlindi) return;
    notifs.bildirimGoster(t("notif.itemBought.title"), t("notif.itemBought.body", { item: urun.ad }), "shopping");
    const alisverisGorebilenUyeler = uyelerRef.current.filter((uye) => {
      const uid = uye.uid || uye.id;
      return uid && uid !== user?.uid && uye.permissions?.pages?.shopping !== false;
    });
    await bildirimHerkeseYazService({
      familyId, uyeler: alisverisGorebilenUyeler, ekleyenUid: user.uid,
      baslik: t("notif.itemBought.title"), aciklama: t("notif.itemBought.by", { name: tamAd, item: urun.ad }),
      baslikKey: "notif.itemBought.title", aciklamaKey: "notif.itemBought.by", aciklamaVars: { name: tamAd, item: urun.ad },
      type: "shopping", kaynak: "shopping", kaynakId: urun.id, gonderenUid: user.uid,
      familyName: family_.familyName || "",
    }).catch(() => {});
  };

  const filtreliUrunler = seciliKategori === "Tümü" ? appData.urunler : appData.urunler.filter((u) => u.kategori === seciliKategori);
  const toplamUrun  = appData.urunler.length;
  const alinanUrun  = appData.urunler.filter((u) => u.alindi).length;
  const bekleyenUrun = toplamUrun - alinanUrun;

  const cocukKartSayisi = useMemo(() => {
    const uyeCocuklari = family_.uyeler.filter((u) => ["Çocuk", "Bebek", "Child", "Baby"].includes(u.profilRol || u.rol)).length;
    return uyeCocuklari + (appData.manualChildren?.length || 0);
  }, [family_.uyeler, appData.manualChildren]);

  const cocukTakipKaydi = useMemo(() => {
    const memberChildren = family_.uyeler
      .filter((u) => ["Çocuk", "Bebek", "Child", "Baby"].includes(u.profilRol || u.rol))
      .map((u) => ({
        id: u.uid || u.id,
        memberUid: u.uid || u.id,
        sourceType: "member",
      }));
    const manualChildren = (appData.manualChildren || []).map((child) => ({
      ...child,
      sourceType: "manual",
    }));
    return totalChildTrackingRecordCount(
      [...memberChildren, ...manualChildren],
      appData.childRecords || [],
      appData.healthRecords || []
    );
  }, [family_.uyeler, appData.manualChildren, appData.childRecords, appData.healthRecords]);

  const aktifKonumPaylasan = useMemo(() => {
    return Object.values(appData.locations || {}).filter((lok) => lok?.paylasimAcik === true && lok?.lat != null && lok?.lng != null).length;
  }, [appData.locations]);

  /* ═══════════ GÖREV STATE & FONKSİYONLAR ═══════════ */

  const [gorevFormAcik, setGorevFormAcik]   = useState(false);
  // Görev formu açılınca kullanıcı varsayılan atanan olarak seçili gelsin
  const gorevFormAcikSet = (val) => {
    if (val && gorevAtananlar.length === 0 && user?.uid) {
      setGorevAtananlar([user.uid]);
    }
    setGorevFormAcik(val);
  };
  const [gorevAdi, setGorevAdi]             = useState("");
  const [gorevAciklama, setGorevAciklama]   = useState("");
  const [gorevAtananlar, setGorevAtananlar] = useState([]); // çoklu atama — form açılınca user.uid ile init edilir
  const [gorevSonTarih, setGorevSonTarih]   = useState("");
  const [gorevOncelik, setGorevOncelik]     = useState("Normal");
  const [gorevFiltresi, setGorevFiltresi]   = useState("Tümü");
  const [gorevKategori, setGorevKategori]   = useState("Diğer");

  const gorevEkle = async () => {
    if (!gorevAdi || gorevAtananlar.length === 0 || !gorevSonTarih) return;
    const aktifGorevSayisi = appData.gorevler.filter((g) => !g.tamamlandi).length;
    const limitKontrol = premium.checkLimit("aktifGorev", aktifGorevSayisi);
    if (!limitKontrol.allowed) {
      showPremiumLimit(t("premium.limit.activeTasks"), aktifGorevSayisi, limitKontrol.limit, [t("premium.benefit.unlimitedTasks"), t("premium.benefit.taskPriority"), t("premium.benefit.taskReminders")], undefined, [t("premium.plus.benefit.taskAssign"), t("premium.plus.benefit.taskRepeat"), t("premium.plus.benefit.taskStats")]);
      return;
    }
    const gorevAtananSet = new Set((gorevAtananlar || []).map(String));
    const atananUyeler = family_.uyeler.filter((u) => gorevAtananSet.has(String(u.uid || u.id || "")));
    const tumAileSecili = family_.uyeler.length > 1 && atananUyeler.length === family_.uyeler.filter((u) => u?.uid || u?.id).length;
    const yeniGorevId = await gorevEkleService({
      familyId, gorevAdi, gorevAciklama, gorevAtananlar: [...gorevAtananSet], gorevSonTarih, gorevOncelik, gorevKategori,
      atananUyeler, tumAileSecili, tumAileEtiketi: lang === "en" ? "All family" : "Tüm aile",
      tamAd, user, profilAvatar: profil?.avatar
    });
    trackCoreAction("task_created");
    // Bildirim useAppData.js polling listener'ında gönderiliyor (çift bildirim önlenir)
    setGorevAdi(""); setGorevAciklama(""); setGorevAtananlar(user?.uid ? [user.uid] : []); setGorevSonTarih(""); setGorevKategori("Diğer");
    return yeniGorevId;
  };

  const gorevTamamla = async (g) => {
    await gorevTamamlaService({ familyId, gorev: g, user, tamAd, profilAvatar: profil?.avatar });
    // Bildirim useAppData.js polling listener'ında gönderiliyor (çift bildirim önlenir)
  };

  const gorevSil = async (g) => {
    if (!silebilirMi(g)) return appAlert(t("common.noDeletePermission"));
    await gorevSilService({ familyId, gorevId: g.id, gorev: g, user, tamAd, profilAvatar: profil?.avatar });
  };

  /* ═══════════ KULLANICI TARAFLI HATIRLATMA ═══════════ */
  const onReminderKur = async ({
    baslik,
    aciklama,
    baslikKey = null,
    baslikVars = null,
    aciklamaKey = null,
    aciklamaVars = null,
    planlananTarih,
    tekrarTipi,
    kaynak = "userReminder",
    kaynakId = null,
    kaynakTuru = null,
    bildirimType = null,
    url = "/",
    idempotencyKey = null,
    extraData = null,
  }) => {
    if (!user?.uid || !familyId || !planlananTarih) return null;

    const kaynakBildirimTypeMap = {
      shopping: "shopping",
      task: "taskReminder",
      calendar: "calendarReminder",
      finance: "finance",
      sharedExpense: "sharedExpense",
    };
    const resolvedBildirimType = bildirimType || kaynakBildirimTypeMap[kaynak] || "scheduled";

    return zamanliBildirimOlustur({
      hedefTipi: "users",
      hedefUidler: [user.uid],
      hedefSayisi: 1,
      baslik,
      aciklama,
      baslikKey,
      baslikVars,
      aciklamaKey,
      aciklamaVars,
      planlananTarih,
      tekrarTipi: tekrarTipi || "none",
      tip: "userReminder",
      bildirimTipi: "scheduled",
      bildirimType: resolvedBildirimType,
      kaynak,
      kaynakId,
      kaynakTuru,
      olusturanUid: user.uid,
      familyId,
      url,
      idempotencyKey,
      extraData,
    });
  };

  const gecikmisMi = (g) =>
    !g.tamamlandi && g.sonTarih && new Date(g.sonTarih) < new Date(new Date().toISOString().slice(0, 10));


  // Ana sayfa takvim kartı ve widget için yaklaşan kayıtlar; takvim,
  // görev ve sağlık kaynaklarından tek bir birleşik liste halinde oluşturulur.
  // Aynı kaynak backend tarafından gerçek takvim event'ine dönüştürülmüşse
  // sanal görev/sağlık kaydı ikinci kez sayılmaz.
  const yaklasanTakvimEtkinlikleri = (() => {
    const baslangic = new Date();
    baslangic.setHours(0, 0, 0, 0);
    const bitis = new Date(baslangic);
    bitis.setDate(bitis.getDate() + 7);
    bitis.setHours(23, 59, 59, 999);

    const dizi = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
    const id = (value) => value === null || value === undefined ? "" : String(value).trim();
    const kayitId = (record = {}) => id(record.id || record.firebaseId || record._id);
    const tarihDegeri = (record = {}) =>
      record.tarih || record.date || record.startDate || record.appointmentDate ||
      record.sonTarih || record.dueDate || record.son_tarih || "";

    const tarihOku = (value) => {
      if (!value) return null;
      if (value instanceof Date) {
        const copy = new Date(value);
        return Number.isNaN(copy.getTime()) ? null : copy;
      }
      if (typeof value === "object") {
        if (typeof value.toDate === "function") return tarihOku(value.toDate());
        if (value.seconds !== undefined) return tarihOku(new Date(Number(value.seconds) * 1000));
      }
      const raw = String(value).trim();
      const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const parsed = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(raw);
      if (Number.isNaN(parsed.getTime())) return null;
      parsed.setHours(0, 0, 0, 0);
      return parsed;
    };

    const yediGunIcinde = (record) => {
      const date = tarihOku(tarihDegeri(record));
      return Boolean(date && date >= baslangic && date <= bitis);
    };

    const calendarEvents = dizi(appData.takvimEtkinlikleri);
    const tasks = dizi(appData.gorevler);
    const healthRecords = dizi(appData.healthRecords);

    const calendarEventIds = new Set(calendarEvents.map(kayitId).filter(Boolean));
    const linkedTaskIds = new Set();
    const linkedHealthIds = new Set();

    calendarEvents.forEach((event) => {
      [event.linkedTaskId, event.taskId, event.linked_task_id, event.task_id,
       String(event.sourceType || "").toLowerCase() === "task" ? event.sourceId : null]
        .map(id).filter(Boolean).forEach((value) => linkedTaskIds.add(value));
      [event.linkedHealthRecordId, event.healthRecordId, event.linked_health_record_id, event.health_record_id,
       String(event.sourceType || "").toLowerCase() === "health" ? event.sourceId : null]
        .map(id).filter(Boolean).forEach((value) => linkedHealthIds.add(value));
    });

    const records = [];
    calendarEvents.forEach((event) => records.push({ ...event, _source: "calendar" }));

    tasks.forEach((task) => {
      if (task.tamamlandi === true || task.completed === true) return;
      if (!tarihDegeri(task)) return;
      const taskId = kayitId(task);
      const linkedCalendarId = id(task.calendarEventId || task.linkedCalendarEventId || task.calendar_event_id || task.linked_calendar_event_id);
      if ((taskId && linkedTaskIds.has(taskId)) || (linkedCalendarId && calendarEventIds.has(linkedCalendarId))) return;
      records.push({ ...task, tarih: tarihDegeri(task), _source: "task" });
    });

    healthRecords.forEach((record) => {
      const linkedCalendarId = id(record.calendarEventId || record.linkedCalendarEventId || record.calendar_event_id || record.linked_calendar_event_id);
      const type = String(record.type || record.recordType || record.kayitTuru || "").toLowerCase();
      const calendarEnabled = record.addToCalendar === true || record.takvimeEkle === true || type === "appointment" || Boolean(linkedCalendarId);
      if (!calendarEnabled || !tarihDegeri(record)) return;
      const healthId = kayitId(record);
      if ((healthId && linkedHealthIds.has(healthId)) || (linkedCalendarId && calendarEventIds.has(linkedCalendarId))) return;
      records.push({ ...record, tarih: tarihDegeri(record), _source: "health" });
    });

    const seen = new Set();
    return records.filter(yediGunIcinde).filter((record) => {
      const source = record._source || "calendar";
      const recordId = kayitId(record);
      const rawTitle = record.baslik || record.title || record.ad || record.doctorName || record.department || "";
      const normalizedTitle = String(rawTitle).trim().toLocaleLowerCase("tr-TR");
      const date = tarihOku(tarihDegeri(record));
      const dateKey = date ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` : "";
      const key = recordId ? `${source}:${recordId}` : `${source}:${dateKey}:${normalizedTitle}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  // Ana sayfa ve Aile Asistanı kartlarının kullandığı özet değerler.
  // Veri yenileme sırasında koleksiyonlar kısa süreliğine boş/undefined kalabilse de
  // render ve build akışı güvenli biçimde devam eder.
  const guvenliTakvimEtkinlikleri = Array.isArray(appData.takvimEtkinlikleri)
    ? appData.takvimEtkinlikleri.filter(Boolean)
    : [];
  const buAyTakvimEtkinlikSayisi = guvenliTakvimEtkinlikleri.filter((e) => {
    const hamTarih = e?.tarih || e?.date || e?.startDate || e?.start_date;
    if (!hamTarih) return false;
    const d = new Date(hamTarih);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;

  const guvenliSistemBildirimleri = Array.isArray(notifs.merkeziBildirimler)
    ? notifs.merkeziBildirimler.filter(Boolean)
    : [];
  const guvenliSohbetBildirimleri = Array.isArray(notifs.merkeziChatBildirimler)
    ? notifs.merkeziChatBildirimler.filter(Boolean)
    : [];
  const okunmamisSistemBildirimSayisi = guvenliSistemBildirimleri.filter((n) => !n?.okundu).length;
  const okunmamisSohbetBildirimSayisi = guvenliSohbetBildirimleri.filter((n) => !n?.okundu).length;
  const toplamBildirimSayisi = guvenliSistemBildirimleri.length + guvenliSohbetBildirimleri.length;
  const toplamOkunmamisBildirimSayisi = okunmamisSistemBildirimSayisi + okunmamisSohbetBildirimSayisi;

  // ── iOS App Icon Badge — bildirim merkezi ile eşzamanlı ──────────────────
  // toplamOkunmamisBildirimSayisi her değiştiğinde (okundu/silindi/yeni geldi)
  // native köprü üzerinden iOS badge güncellenir. Network isteği yok — saf JS→Native.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.webkit?.messageHandlers?.aileNativeBridge) return;
    try {
      window.webkit.messageHandlers.aileNativeBridge.postMessage({
        action: "setBadge",
        count: toplamOkunmamisBildirimSayisi,
      });
    } catch {}
  }, [toplamOkunmamisBildirimSayisi]); // eslint-disable-line

  // ── Badge-reset: push_devices.badge_count'u gerçek sayıyla güncelle ───────
  // Uygulama açılınca ve sayı değişince backend'deki sayaç senkronize edilir.
  // Sonraki push'ta bu sayıdan +1 artırılır — 62, 63, 64... sırayla gider.
  // ÖNEMLI: Sadece sayı > 0 iken çağır — ilk render'da veriler henüz
  // yüklenmemiş olabilir, 0 yazarsak badge_count sıfırlanır.
  useEffect(() => {
    if (!user?.uid || !familyId) return;
    if (toplamOkunmamisBildirimSayisi === 0) return; // veriler henüz yüklenmediyse çağırma
    apiRequest("/notifications/badge-reset", {
      method: "POST",
      body: JSON.stringify({ count: toplamOkunmamisBildirimSayisi }),
    }).catch(() => {});
  }, [toplamOkunmamisBildirimSayisi, user?.uid, familyId]); // eslint-disable-line

  const filtreliGorevler = (Array.isArray(appData.gorevler) ? appData.gorevler : []).filter((g) => {
    if (gorevFiltresi === "Bana Atanan")  return g.atananUid === user.uid && !g.tamamlandi;
    if (gorevFiltresi === "Tamamlanan")   return g.tamamlandi;
    if (gorevFiltresi === "Geciken")      return gecikmisMi(g);
    return true;
  });

  /* ═══════════ FİNANS STATE & FONKSİYONLAR ═══════════ */

  const [finansFormAcik, setFinansFormAcik]   = useState(false);
  const [finansBaslik, setFinansBaslik]       = useState("");
  const [finansTutar, setFinansTutar]         = useState("");
  const [finansTur, setFinansTur]             = useState("Gelir");
  const [finansKategori, setFinansKategori]   = useState("Maaş");
  const [finansTarih, setFinansTarih]         = useState(new Date().toISOString().slice(0, 10));
  const [finansTekrar, setFinansTekrar]       = useState({ enabled: false, frequency: "monthly", endDate: "" });
  const [finansPeriyot, setFinansPeriyot]     = useState("Aylık");
  const [finansSeciliAy, setFinansSeciliAy]   = useState(new Date().getMonth());
  const [finansSeciliYil, setFinansSeciliYil] = useState(new Date().getFullYear());
  const [ekKategori, setEkKategori]           = useState("");
  const [financeMarketPrices, setFinanceMarketPrices] = useState({});

  useEffect(() => {
    if (!familyId) return undefined;
    let active = true;
    getGlobalAssetPricesOnce()
      .then((data) => { if (active) setFinanceMarketPrices(data?.prices || {}); })
      .catch(() => {});
    return () => { active = false; };
  }, [familyId]);

  const finansEkle = async () => {
    if (!finansBaslik || !finansTutar) return;
    const buAy = new Date();
    const buAyKayit = appData.finanslar.filter((f) => {
      const d = new Date(f.tarih || f.createdAt?.seconds * 1000 || 0);
      return d.getFullYear() === buAy.getFullYear() && d.getMonth() === buAy.getMonth();
    }).length;
    const limitKontrol = premium.checkLimit("aylikFinans", buAyKayit);
    if (!limitKontrol.allowed) {
      showPremiumLimit(t("premium.limit.financeRecords"), buAyKayit, limitKontrol.limit, [t("premium.benefit.unlimitedMonthly"), t("premium.benefit.reportsCharts"), t("premium.benefit.savingsGoals"), t("premium.benefit.installmentsSubscriptions")], undefined, [t("premium.plus.benefit.financeMulti"), t("premium.plus.benefit.financeAdvanced"), t("premium.plus.benefit.financeExport")]);
      return;
    }
    await finansEkleService({ familyId, finansBaslik, finansTutar, finansTur, finansKategori, finansTarih, tamAd, user, profilAvatar: profil?.avatar, paraBirimi, recurrence: finansTekrar });
    trackCoreAction(finansTur === "Gelir" ? "income_added" : "expense_added");
    // Bildirim useAppData.js polling listener'ında gönderiliyor (çift bildirim önlenir)
    setFinansBaslik(""); setFinansTutar(""); setFinansTekrar({ enabled: false, frequency: "monthly", endDate: "" });
  };

  const finansSil = async (f, scope = "current") => {
    if (!silebilirMi(f)) return appAlert(t("common.noDeletePermission"));
    await finansSilService({ familyId, finansId: f.id, finans: f, user, tamAd, profilAvatar: profil?.avatar, scope });
  };

  const periyotFiltre = (tarih) => {
    const d = new Date(tarih);
    if (finansPeriyot === "Yıllık") return d.getFullYear() === finansSeciliYil;
    if (finansSeciliAy === -1)      return d.getFullYear() === finansSeciliYil;
    return d.getFullYear() === finansSeciliYil && d.getMonth() === finansSeciliAy;
  };

  const finansFiltreli  = appData.finanslar.filter((f) => periyotFiltre(f.tarih));
  const finansBazTutari = useCallback(
    (item) => financeAmountInBase(item, paraBirimi, financeMarketPrices),
    [paraBirimi, financeMarketPrices]
  );
  const toplamGelir     = finansFiltreli.filter((f) => f.tur === "Gelir").reduce((a, b) => a + (finansBazTutari(b) ?? 0), 0);
  const toplamGider     = finansFiltreli.filter((f) => f.tur === "Gider").reduce((a, b) => a + (finansBazTutari(b) ?? 0), 0);
  const netBakiye       = toplamGelir - toplamGider;
  const gelirYuzde      = toplamGelir + toplamGider === 0 ? 50 : (toplamGelir / (toplamGelir + toplamGider)) * 100;

  const finansKategoriEkle = () => {
    if (!yoneticiMi) { appAlert(t("common.categoryAdminOnly")); return; }
    if (!ekKategori.trim()) return;
    appAlert(`Kategori eklendi: ${ekKategori.trim()}`);
    setEkKategori("");
  };

  const kategoriOzetleri = useMemo(() => {
    const map = {};
    finansFiltreli.forEach((f) => { const key = `${f.tur} - ${f.kategori}`; map[key] = (map[key] || 0) + (finansBazTutari(f) ?? 0); });
    return Object.entries(map).map(([ad, tutar]) => ({ ad, tutar }));
  }, [finansFiltreli, finansBazTutari]);

  /* ═══════════ ORTAK HARCAMA STATE & FONKSİYONLAR ═══════════ */

  const [harcamaFormAcik, setHarcamaFormAcik]     = useState(false);
  const [harcamaBaslik, setHarcamaBaslik]         = useState("");
  const [harcamaTutar, setHarcamaTutar]           = useState("");
  const [harcamaKategori, setHarcamaKategori]     = useState("Market");
  const [harcamaTarih, setHarcamaTarih]           = useState(new Date().toISOString().slice(0, 10));

  const harcamaEkle = async () => {
    if (!harcamaBaslik || !harcamaTutar) return;
    const yeniHarcamaId = await harcamaEkleService({ familyId, harcamaBaslik, harcamaTutar, harcamaKategori, harcamaTarih, tamAd, user, profilAvatar: profil?.avatar });
    trackCoreAction("shared_expense_added");
    // Bildirim useAppData.js polling listener'ında gönderiliyor (çift bildirim önlenir)
    setHarcamaBaslik(""); setHarcamaTutar("");
    return yeniHarcamaId;
  };

  const harcamaSil = async (h) => {
    if (!silebilirMi(h)) return appAlert(t("common.noDeletePermission"));
    await harcamaSilService({ familyId, harcama: h });
  };

  const buAyHarcama = appData.harcamalar.filter((h) => {
    const d = new Date(h.tarih); const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });
  const toplamOrtakHarcama = buAyHarcama.reduce((a, b) => a + Number(b.tutar), 0);

  const kisiBazli = useMemo(() => family_.uyeler.map((u) => {
    const isim = `${u.ad} ${u.soyad}`;
    return { isim, tutar: buAyHarcama.filter((h) => h.ekleyen === isim || h.ekleyenUid === u.uid || h.userId === u.uid).reduce((a, b) => a + Number(b.tutar), 0) };
  }), [family_.uyeler, buAyHarcama]); // eslint-disable-line react-hooks/exhaustive-deps

  const borcOzeti = useMemo(() => {
    if (!family_.borcHesabi || family_.uyeler.length === 0) return [];
    const kisiBasi = toplamOrtakHarcama / family_.uyeler.length;
    return kisiBazli.map((k) => ({ ...k, fark: k.tutar - kisiBasi }));
  }, [family_.borcHesabi, toplamOrtakHarcama, family_.uyeler, kisiBazli]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ═══════════ BELGELER FONKSİYONLAR ═══════════ */

  const belgeYukle = async (params) => {
    appData.setBelgeYukleniyor(true); appData.setBelgeYuklemePct(0);
    try {
      return await belgeYukleService({ familyId, ...params, yukleyenAd: tamAd, yukleyenUid: user.uid, profilAvatar: profil?.avatar, onProgress: (pct) => appData.setBelgeYuklemePct(pct) });
    } finally { appData.setBelgeYukleniyor(false); appData.setBelgeYuklemePct(0); }
  };

  const belgeSil = async ({ belgeId, belge }) => { await belgeSilService({ familyId, belgeId, belge, user, tamAd, profilAvatar: profil?.avatar }); };

  const belgeGuncelle = async ({ belgeId, guncel }) => { await belgeGuncelleService({ familyId, belgeId, guncel }); };

  const belgeVersiyonEkle = async ({ belgeId, eskiVeri, dosya }) => {
    appData.setBelgeYukleniyor(true); appData.setBelgeYuklemePct(0);
    try {
      await belgeVersiyonEkleService({ familyId, belgeId, eskiVeri, dosya, yukleyenAd: tamAd, yukleyenUid: user.uid, profilAvatar: profil?.avatar, onProgress: (pct) => appData.setBelgeYuklemePct(pct) });
    } finally { appData.setBelgeYukleniyor(false); appData.setBelgeYuklemePct(0); }
  };

  /* ═══════════ İLAÇ HATIRLATICISI ═══════════
     Native push + web push + bildirim merkezi kaydı backend cron tarafından üretilir.
     Burada lokal toast üretmek aynı ilaç için çift in-app bildirime ve merkezsiz kayda neden oluyordu.
  ═══════════ */

  /* ═══════════════════════════════════════════════════════════
     ANDROID SİSTEM GERİ TUŞU
  ═══════════════════════════════════════════════════════════ */

  const androidBackHandlerRef = useRef(null);
  androidBackHandlerRef.current = () => {
    if (family_.seciliUye) {
      family_.setSeciliUye(null);
      return;
    }
    if (premiumPopup.visible) {
      closePremiumPopup();
      return;
    }
    if (noFamilyDeleteOpen) {
      resetNoFamilyDelete();
      return;
    }
    if (notifs.bildirimPopupAcik) {
      notifs.setBildirimPopupAcik(false);
      return;
    }
    if (gunlukPopupAcik) {
      setGunlukPopupAcik(false);
      return;
    }
    if (family_.basariMesaji) {
      family_.setBasariMesaji("");
      return;
    }
    if (appTourActive || appTourPending) {
      setAppTourActive(false);
      setAppTourPending(false);
      return;
    }
    if (menuAcik) {
      setMenuAcik(false);
      return;
    }
    if (ayarlarAcik) {
      setAyarlarAcik(false);
      return;
    }
    if (calendarIntegrationOpen) {
      closeCalendarIntegration();
      return;
    }
    if (alisverisFormAcik) {
      setAlisverisFormAcik(false);
      return;
    }
    if (gorevFormAcik) {
      setGorevFormAcik(false);
      return;
    }
    if (finansFormAcik) {
      setFinansFormAcik(false);
      return;
    }
    if (harcamaFormAcik) {
      setHarcamaFormAcik(false);
      return;
    }
    if (chatRoomAcik) {
      chatRoomBackWithGlobalAnimation();
      return;
    }
    if (aktifSayfaRef.current === "finans" && typeof window.__financeSubpageBack === "function") {
      const financeBack = window.__financeSubpageBack;
      setSwipeBgMode("finans");
      requestAnimationFrame(() => requestAnimationFrame(() => showBg()));
      snapComplete(0, () => financeBack(), "sekme");
      return;
    }
    if (aktifSayfaRef.current === "belgeler" && documentsDetailAcik) {
      documentsDetailBackWithGlobalAnimation();
      return;
    }
    if (aktifSayfaRef.current === "saglik" && healthDetailAcik) {
      healthDetailBackWithGlobalAnimation();
      return;
    }
    if (aktifSayfaRef.current === "cocuk" && childrenDetailAcik) {
      childrenDetailBackWithGlobalAnimation();
      return;
    }
    if (aktifSayfaRef.current !== "ana") {
      navigateTo(assistantBackTarget());
      return;
    }
    CapacitorApp.exitApp();
  };

  useEffect(() => {
    if (Capacitor.getPlatform() !== "android") return undefined;

    let listenerHandle;
    let disposed = false;

    const registerBackButton = async () => {
      listenerHandle = await CapacitorApp.addListener("backButton", () => {
        if (window.__APP_FORCE_UPDATE_ACTIVE__ === true) return;
        androidBackHandlerRef.current?.();
      });

      if (disposed) listenerHandle?.remove();
    };

    registerBackButton().catch((error) => {
      console.warn("[Android] Sistem geri tuşu dinleyicisi kurulamadı:", error);
    });

    return () => {
      disposed = true;
      listenerHandle?.remove();
    };
  }, []); // Android'e özel; iOS ve web'de dinleyici kurulmaz.

  /* ═══════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */

  const completeEmailVerification = async () => {
    const dogrulandi = await auth_.emailDogrulamaKontrolEt();
    if (!dogrulandi) return false;
    clearEmailAction();
    setLoadingFamily(true);
    await family_.aileBilgisiYukle(auth_.user || user);
    return true;
  };

  if (resetPasswordToken) {
    return withStartupSplash(<ResetPasswordPage token={resetPasswordToken} onContinue={clearEmailAction} />);
  }

  if (verifyEmailToken) {
    return withStartupSplash(
      <VerifyEmailPage
        token={verifyEmailToken}
        onContinue={clearEmailAction}
        onVerified={completeEmailVerification}
      />
    );
  }

  /* ── Cache'den hızlı başlatma kontrolü ──
     Önceki oturumdan hem token+user hem aile cache'i varsa
     loading ekranlarını atla, arka planda doğrulama devam etsin */
  const hasCachedSession = (() => {
    try {
      const token = getAccessToken() || getRefreshToken();
      const userRaw = localStorage.getItem("fp:user");
      const familyRaw = localStorage.getItem("fp:familyCache");
      if (!token || !userRaw || !familyRaw) return false;
      const cachedUser = JSON.parse(userRaw);
      const cachedFamily = JSON.parse(familyRaw);
      const userUid = String(cachedUser?.uid || cachedUser?.firebase_uid || cachedUser?.id || "").trim();
      return !!(
        userUid &&
        cachedUser?.emailVerified &&
        String(cachedFamily?.userUid || "").trim() === userUid &&
        cachedFamily?.familyId
      );
    } catch { return false; }
  })();

  // Cache'den familyId oku — state yüklenene kadar "aile yok" ekranını engelle
  const cachedFamilyId = (() => {
    try {
      const familyRaw = localStorage.getItem("fp:familyCache");
      const userRaw = localStorage.getItem("fp:user");
      if (!familyRaw || !userRaw) return null;
      const familyCache = JSON.parse(familyRaw);
      const cachedUser = JSON.parse(userRaw);
      const userUid = String(cachedUser?.uid || cachedUser?.firebase_uid || cachedUser?.id || "").trim();
      return String(familyCache?.userUid || "").trim() === userUid ? (familyCache?.familyId || null) : null;
    } catch { return null; }
  })();

  // Ana render için: gerçek familyId state'i yoksa cache'den geçici doldur
  const effectiveFamilyId = familyId || (hasCachedSession ? cachedFamilyId : null);

  /* Auth kontrol yükleniyor */
  if (authKontrolEdiliyor && !hasCachedSession && !webSocialPopupPending) {
    return withStartupSplash(
      <div className={`page loadingPage${webPageClass}`}>
        <div className="phoneContainer">
          <div className="loadingCard">
            <div className="loadingIcon">
  <img src="/logo512.png" alt={t("app.name")} className="loadingLogo" />
</div>
            <h2>{t("common.loading")}</h2>
            <p>{t("app.loadingSession")}</p>
          </div>
        </div>
      </div>
    );
  }

  const interaktifGirisYap = async (girisIslemi, startupBaslangiciniErtele = false) => {
    setLoginStartupRequiresReady(true);
    setLoginStartupReady(false);
    if (startupBaslangiciniErtele) {
      setWebSocialPopupPending(true);
    } else {
      setLoginSplashRunKey((value) => value + 1);
      setLoginStartupVisible(true);
    }
    setLoginHydrationUid("");
    try {
      const loggedInUser = await girisIslemi?.();
      if (!loggedInUser || loggedInUser?.onboardingRequired) {
        setLoginStartupVisible(false);
        setLoginStartupReady(false);
        setLoginStartupRequiresReady(false);
        return loggedInUser || null;
      }

      if (startupBaslangiciniErtele) {
        setLoginSplashRunKey((value) => value + 1);
        setLoginStartupVisible(true);
      }

      const uid = String(loggedInUser?.uid || loggedInUser?.firebase_uid || loggedInUser?.id || "").trim();
      setLoginHydrationUid(uid);
      setLoadingFamily(true);
      const resolvedFamilyId = await family_.aileBilgisiYukle(
        loggedInUser,
        loggedInUser?.profil || loggedInUser
      );

      if (resolvedFamilyId) {
        appData.hydrateDashboardCache(resolvedFamilyId, uid);
      } else {
        appData.setUrunler([]);
        appData.setGorevler([]);
        appData.setFinanslar([]);
        appData.setHarcamalar([]);
        appData.setTakvimEtkinlikleri([]);
        appData.setBelgeler([]);
        appData.setHealthRecords([]);
        appData.setManualChildren([]);
        appData.setChildRecords([]);
      }
      return loggedInUser;
    } catch (e) {
      setLoginStartupVisible(false);
      setLoginStartupReady(false);
      setLoginStartupRequiresReady(false);
      setLoginHydrationUid("");
      throw e;
    } finally {
      if (startupBaslangiciniErtele) {
        setWebSocialPopupPending(false);
      }
    }
  };

  /* Giriş yapılmamış — cache varsa user state yüklenene kadar gösterme */
  if (!user && !hasCachedSession) {
    return withStartupSplash(
      <AuthPage
        loginBilgi={auth_.loginBilgi} setLoginBilgi={auth_.setLoginBilgi}
        sifre={auth_.sifre} setSifre={auth_.setSifre}
        kayitSifre={auth_.kayitSifre} setKayitSifre={auth_.setKayitSifre}
        sifreInputRef={auth_.sifreInputRef}
        sifreGoster={auth_.sifreGoster} setSifreGoster={auth_.setSifreGoster}
        hata={auth_.hata} setHata={auth_.setHata}
        bilgiMesaji={auth_.bilgiMesaji} setBilgiMesaji={auth_.setBilgiMesaji}
        yukleniyor={auth_.yukleniyor}
        tema={tema} setTema={setTema}
        girisYap={() => interaktifGirisYap(auth_.girisYap)}
        sosyalGirisYap={(provider) => interaktifGirisYap(
          () => auth_.sosyalGirisYap(provider),
          !Capacitor.isNativePlatform()
        )}
        sifremiUnuttum={auth_.sifremiUnuttum}
        kayitModu={auth_.kayitModu} setKayitModu={auth_.setKayitModu}
        yasalOnay={auth_.yasalOnay} setYasalOnay={auth_.setYasalOnay}
        ad={auth_.ad} setAd={auth_.setAd}
        soyad={auth_.soyad} setSoyad={auth_.setSoyad}
        dogumTarihi={auth_.dogumTarihi} setDogumTarihi={auth_.setDogumTarihi}
        cinsiyet={auth_.cinsiyet} setCinsiyet={auth_.setCinsiyet}
        telefon={auth_.telefon} setTelefon={auth_.setTelefon}
        kullaniciAdi={auth_.kullaniciAdi} setKullaniciAdi={auth_.setKullaniciAdi}
        rol={auth_.rol} setRol={auth_.setRol}
        kanGrubu={auth_.kanGrubu} setKanGrubu={auth_.setKanGrubu}
        boy={auth_.boy} setBoy={auth_.setBoy}
        kilo={auth_.kilo} setKilo={auth_.setKilo}
        email={auth_.email} setEmail={auth_.setEmail}
        bekleyenSosyalKayit={auth_.bekleyenSosyalKayit}
        kayitAlanHatasi={auth_.kayitAlanHatasi}
        setKayitAlanHatasi={auth_.setKayitAlanHatasi}
        sosyalAkisiIptalEt={auth_.sosyalAkisiIptalEt}
        kayitOl={async () => {
          const registeredUser = await auth_.kayitOl();
          if (!registeredUser) return;

          const shouldResolveFamilyNow = !!registeredUser?.emailVerified;
          if (shouldResolveFamilyNow) {
            setLoginSplashRunKey((value) => value + 1);
            setLoginStartupRequiresReady(true);
            setLoginStartupReady(false);
            setLoginStartupVisible(true);
            setLoginHydrationUid(String(registeredUser?.uid || registeredUser?.firebase_uid || registeredUser?.id || "").trim());
          }

          family_.aileSifirla({ clearCache: true });
          appData.setUrunler([]);
          appData.setGorevler([]);
          appData.setFinanslar([]);
          appData.setHarcamalar([]);
          appData.setTakvimEtkinlikleri([]);
          appData.setBelgeler([]);
          appData.setHealthRecords([]);
          appData.setManualChildren([]);
          appData.setChildRecords([]);
          appData.setLocations({});
          appData.setLocationsFamilyId(null);
          appData.setMapMetricCounts({ places: 0, geofences: 0, safeZones: 0, locationPoints: 0 });
          appData.setOkunmamisChat(0);
          appData.setOkunmamisChatOda(0);
          appData.setToplamSohbet(0);
          notifs.setMerkeziBildirimler([]);
          notifs.setMerkeziChatBildirimler([]);
          notifs.setBildirimPopupAcik(false);
          setAktifSayfa("ana");

          if (shouldResolveFamilyNow) {
            try {
              const resolvedFamilyId = await family_.aileBilgisiYukle(
                registeredUser,
                registeredUser?.profil || registeredUser
              );
              if (!resolvedFamilyId) {
                setLoginStartupVisible(false);
                setLoginStartupRequiresReady(false);
                setLoginHydrationUid("");
              }
            } catch {
              setLoginStartupVisible(false);
              setLoginStartupRequiresReady(false);
              setLoginHydrationUid("");
            }
          }
        }}
      />
    );
  }

  /* E-posta doğrulanmamış — backend sisteminde user null ise bu blok atlanır */
  if (user && !emailDogrulandi && user.uid) {
    return withStartupSplash(
      <AuthPage
        emailDogrulamaBekliyor
        userEmail={user.email}
        loginBilgi={auth_.loginBilgi} setLoginBilgi={auth_.setLoginBilgi}
        sifre={auth_.sifre} setSifre={auth_.setSifre}
        kayitSifre={auth_.kayitSifre} setKayitSifre={auth_.setKayitSifre}
        sifreInputRef={auth_.sifreInputRef}
        sifreGoster={auth_.sifreGoster} setSifreGoster={auth_.setSifreGoster}
        hata={auth_.hata} setHata={auth_.setHata}
        bilgiMesaji={auth_.bilgiMesaji} setBilgiMesaji={auth_.setBilgiMesaji}
        yukleniyor={auth_.yukleniyor}
        tema={tema} setTema={setTema}
        girisYap={auth_.girisYap}
        sifremiUnuttum={auth_.sifremiUnuttum}
        dogrulamaMailiGonderildi={auth_.dogrulamaMailiGonderildi}
        dogrulamaMailiTekrarGonder={auth_.dogrulamaMailiTekrarGonder}
        dogrulamaEmailDegistir={auth_.dogrulamaEmailDegistir}
        emailDogrulamaKontrolEt={completeEmailVerification}
        cikisYap={auth_.cikisYap}
        kayitModu={false} setKayitModu={auth_.setKayitModu}
        yasalOnay={auth_.yasalOnay} setYasalOnay={auth_.setYasalOnay}
        ad={auth_.ad} setAd={auth_.setAd}
        soyad={auth_.soyad} setSoyad={auth_.setSoyad}
        dogumTarihi={auth_.dogumTarihi} setDogumTarihi={auth_.setDogumTarihi}
        cinsiyet={auth_.cinsiyet} setCinsiyet={auth_.setCinsiyet}
        telefon={auth_.telefon} setTelefon={auth_.setTelefon}
        kullaniciAdi={auth_.kullaniciAdi} setKullaniciAdi={auth_.setKullaniciAdi}
        rol={auth_.rol} setRol={auth_.setRol}
        kanGrubu={auth_.kanGrubu} setKanGrubu={auth_.setKanGrubu}
        boy={auth_.boy} setBoy={auth_.setBoy}
        kilo={auth_.kilo} setKilo={auth_.setKilo}
        email={auth_.email} setEmail={auth_.setEmail}
        kayitAlanHatasi={auth_.kayitAlanHatasi}
        setKayitAlanHatasi={auth_.setKayitAlanHatasi}
        kayitOl={auth_.kayitOl}
      />
    );
  }

  /* Aile bilgisi yükleniyor / henüz kesinleşmedi.
     "Aile grubun yok" ekranı sadece başarılı /families/my cevabından sonra gösterilir. */
  if ((loadingFamily || !familyResolved) && !effectiveFamilyId) {
    return withStartupSplash(
      <div className={`page loadingPage${webPageClass}`}>
        <div className="phoneContainer">
          <div className="loadingCard">
            <div className="loadingIcon">
  <img src="/logo512.png" alt={t("app.name")} className="loadingLogo" />
</div>
            <h2>{t("common.loading")}</h2>
            <p>{t("app.loadingFamily")}</p>
          </div>
        </div>
      </div>
    );
  }

  /* Profil/aile kararı kesinleşmeden aile oluştur/katıl ekranı gösterme */
  if (user && emailDogrulandi && (!profil || !familyResolved) && !effectiveFamilyId) {
    return withStartupSplash(
      <div className={`page loadingPage${webPageClass}`}>
        <div className="phoneContainer">
          <div className="loadingCard">
            <div className="loadingIcon">
  <img src="/logo512.png" alt={t("app.name")} className="loadingLogo" />
</div>
            <h2>{t("common.loading")}</h2>
            <p>{t("app.loadingFamily")}</p>
          </div>
        </div>
      </div>
    );
  }

  /* Aile yok ekranı yalnızca backend aile kararını başarıyla kesinleştirdiyse gösterilir */
  if (familyResolved && !effectiveFamilyId) {
    if (!noFamilyOnboardingStartedAtRef.current) noFamilyOnboardingStartedAtRef.current = Date.now();
    const noFamilySurname = String(
      profil?.soyad || profil?.surname || user?.profil?.soyad || user?.soyad || user?.surname || ""
    ).trim();
    const suggestedFamilyName = noFamilySurname
      ? t("family.suggestedName", { surname: noFamilySurname })
      : "";
    const noFamilyAuthProviders = Array.isArray(user?.authProviders) ? user.authProviders : [];
    const noFamilyAppleLinked = noFamilyAuthProviders.includes("apple.com");
    const noFamilyUsePasswordForDeletion = user?.hasPassword !== false && !noFamilyAppleLinked;
    const noFamilyDeletionProviders = noFamilyAppleLinked
      ? ["apple.com"]
      : noFamilyAuthProviders.filter((provider) => provider === "google.com" || provider === "apple.com");
    const farkliHesabaGec = async () => {
      const confirmed = await appConfirm(
        lang === "en"
          ? "This account has already been created. If you continue, sign in to another existing account instead of registering the same person again. Do you want to sign out?"
          : "Bu hesap zaten oluşturuldu. Devam edersen aynı kişi için yeniden kayıt olmak yerine mevcut diğer hesabınla giriş yap. Çıkış yapmak istiyor musun?",
        {
          title: t("auth.diffAccount"),
          confirmText: t("profile.logout"),
          cancelText: t("common.cancel"),
          danger: true,
        }
      );
      if (!confirmed) return;
      resetNoFamilyDelete();
      await auth_.cikisYap();
    };
    const aileOlusturmaFormunuAc = async () => {
      if (!(await aileLimitKontrolEt(t("premium.feature.familyCreate")))) return;
      setMenuAcik(false);
      family_.setSeciliAileEkrani("olustur");
      family_.setAileHata("");
      setNoFamilyDeleteOpen(false);
    };
    const aileKatilmaFormunuAc = async () => {
      if (!(await aileLimitKontrolEt(t("premium.feature.familyJoin")))) return;
      setMenuAcik(false);
      family_.setSeciliAileEkrani("katil");
      family_.setAileHata("");
      setNoFamilyDeleteOpen(false);
    };
    const aileOlusturmayiTamamla = async (event) => {
      event?.preventDefault?.();
      if (!(await aileLimitKontrolEt(t("premium.feature.familyCreate")))) return;
      setMenuAcik(false);
      setLoginSplashRunKey((value) => value + 1);
      setLoginStartupRequiresReady(false);
      setLoginStartupVisible(true);
      noFamilyTourSourceRef.current = true;
      try {
        await family_.aileOlustur(suggestedFamilyName);
      } finally {
        // Aile sonucu artık create yanıtıyla hemen commit edilir; splash
        // yalnız mevcut premium animasyonun minimum süresini tamamlar.
        window.setTimeout(() => setLoginStartupVisible(false), 2000);
      }
    };
    const aileyeKatilmayiTamamla = async (event) => {
      event?.preventDefault?.();
      if (!(await aileLimitKontrolEt(t("premium.feature.familyJoin")))) return;
      setMenuAcik(false);
      setLoginSplashRunKey((value) => value + 1);
      setLoginStartupRequiresReady(false);
      setLoginStartupVisible(true);
      noFamilyTourSourceRef.current = true;
      try {
        await family_.aileyeKatil();
      } finally {
        window.setTimeout(() => setLoginStartupVisible(false), 800);
      }
    };
    return withStartupSplash(
      <div className={`page noFamilyPage${webPageClass}`}>
        <div className="phoneContainer">
          <Header familyName={t("app.name")} profil={profil} cikisYap={auth_.cikisYap} noMenu />
          <main className="familyChoiceCard familyEmptyHeroCard familyOnboardingCard">
            <div className="familyOnboardingIntro">
              <div className="familyEmptyHeroIcon">
                <img src="/logo512.png" alt="" aria-hidden="true" />
              </div>
              <div className="familyOnboardingIntroCopy">
                <span className="familyOnboardingEyebrow">{t("family.setupEyebrow")}</span>
                <h2>{t("family.noneTitle")}</h2>
                <p>{t("family.noneDesc")}</p>
              </div>
            </div>

            <div className="familyOnboardingProgress" aria-label={t("family.setupProgressLabel")}>
              <span className="isActive"><b>1</b>{t("family.setupStepChoose")}</span>
              <i aria-hidden="true" />
              <span className={family_.seciliAileEkrani ? "isActive" : ""}><b>2</b>{t("family.setupStepComplete")}</span>
            </div>

            <section className="familyPathGrid" aria-label={t("family.setupChooseLabel")}>
              <button
                type="button"
                className={`familyPathCard familyPathCardCreate${family_.seciliAileEkrani === "olustur" ? " isSelected" : ""}`}
                onClick={aileOlusturmaFormunuAc}
                aria-pressed={family_.seciliAileEkrani === "olustur"}
              >
                <span className="familyPathIcon" aria-hidden="true">⌂</span>
                <span className="familyPathBadge">{t("family.createOptionBadge")}</span>
                <strong>{t("family.createOptionTitle")}</strong>
                <small>{t("family.createOptionDesc")}</small>
                <span className="familyPathAction">{t("family.createOptionAction")} <b>→</b></span>
              </button>
              <button
                type="button"
                className={`familyPathCard familyPathCardJoin${family_.seciliAileEkrani === "katil" ? " isSelected" : ""}`}
                onClick={aileKatilmaFormunuAc}
                aria-pressed={family_.seciliAileEkrani === "katil"}
              >
                <span className="familyPathIcon" aria-hidden="true">#</span>
                <span className="familyPathBadge">{t("family.joinOptionBadge")}</span>
                <strong>{t("family.joinOptionTitle")}</strong>
                <small>{t("family.joinOptionDesc")}</small>
                <span className="familyPathAction">{t("family.joinOptionAction")} <b>→</b></span>
              </button>
            </section>

            {family_.seciliAileEkrani === "olustur" && (
              <form className="familySetupForm" onSubmit={aileOlusturmayiTamamla}>
                <div className="familySetupFormHeading">
                  <span className="familySetupStepNumber">2</span>
                  <div>
                    <h3>{t("family.createNew")}</h3>
                    <p>{t("family.createFormDesc")}</p>
                  </div>
                </div>
                <label className="familySetupField">
                  <span>{t("family.nameLabel")}</span>
                  <input
                    className="input fullInput"
                    placeholder={suggestedFamilyName || t("family.placeholderName")}
                    value={family_.aileAdi}
                    onChange={(e) => family_.setAileAdi(e.target.value)}
                    autoComplete="off"
                    autoFocus
                  />
                  <small>{t("family.nameHelp")}</small>
                </label>
                {family_.aileHata && <div className="errorBox">{family_.aileHata}</div>}
                <button type="submit" className="primaryButton familySetupSubmit">{t("family.createButton")} <span>→</span></button>
              </form>
            )}
            {family_.seciliAileEkrani === "katil" && (
              <form className="familySetupForm" style={{ "--primary-color": profil?.calendarColor || "#6366f1" }} onSubmit={aileyeKatilmayiTamamla}>
                <div className="familySetupFormHeading">
                  <span className="familySetupStepNumber">2</span>
                  <div>
                    <h3>{t("familyInvite.joinTitle")}</h3>
                    <p>{t("familyInvite.joinDescription")}</p>
                  </div>
                </div>
                <label className="familySetupField">
                  <span>{t("family.inviteCodeLabel")}</span>
                  <input
                    className="input fullInput familyInviteJoinInput"
                    placeholder="FP-XXXXXXX"
                    value={family_.katilmaKodu}
                    onChange={(e) => family_.setKatilmaKodu(formatFamilyInviteInput(e.target.value))}
                    maxLength={10}
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="text"
                    autoFocus
                  />
                  <small>{t("family.inviteCodeHelp")}</small>
                </label>
                <div className="familyInviteJoinHelper">
                  <span aria-hidden="true">🔒</span>
                  <div>{t("familyInvite.joinHelper")}</div>
                </div>
                {family_.aileHata && <div className="errorBox">{family_.aileHata}</div>}
                <button type="submit" className="primaryButton familySetupSubmit">{t("family.joinButton")} <span>→</span></button>
              </form>
            )}

            <div className="familyAccountSwitch">
              <span>{t("family.wrongAccountHint")}</span>
              <button type="button" onClick={farkliHesabaGec}>{t("auth.diffAccount")}</button>
            </div>
          </main>
          <div className="familyChoiceCard familyDeleteAccountCard">
            <div className="familyDeleteAccountTop">
              <div className="familyDeleteAccountIcon">🔐</div>
              <div>
                <h3>{t("family.noFamilyDeleteTitle")}</h3>
              </div>
            </div>
            {!noFamilyDeleteOpen ? (
              <button
                className="familyDeleteAccountButton"
                onClick={() => {
                  family_.setSeciliAileEkrani(null);
                  setNoFamilyDeleteOpen(true);
                  setNoFamilyDeleteReasons([]);
                  setNoFamilyDeleteOtherText("");
                  setNoFamilyDeleteSurveyComplete(false);
                  setNoFamilyDeletePassword("");
                }}
              >{t("settings.privacy.deleteAccount")}</button>
            ) : !noFamilyDeleteSurveyComplete ? (
              <AccountDeletionSurvey
                t={t}
                reasons={noFamilyDeleteReasons}
                setReasons={setNoFamilyDeleteReasons}
                otherText={noFamilyDeleteOtherText}
                setOtherText={setNoFamilyDeleteOtherText}
                onContinue={() => {
                  setNoFamilyDeletePassword("");
                  setNoFamilyDeleteSurveyComplete(true);
                }}
                onCancel={resetNoFamilyDelete}
              />
            ) : (
              <div className="familyDeleteAccountForm">
                {noFamilyUsePasswordForDeletion ? (
                  <input
                    className="input fullInput"
                    type="password"
                    placeholder={t("settings.privacy.confirmPasswordPlaceholder")}
                    value={noFamilyDeletePassword}
                    onChange={(e) => setNoFamilyDeletePassword(e.target.value)}
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                ) : (
                  <p>{t("settings.privacy.socialDeleteHint")}</p>
                )}
                <div className="familyDeleteAccountActions">
                  {(noFamilyUsePasswordForDeletion
                    ? [{ provider: "", label: t("settings.privacy.confirmDelete") }]
                    : noFamilyDeletionProviders
                        .map((provider) => ({
                          provider,
                          label: `${provider === "apple.com" ? "" : "G"} ${t("settings.privacy.socialReauth")}`,
                        }))
                  ).map(({ provider, label }) => (
                    <button
                      key={provider || "password"}
                      className="familyDeleteAccountButton"
                      disabled={noFamilyDeleteLoading}
                      onClick={async () => {
                      if (noFamilyUsePasswordForDeletion && !noFamilyDeletePassword) {
                          await appAlert(t("settings.privacy.passwordRequired"), { modal: true, title: t("settings.privacy.deleteAccount") });
                          return;
                        }
                      const onay = await appConfirm(t("family.noFamilyDeleteFinalWarning"), {
                        title: t("settings.privacy.deleteAccount"),
                        confirmText: t("settings.privacy.confirmDelete"),
                        cancelText: t("common.cancel"),
                        danger: true,
                      });
                      if (!onay) return;
                      try {
                        setNoFamilyDeleteLoading(true);
                        const elapsedSeconds = Math.max(
                          0,
                          Math.floor((Date.now() - (noFamilyOnboardingStartedAtRef.current || Date.now())) / 1000)
                        );
                        await auth_.hesapSil(noFamilyDeletePassword, provider, {
                          reasons: noFamilyDeleteReasons,
                          otherText: noFamilyDeleteReasons.includes("other") ? noFamilyDeleteOtherText.trim() : "",
                          flowStage: "no_family_onboarding",
                          onboardingElapsedSeconds: elapsedSeconds,
                        });
                        resetNoFamilyDelete();
                        await appAlert(t("settings.privacy.deleteSuccess"), { modal: true, title: t("settings.privacy.deleteAccount") });
                      } catch (e) {
                        const code = e?.code;
                        const message = code === "invalid_password"
                          ? t("settings.privacy.invalidPassword")
                          : (e?.message || t("settings.privacy.deleteFailed"));
                        await appAlert(message, { modal: true, title: t("settings.privacy.deleteAccount") });
                      } finally {
                        setNoFamilyDeleteLoading(false);
                      }
                    }}
                    >{noFamilyDeleteLoading ? (t("common.loading") || "Bekleyin...") : label}</button>
                  ))}
                  <button
                    className="secondaryButton"
                    disabled={noFamilyDeleteLoading}
                    onClick={resetNoFamilyDelete}
                  >{t("common.cancel")}</button>
                </div>
              </div>
            )}
          </div>
          <PremiumPopup
            visible={premiumPopup.visible}
            onClose={closePremiumPopup}
            onPurchase={premium.purchasePremium}
            onRestore={premium.restorePurchase}
            iapEnabled={premium.iapEnabled}
            mode={premiumPopup.mode}
            featureTitle={premiumPopup.featureTitle}
            featureDesc={premiumPopup.featureDesc}
            limitLabel={premiumPopup.limitLabel}
            limitCurrent={premiumPopup.limitCurrent}
            limitMax={premiumPopup.limitMax}
            benefits={premiumPopup.benefits}
            plusBenefits={premiumPopup.plusBenefits}
            familyId={familyId}
          />
        </div>
      </div>
    );
  }

  /* Aile bulundu fakat bu cihazda dashboard cache'i yoksa ilk temel veriler
     tamamlanmadan sahte 0 değerleri gösterme. Cache varsa ana sayfa anında açılır. */
  if (
    effectiveFamilyId &&
    (
      appData.initialDataUserUid !== String(user?.uid || user?.firebase_uid || user?.id || "").trim() ||
      appData.initialDataFamilyId !== effectiveFamilyId ||
      appData.initialDataLoading
    )
  ) {
    return withStartupSplash(<div className={`page${webPageClass}`} aria-hidden="true" />);
  }

  /* ── Ana uygulama ── */
  return withStartupSplash(
    <div className={`page${webPageClass} ${STATUS_BAR_GUARD_PAGES.has(aktifSayfa) ? "appStatusBarGuard" : ""} ${aktifSayfa === "chat" ? "chatPageActive" : ""} ${aktifSayfa === "finans" ? "financePageActive" : ""}`.trim()}>
      {/* GLOBAL ARAMA EKRANI — uygulamanın kökünde. Hangi sayfa açık olursa
          olsun gelen/giden/aktif arama arayüzü burada gösterilir. */}
      <AramaEkrani
        uyeler={family_.uyeler || []}
        uyeRenk={null}
        benimUid={user?.uid || ""}
      />
      <OfflineBanner />
      <SideMenu acik={menuAcik} kapat={() => setMenuAcik(false)} aktifSayfa={aktifSayfa} setAktifSayfa={navigateTo} permissions={permissions} onChangeLang={auth_.dilGuncelle} yoneticiMi={yoneticiMi || isSuperAdmin} tema={tema} setTema={setTema} />

      {/* Sol kenar gölgesi — swipe sırasında görünür */}
      <div ref={edgeShadowRef} className="swipeEdgeShadow" aria-hidden="true" />

      {/* ── ARKA PLAN KATMANI — swipePageWrap'in DIŞINDA, transform etkilenmesin ── */}
      <div className="swipeBgLayer" aria-hidden={aktifSayfa !== "ana"}>
      <div ref={bgInnerRef} className="swipeBgInner">

        {/* Ana sayfa — aktif sayfadayken VEYA bg mode "ana" iken göster */}
        <div style={aktifSayfa !== "ana" && swipeBgMode !== "ana" ? { display: "none" } : {}}>
          {adminMode && isSuperAdmin ? (
            <AdminDashboardPage
              user={user}
              profil={profil}
              setAktifSayfa={navigateTo}
              onExitAdmin={() => {
                setAdminMode(false);
                navigateTo("ana");
              }}
            />
          ) : (
            <HomePage
              familyId={familyId}
              user={user}
              profil={profil} setAktifSayfa={navigateTo} permissions={permissions}
              cikisYap={auth_.cikisYap} familyName={family_.familyName}
              familyCode={family_.familyCode} yoneticiMi={yoneticiMi}
              koduKopyala={family_.koduKopyala} uyeler={family_.uyeler}
              setSeciliUye={family_.setSeciliUye} ailedenAyril={family_.ailedenAyril}
              toplamUrun={toplamUrun} bekleyenUrun={bekleyenUrun} alinanUrun={alinanUrun}
              yaklasanlar={yaklasanTakvimEtkinlikleri} toplamEtkinlikBuAy={buAyTakvimEtkinlikSayisi} netBakiye={netBakiye}
              toplamGorev={appData.gorevler.filter((g) => !g.tamamlandi).length}
              toplamOrtakHarcama={toplamOrtakHarcama}
              toplamSaglikKaydi={appData.healthRecords.length}
              toplamBelge={appData.belgeler.length}
              belgeler={appData.belgeler}
              cocukKartSayisi={cocukKartSayisi}
              cocukTakipKaydi={cocukTakipKaydi}
              aktifKonumPaylasan={aktifKonumPaylasan}
              toplamHaritaKaydi={(appData.mapMetricCounts?.places || 0) + (appData.mapMetricCounts?.geofences || 0) + (appData.mapMetricCounts?.safeZones || 0) + (appData.mapMetricCounts?.locationPoints || 0)}
              paraFormatla={paraFormatla}
              profilGuncelle={(params) => auth_.profilGuncelle(params, family_.uyeProfilGuncelle)}
              profilFotografiYukle={(file, onProgress) => auth_.profilFotografiYukle(file, onProgress, family_.uyeProfilGuncelle)}
              profilFotografiSil={(fallbackAvatar) => auth_.profilFotografiSil(fallbackAvatar, family_.uyeProfilGuncelle)}
              sifreGuncelle={auth_.sifreGuncelle}
              ayarlarAcik={ayarlarAcik} setAyarlarAcik={setAyarlarAcik}
              tema={tema} setTema={setTema}
              paraBirimi={paraBirimi} setParaBirimi={setParaBirimi}
              bildirimAcik={bildirimAcik} setBildirimAcik={setBildirimAcik}
              bildirimGun={bildirimGun} setBildirimGun={setBildirimGun}
              varsayilanSayfa={varsayilanSayfa} setVarsayilanSayfa={setVarsayilanSayfa}
              alisverisHatirlatici={alisverisHatirlatici} setAlisverisHatirlatici={setAlisverisHatirlatici}
              hesapSil={auth_.hesapSil} aileAdiGuncelle={family_.aileAdiGuncelle}
              sosyalHesapBagla={auth_.sosyalHesapBagla}
              uyeRenkGuncelle={(uid, renk) => family_.uyeRenkGuncelle(uid, renk, uid === user?.uid ? (r) => setProfil(p => ({ ...p, calendarColor: r })) : undefined)} ownerUid={family_.ownerUid}
              okunmamisChat={appData.okunmamisChat} okunmamisChatOda={appData.okunmamisChatOda}
              toplamSohbet={appData.toplamSohbet || 0}
              onBildirimAc={() => notifs.setBildirimPopupAcik(true)}
              toplamOkunmamisBildirim={toplamOkunmamisBildirimSayisi}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLimitHit={showPremiumLimit}
              onAileDegisti={async (yeniAile) => {
                if (yeniAile?.isAdminMode) {
                  if (!isSuperAdmin) return;
                  setAdminMode(true);
                  navigateTo("ana");
                  return;
                }
                setAdminMode(false);
                // Aile değişiminde mevcut tam ekran logo splash'ini yeniden oynat;
                // hedef aile arkada hazırlanırken iki saniye boyunca görünür kalır.
                if (yeniAile.familyId && yeniAile.familyId !== familyId) {
                  setAileGecisCikabilir(false);
                  setAileGecisIcerikGoster(false);
                  setAileGecisHedefId(String(yeniAile.familyId));
                  setAileGecisRunKey((k) => k + 1);
                  setAileGecisSplashVisible(true);

                  // Hedef ailenin son güvenli dashboard snapshot'ını splash
                  // arkasında önceden uygula; fade başladığında ana sayfa hazırdır.
                  appData.hydrateDashboardCache(
                    String(yeniAile.familyId),
                    String(user?.uid || user?.firebase_uid || user?.id || "").trim()
                  );
                }
                await family_.switchFamily(yeniAile.familyId, {
                  reason: "manual",
                  family: yeniAile,
                  families: yeniAile.families,
                  backendAlreadyPersisted: true,
                });
                // Üyeler familyId değişince useEffect tarafından tek kaynaktan yenilenir.
              }}
              dilGuncelle={auth_.dilGuncelle}
              verileriDisaAktar={auth_.verileriDisaAktar}
              isSuperAdmin={isSuperAdmin}
              onOpenLegal={openLegalDocument}
              onOpenFeedback={openFeedbackPage}
              onStartAppTour={() => {
                setAppTourPending(true);
                window.requestAnimationFrame(() => window.setTimeout(() => {
                  setAppTourActive(true);
                  setAppTourPending(false);
                }, 40));
              }}
            />
          )}
        </div>
        {/* /ana sayfa wrapper */}

        {/* İç içe sayfa bg'leri — sadece gesture sırasında ilgili mod aktifken render */}
        {swipeBgMode === "saglik" && !areAndroidHealthFeaturesDisabled() && (
          <div>
            <Header familyName={sayfaBasliklari["saglik"]} profil={profil} cikisYap={auth_.cikisYap} menuAc={() => {}} geriDon={() => {}} />
            <HealthPage
              familyId={familyId} uyeler={family_.uyeler} user={user}
              yoneticiMi={yoneticiMi} permissions={permissions}
              belgeler={appData.belgeler} belgeYukle={belgeYukle} belgeSil={belgeSil}
              yukleniyor={false} yuklemePct={0}
              onHealthDetailStateChange={() => {}} onDetailBackRequest={() => {}}
              onInternalForwardTransition={(commitFn) => commitFn?.()}
              isPremium={premium.isPremium} premiumConfig={premium.premiumConfig} onPremiumLimitHit={() => {}}
              onOwnHealthProfileUpdated={({ healthProfile, user: updatedUser }) => {
                setProfil((p) => ({
                  ...(p || {}),
                  ...(updatedUser || {}),
                  healthProfile: healthProfile || updatedUser?.healthProfile || p?.healthProfile || {},
                }));
                family_.setUyeler((prev) => {
                  const next = (prev || []).map((u) => {
                    if ((u.uid || u.id) !== user?.uid) return u;
                    return {
                      ...u,
                      ...(updatedUser || {}),
                      healthProfile: healthProfile || updatedUser?.healthProfile || u.healthProfile || {},
                    };
                  });
                  family_.uyelerRef.current = next;
                  return next;
                });
              }}
            />
          </div>
        )}
        {swipeBgMode === "cocuk" && !areAndroidHealthFeaturesDisabled() && (
          <div>
            <Header familyName={sayfaBasliklari["cocuk"]} profil={profil} cikisYap={auth_.cikisYap} menuAc={() => {}} geriDon={() => {}} />
            <ChildrenPage
              onChildrenDetailStateChange={() => {}} familyId={familyId} uyeler={family_.uyeler} user={user}
              yoneticiMi={yoneticiMi} permissions={permissions}
              belgeler={appData.belgeler} belgeYukle={belgeYukle} belgeSil={belgeSil}
              yukleniyor={false} yuklemePct={0} onDetailBackRequest={() => {}}
              isPremium={premium.isPremium} premiumConfig={premium.premiumConfig}
              onPremiumLock={() => {}} onPremiumLimitHit={() => {}}
            />
          </div>
        )}
        {swipeBgMode === "belgeler" && (
          <div>
            <Header familyName={sayfaBasliklari["belgeler"]} profil={profil} cikisYap={auth_.cikisYap} menuAc={() => {}} geriDon={() => {}} />
            <DocumentsPage
              belgeler={appData.belgeler} uyeler={family_.uyeler}
              belgeYukle={belgeYukle} belgeGuncelle={belgeGuncelle}
              belgeSil={belgeSil} belgeVersiyonEkle={belgeVersiyonEkle}
              yukleniyor={false} yuklemePct={0}
              permissions={permissions} user={user} yoneticiMi={yoneticiMi}
              onDocumentsDetailStateChange={() => {}} onDocumentsDetailBackRequest={() => {}}
              isPremium={premium.isPremium} premiumConfig={premium.premiumConfig} onPremiumLimitHit={() => {}}
            />
          </div>
        )}
        {/* Chat odası açıkken Aile Sohbeti listesi arka katmanda önceden hazır tutulur.
            Normal sayfalarda ana sayfanın arkada hazır beklemesiyle aynı mantık. */}
        {(swipeBgMode === "chat" || (aktifSayfa === "chat" && chatRoomAcik)) && (
          <div
            style={swipeBgMode === "chat"
              ? {}
              : { position: "absolute", inset: 0, width: "100%", visibility: "hidden", pointerEvents: "none" }}
            aria-hidden={swipeBgMode !== "chat"}
          >
            <Header familyName={sayfaBasliklari["chat"]} profil={profil} cikisYap={auth_.cikisYap} menuAc={() => {}} geriDon={() => {}} />
            <ChatPage
              familyId={familyId} user={user} tamAd={tamAd}
              uyeler={family_.uyeler} permissions={permissions}
              yoneticiMi={yoneticiMi} islemIzniVarMi={islemIzniVarMi}
              setAktifSayfa={() => {}}
              gorevEkle={gorevEkle} urunEkle={urunEkle}
              bildirimGoster={() => {}}
              bildirimHerkeseYazService={bildirimHerkeseYazService}
              initialRoomId={null} onInitialRoomConsumed={() => {}}
              onRoomOpened={() => {}} onChatRoomStateChange={() => {}}
              isPremium={premium.isPremium} premiumConfig={premium.premiumConfig}
              onPremiumLock={() => {}} onPremiumLimitHit={() => {}}
              isBgInstance={true}
            />
          </div>
        )}
        {swipeBgMode === "takvim" && (
          <div>
            <Header familyName={sayfaBasliklari["takvim"]} profil={profil} cikisYap={auth_.cikisYap} menuAc={() => {}} geriDon={() => {}} />
            <CalendarPage
              isBgInstance={true}
              uyeler={family_.uyeler || []} gorevler={appData.gorevler || []}
              takvimEtkinlikleri={appData.takvimEtkinlikleri || []}
              takvimRenkleri={takvimRenkleri} familyId={familyId}
              user={user} tamAd={tamAd} permissions={permissions}
              takvimEtkinlikEkleService={() => {}}
              takvimEtkinlikSilService={() => {}}
              takvimEtkinlikGuncelleService={() => {}}
              bildirimHerkeseYazService={() => {}}
              onReminderKur={() => {}}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLimitHit={() => {}}
              onBirthdayClick={openBirthdayDetail}
            />
          </div>
        )}
        {swipeBgMode === "finans" && (
          <div>
            <Header familyName={sayfaBasliklari["finans"]} profil={profil} cikisYap={auth_.cikisYap} menuAc={() => {}} geriDon={() => {}} />
            <FinancePage
              isBgInstance={true}
              familyId={familyId}
              finansFormAcik={false} setFinansFormAcik={() => {}}
              yoneticiMi={yoneticiMi} finansBaslik={finansBaslik} setFinansBaslik={() => {}}
              finansTutar={finansTutar} setFinansTutar={() => {}}
              finansTur={finansTur} setFinansTur={() => {}}
              finansKategori={finansKategori} setFinansKategori={() => {}}
              finansTarih={finansTarih} setFinansTarih={() => {}}
              finansEkle={() => {}} finansPeriyot={finansPeriyot} setFinansPeriyot={() => {}}
              finansSeciliAy={finansSeciliAy} setFinansSeciliAy={() => {}}
              finansSeciliYil={finansSeciliYil} setFinansSeciliYil={() => {}}
              toplamGelir={toplamGelir} toplamGider={toplamGider} netBakiye={netBakiye}
              gelirYuzde={gelirYuzde} finansFiltreli={finansFiltreli} finansSil={() => {}}
              gelirKategorileri={gelirKategorileri} giderKategorileri={giderKategorileri}
              ekKategori={ekKategori} setEkKategori={() => {}}
              finansKategoriEkle={() => {}} kategoriOzetleri={kategoriOzetleri}
              paraFormatla={paraFormatla} permissions={permissions} user={user}
              uyeler={family_.uyeler} paraBirimi={paraBirimi}
              tumFinanslar={appData.finanslar} buAyHarcama={buAyHarcama}
              toplamOrtakHarcama={toplamOrtakHarcama} kisiBazli={kisiBazli} borcOzeti={borcOzeti}
              navigateTo={() => {}} onReminderKur={() => {}}
              isPremium={premium.isPremium} premiumConfig={premium.premiumConfig}
              onPremiumLock={() => {}} onPremiumLimitHit={() => {}}
            />
          </div>
        )}

      </div>
      </div>
      {/* /swipeBgLayer */}

      <div className="phoneContainer" ref={phoneContainerRef}>
        {/* ── ÜST KATMAN: Aktif sayfa — yalnızca ana sayfa değilken render, swipe ile sürüklenir ── */}
        {aktifSayfa !== "ana" && (
        <div ref={activePageRef} className={`swipePageWrap ${aktifSayfa === "alisveris" ? (compactShoppingViewport ? "shoppingCompactPageScroll" : "shoppingNoPageScroll") : ""} ${aktifSayfa === "takvim" && !calendarIntegrationOpen ? (compactCalendarViewport ? "calendarCompactPageScroll" : "calendarNoPageScroll") : ""} ${aktifSayfa === "takvim" && calendarIntegrationOpen ? "calendarIntegrationScroll" : ""} ${aktifSayfa === "gorev" ? `tasksTransitionGuard ${compactTasksViewport ? "tasksCompactPageScroll" : ""}` : ""} ${aktifSayfa === "cocuk" ? "childrenTransitionGuard" : ""} ${aktifSayfa === "chat" ? "chatTransitionGuard" : ""} ${["harcama", "belgeler", "nePisirsam", "activity", "saglik", "kullaniciYonetimi", "feedback"].includes(aktifSayfa) ? "stablePageTransitionGuard" : ""} ${aktifSayfa === "harcama" ? "expenseAutoScrollGuard" : ""}`.trim()}>
          {/* Chat header sohbet odası açıkken de DOM'da kalır. Oda ekranı fixed/z-index ile
              üstünü örter; geri swipe başladığında alttaki gerçek Aile Sohbeti sayfası
              header dahil eksiksiz ve aynı frame'de görünür. */}
          {aktifSayfa !== "legal" && !(aktifSayfa === "saglik" && healthDetailAcik) && !(aktifSayfa === "cocuk" && childrenDetailAcik) && !(aktifSayfa === "belgeler" && documentsDetailAcik) && (
            <Header
              familyName={aktifSayfa === "takvim" && calendarIntegrationOpen
                ? (lang === "en" ? "Calendar Integrations" : "Takvim Entegrasyonları")
                : sayfaBasliklari[aktifSayfa]}
              profil={profil}
              cikisYap={auth_.cikisYap}
              menuAc={() => setMenuAcik(true)}
              rightAction={aktifSayfa === "takvim" && !calendarIntegrationOpen ? {
                icon: "⚙️",
                label: lang === "en" ? "Calendar integration settings" : "Takvim entegrasyonu ayarları",
                onClick: () => setCalendarIntegrationOpen(true),
              } : null}
              geriDon={() => {
              if (aktifSayfa === "takvim" && calendarIntegrationOpen) {
                closeCalendarIntegration();
                return;
              }
              if (aktifSayfa === "finans" && typeof window.__financeSubpageBack === "function") {
                // Swipe-to-back'in finans dalıyla (onEnd içindeki "sekme" mode'u) birebir
                // aynı mekanizma: BG katmanı gösterilip snapComplete ile geri dönülüyor.
                const _finBack = window.__financeSubpageBack;
                setSwipeBgMode("finans");
                requestAnimationFrame(() => requestAnimationFrame(() => showBg()));
                snapComplete(0, () => { if (typeof _finBack === "function") _finBack(); }, "sekme");
                return;
              }
              if (aktifSayfa === "saglik" && typeof window.__healthDetailCommitBack === "function") { healthDetailBackWithGlobalAnimation(); return; }
              if (aktifSayfa === "cocuk" && typeof window.__childrenDetailCommitBack === "function") { childrenDetailBackWithGlobalAnimation(); return; }
              if (aktifSayfa === "belgeler" && typeof window.__documentsDetailCommitBack === "function") { documentsDetailBackWithGlobalAnimation(); return; }
              navigateTo(assistantBackTarget());
            }} />
          )}



          {/* SAYFALAR (ana hariç) */}
          {aktifSayfa === "legal" && (
            <LegalDocumentPage
              documentKey={legalDocumentKey}
              embedded
              transitionDirection={legalTransitionDirection}
              onBack={() => navigateTo("ana")}
              onNavigate={changeLegalDocument}
            />
          )}

          {aktifSayfa === "feedback" && (
            <FeedbackPage
              familyId={familyId}
              onBack={() => navigateTo("ana")}
            />
          )}

          {aktifSayfa === "alisveris" && sayfaIzniVarMi("shopping") && (
            <ShoppingPage
              alisverisFormAcik={alisverisFormAcik} setAlisverisFormAcik={setAlisverisFormAcik}
              yoneticiMi={yoneticiMi} toplamUrun={toplamUrun} bekleyenUrun={bekleyenUrun} alinanUrun={alinanUrun}
              alisverisKategorileri={appData.alisverisKategorileri} seciliKategori={seciliKategori} setSeciliKategori={setSeciliKategori}
              urunAdi={urunAdi} setUrunAdi={setUrunAdi} urunMiktar={urunMiktar} setUrunMiktar={setUrunMiktar}
              urunBirim={urunBirim} setUrunBirim={setUrunBirim} urunKategori={urunKategori} setUrunKategori={setUrunKategori}
              urunEkle={urunEkle} filtreliUrunler={filtreliUrunler}
              alindiDegistir={alindiDegistir} urunSil={urunSil} permissions={permissions} user={user}
              familyId={familyId} tamAd={tamAd} profilAvatar={profil?.avatar}
              onReminderKur={onReminderKur}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLimitHit={showPremiumLimit}
            />
          )}

          {aktifSayfa === "gorev" && sayfaIzniVarMi("tasks") && (
            <TasksPage
              permissions={permissions} user={user}
              gorevFormAcik={gorevFormAcik} setGorevFormAcik={gorevFormAcikSet}
              uyeler={family_.uyeler} gorevAdi={gorevAdi} setGorevAdi={setGorevAdi}
              gorevAciklama={gorevAciklama} setGorevAciklama={setGorevAciklama}
              gorevAtananlar={gorevAtananlar} setGorevAtananlar={setGorevAtananlar}
              gorevSonTarih={gorevSonTarih} setGorevSonTarih={setGorevSonTarih}
              gorevOncelik={gorevOncelik} setGorevOncelik={setGorevOncelik}
              gorevKategori={gorevKategori} setGorevKategori={setGorevKategori}
              gorevEkle={gorevEkle} gorevFiltresi={gorevFiltresi} setGorevFiltresi={setGorevFiltresi}
              filtreliGorevler={filtreliGorevler} tumGorevler={Array.isArray(appData.gorevler) ? appData.gorevler : []} gecikmisMi={gecikmisMi}
              gorevTamamla={gorevTamamla} gorevSil={gorevSil}
              onReminderKur={onReminderKur}
            />
          )}

          {aktifSayfa === "finans" && sayfaIzniVarMi("finance") && (
            <FinancePage
              familyId={familyId}
              finansFormAcik={finansFormAcik} setFinansFormAcik={setFinansFormAcik}
              yoneticiMi={yoneticiMi} finansBaslik={finansBaslik} setFinansBaslik={setFinansBaslik}
              finansTutar={finansTutar} setFinansTutar={setFinansTutar}
              finansTur={finansTur} setFinansTur={setFinansTur}
              finansKategori={finansKategori} setFinansKategori={setFinansKategori}
              finansTarih={finansTarih} setFinansTarih={setFinansTarih}
              finansTekrar={finansTekrar} setFinansTekrar={setFinansTekrar}
              finansEkle={finansEkle} finansPeriyot={finansPeriyot} setFinansPeriyot={setFinansPeriyot}
              finansSeciliAy={finansSeciliAy} setFinansSeciliAy={setFinansSeciliAy}
              finansSeciliYil={finansSeciliYil} setFinansSeciliYil={setFinansSeciliYil}
              toplamGelir={toplamGelir} toplamGider={toplamGider} netBakiye={netBakiye}
              gelirYuzde={gelirYuzde} finansFiltreli={finansFiltreli} finansSil={finansSil}
              gelirKategorileri={gelirKategorileri} giderKategorileri={giderKategorileri}
              ekKategori={ekKategori} setEkKategori={setEkKategori}
              finansKategoriEkle={finansKategoriEkle} kategoriOzetleri={kategoriOzetleri}
              paraFormatla={paraFormatla} permissions={permissions} user={user}
              uyeler={family_.uyeler}
              paraBirimi={paraBirimi}
              tumFinanslar={appData.finanslar}
              buAyHarcama={buAyHarcama}
              toplamOrtakHarcama={toplamOrtakHarcama}
              kisiBazli={kisiBazli}
              borcOzeti={borcOzeti}
              navigateTo={navigateTo}
              onReminderKur={onReminderKur}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLock={showPremiumLock}
              onPremiumLimitHit={showPremiumLimit}
              onInternalForwardTransition={runForwardPageTransition}
            />
          )}

          {aktifSayfa === "harcama" && sayfaIzniVarMi("sharedExpenses") && (
            <SharedExpensesPage
              harcamaFormAcik={harcamaFormAcik} setHarcamaFormAcik={setHarcamaFormAcik}
              borcHesabi={family_.borcHesabi} borcHesabiDegistir={family_.borcHesabiDegistir}
              harcamaBaslik={harcamaBaslik} setHarcamaBaslik={setHarcamaBaslik}
              harcamaTutar={harcamaTutar} setHarcamaTutar={setHarcamaTutar}
              harcamaKategori={harcamaKategori} setHarcamaKategori={setHarcamaKategori}
              harcamaTarih={harcamaTarih} setHarcamaTarih={setHarcamaTarih}
              harcamaEkle={harcamaEkle} toplamOrtakHarcama={toplamOrtakHarcama}
              kisiBazli={kisiBazli} borcOzeti={borcOzeti} buAyHarcama={buAyHarcama}
              tumHarcamalar={appData.harcamalar} uyeler={family_.uyeler}
              harcamaSil={harcamaSil} harcamaKategorileri={harcamaKategorileri}
              paraFormatla={paraFormatla} permissions={permissions} user={user}
              familyId={familyId} tamAd={tamAd}
              onReminderKur={onReminderKur}
            />
          )}

          {aktifSayfa === "saglik" && !areAndroidHealthFeaturesDisabled() && sayfaIzniVarMi("health") && (
            <HealthPage
              familyId={familyId} uyeler={family_.uyeler} user={user}
              yoneticiMi={yoneticiMi} permissions={permissions}
              belgeler={appData.belgeler} belgeYukle={belgeYukle} belgeSil={belgeSil}
              yukleniyor={appData.belgeYukleniyor} yuklemePct={appData.belgeYuklemePct}
              onHealthDetailStateChange={setHealthDetailAcik}
              onDetailBackRequest={healthDetailBackWithGlobalAnimation}
              onInternalForwardTransition={runForwardPageTransition}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLimitHit={showPremiumLimit}
              onOwnHealthProfileUpdated={({ healthProfile, user: updatedUser }) => {
                setProfil((p) => ({
                  ...(p || {}),
                  ...(updatedUser || {}),
                  healthProfile: healthProfile || updatedUser?.healthProfile || p?.healthProfile || {},
                }));
                family_.setUyeler((prev) => {
                  const next = (prev || []).map((u) => {
                    if ((u.uid || u.id) !== user?.uid) return u;
                    return {
                      ...u,
                      ...(updatedUser || {}),
                      healthProfile: healthProfile || updatedUser?.healthProfile || u.healthProfile || {},
                    };
                  });
                  family_.uyelerRef.current = next;
                  return next;
                });
              }}
            />
          )}

          {aktifSayfa === "cocuk" && !areAndroidHealthFeaturesDisabled() && sayfaIzniVarMi("children") && (
            <ChildrenPage
              onChildrenDetailStateChange={setChildrenDetailAcik}
              familyId={familyId} uyeler={family_.uyeler} user={user}
              yoneticiMi={yoneticiMi} permissions={permissions}
              belgeler={appData.belgeler} belgeYukle={belgeYukle} belgeSil={belgeSil}
              yukleniyor={appData.belgeYukleniyor} yuklemePct={appData.belgeYuklemePct}
              onDetailBackRequest={childrenDetailBackWithGlobalAnimation}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLock={showPremiumLock}
              onPremiumLimitHit={showPremiumLimit}
              onInternalForwardTransition={runForwardPageTransition}
            />
          )}

          {aktifSayfa === "belgeler" && sayfaIzniVarMi("documents") && (
            <DocumentsPage
              belgeler={appData.belgeler} uyeler={family_.uyeler}
              belgeYukle={belgeYukle} belgeGuncelle={belgeGuncelle}
              belgeSil={belgeSil} belgeVersiyonEkle={belgeVersiyonEkle}
              yukleniyor={appData.belgeYukleniyor} yuklemePct={appData.belgeYuklemePct}
              permissions={permissions} user={user} yoneticiMi={yoneticiMi}
              onDocumentsDetailStateChange={setDocumentsDetailAcik}
              onDocumentsDetailBackRequest={documentsDetailBackWithGlobalAnimation}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLimitHit={showPremiumLimit}
              onInternalForwardTransition={runForwardPageTransition}
            />
          )}


          {aktifSayfa === "activity" && (
            <ActivityFeedPage
              familyId={familyId}
              user={user}
              permissions={permissions}
              setAktifSayfa={navigateTo}
              uyeler={family_.uyeler}
            />
          )}

          {aktifSayfa === "assistant" && sayfaIzniVarMi("assistant") && (
            <AssistantPage
              familyId={familyId}
              user={user}
              userName={profil?.ad || profil?.firstName || ""}
              permissions={permissions}
              setAktifSayfa={navigateTo}
              navigateTo={navigateTo}
              navigateFromAssistant={navigateFromAssistant}
              refreshKey={`${appData.gorevler.length}|${appData.urunler.length}|${appData.takvimEtkinlikleri.length}|${appData.finanslar.length}|${appData.harcamalar.length}|${appData.healthRecords.length}|${appData.belgeler.length}|${appData.childRecords?.length || 0}|${appData.manualChildren?.length || 0}|${appData.okunmamisChat || 0}|${appData.toplamSohbet || 0}|${appData.mapMetricCounts?.places || 0}|${appData.mapMetricCounts?.geofences || 0}|${appData.mapMetricCounts?.safeZones || 0}|${appData.mapMetricCounts?.locationPoints || 0}|${toplamOkunmamisBildirimSayisi}`}
              toplamGorev={appData.gorevler.filter((g) => !g.tamamlandi).length}
              toplamUrun={toplamUrun}
              bekleyenUrun={bekleyenUrun}
              yaklasanlar={yaklasanTakvimEtkinlikleri}
              toplamEtkinlikBuAy={buAyTakvimEtkinlikSayisi}
              netBakiye={netBakiye}
              toplamOrtakHarcama={toplamOrtakHarcama}
              toplamSaglikKaydi={appData.healthRecords.length}
              toplamBelge={appData.belgeler.length}
              cocukKartSayisi={cocukKartSayisi}
              cocukTakipKaydi={cocukTakipKaydi}
              aktifKonumPaylasan={aktifKonumPaylasan}
              toplamOrtakYer={appData.mapMetricCounts?.places || 0}
              toplamSinir={appData.mapMetricCounts?.geofences || 0}
              toplamGuvenliAlan={appData.mapMetricCounts?.safeZones || 0}
              toplamKonumGecmisi={appData.mapMetricCounts?.locationPoints || 0}
              okunmamisChat={appData.okunmamisChat}
              toplamSohbet={appData.toplamSohbet || 0}
              toplamBildirim={toplamBildirimSayisi}
              toplamOkunmamisBildirim={toplamOkunmamisBildirimSayisi}
              toplamOkunmamisSistemBildirim={okunmamisSistemBildirimSayisi}
              toplamOkunmamisSohbetBildirim={okunmamisSohbetBildirimSayisi}
            />
          )}

          {aktifSayfa === "kullaniciYonetimi" && sayfaIzniVarMi("userManagement") && (
            <UserManagementPage
              profil={profil}
              uyeler={family_.uyeler}
              seciliYetkiUye={family_.seciliYetkiUye}
              setSeciliYetkiUye={family_.setSeciliYetkiUye}
              yetkiKaydet={family_.yetkiKaydet}
              uyeSifreSifirla={family_.uyeSifreSifirla}
              uyeYetkiDegistir={family_.uyeYetkiDegistir}
              uyeRolDegistir={family_.uyeRolDegistir}
              permissions={permissions}
              yoneticiMi={yoneticiMi || isSuperAdmin}
              premiumConfig={premium.premiumConfig}
            />
          )}

          {aktifSayfa === "takvim" && sayfaIzniVarMi("calendar") && calendarIntegrationOpen && (
            <CalendarIntegrationSettingsPage
              onPreferencesChanged={calendarIntegrationPreferencesChanged}
              onRefresh={async () => {
                await refreshExternalCalendarEvents();
              }}
              refreshing={externalCalendarRefreshing}
              importing={externalCalendarImporting}
              importableCount={importableExternalEvents.length}
              onImportAll={importAllExternalCalendarEvents}
            />
          )}

          {aktifSayfa === "takvim" && sayfaIzniVarMi("calendar") && !calendarIntegrationOpen && (
            <CalendarPage
              uyeler={family_.uyeler || []} gorevler={appData.gorevler || []}
              takvimEtkinlikleri={appData.takvimEtkinlikleri || []}
              takvimRenkleri={takvimRenkleri} familyId={familyId}
              user={user} tamAd={tamAd} permissions={permissions}
              takvimEtkinlikEkleService={takvimEtkinlikEkleService}
              takvimEtkinlikSilService={takvimEtkinlikSilService}
              takvimEtkinlikGuncelleService={takvimEtkinlikGuncelleService}
              bildirimHerkeseYazService={bildirimHerkeseYazService}
              onReminderKur={onReminderKur}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLimitHit={showPremiumLimit}
              externalCalendarEvents={externalCalendarEvents}
              onBirthdayClick={openBirthdayDetail}
            />
          )}

          {aktifSayfa === "nePisirsam" && sayfaIzniVarMi("nePisirsam") && (
            <NePisirsamPage
              familyId={familyId} onGeri={() => navigateTo("ana")}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLock={showPremiumLock}
              onPremiumLimitHit={showPremiumLimit}
              canAddShopping={permissions?.actions?.addShopping === true && sayfaIzniVarMi("shopping")}
              onShoppingAdd={tarifMalzemeleriniAlisveriseEkle}
              onOpenShopping={() => navigateTo("alisveris")}
            />
          )}

          {aktifSayfa === "harita" && sayfaIzniVarMi("harita") && (
            <HaritaPage
              familyId={familyId}
              user={user}
              tamAd={tamAd}
              uyeler={family_.uyeler}
              yoneticiMi={yoneticiMi}
              permissions={permissions}
              bildirimEkleService={bildirimEkleService}
              bildirimHerkeseYazService={bildirimHerkeseYazService}
              bildirimGoster={notifs.bildirimGoster}
              uyelerRef={uyelerRef}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLock={showPremiumLock}
              onPremiumLimitHit={showPremiumLimit}
            />
          )}

          {aktifSayfa === "chat" && sayfaIzniVarMi("chat") && (
            <ChatPage
              familyId={familyId} user={user} tamAd={tamAd}
              uyeler={family_.uyeler} permissions={permissions}
              yoneticiMi={yoneticiMi} islemIzniVarMi={islemIzniVarMi}
              setAktifSayfa={navigateTo}
              gorevEkle={gorevEkle} urunEkle={urunEkle}
              bildirimGoster={notifs.bildirimGoster}
              bildirimHerkeseYazService={bildirimHerkeseYazService}
              initialRoomId={notifs.bildirimChatRoomId}
              onInitialRoomConsumed={() => notifs.setBildirimChatRoomId(null)}
              onRoomOpened={(roomId) => notifs.chatOdaBildirimleriniTemizle(roomId, familyId, user?.uid)}
              onChatRoomStateChange={setChatRoomAcik}
              isPremium={premium.isPremium}
              premiumConfig={premium.premiumConfig}
              onPremiumLock={showPremiumLock}
              onPremiumLimitHit={showPremiumLimit}
              onInternalForwardTransition={runForwardPageTransition}
              onInternalBackTransition={chatRoomBackWithGlobalAnimation}
            />
          )}

        </div>
        )}
        {/* /swipePageWrap */}

        {/* ── Modallar ve overlay'ler — her iki katmanın dışında ── */}
        {family_.seciliUye && (
          <UyeModal
            seciliUye={family_.seciliUye} setSeciliUye={family_.setSeciliUye}
            yasHesapla={yasHesapla} yoneticiMi={yoneticiMi}
            uyeRolDegistir={family_.uyeRolDegistir}
            uyeYetkiDegistir={family_.uyeYetkiDegistir}
            uyeyiAiledenCikar={family_.uyeyiAiledenCikar}
            ownerUid={family_.ownerUid}
          />
        )}

        <PremiumPopup
          visible={premiumPopup.visible}
          onClose={closePremiumPopup}
          onPurchase={premium.purchasePremium}
          onRestore={premium.restorePurchase}
          iapEnabled={premium.iapEnabled}
          mode={premiumPopup.mode}
          featureTitle={premiumPopup.featureTitle}
          featureDesc={premiumPopup.featureDesc}
          limitLabel={premiumPopup.limitLabel}
          limitCurrent={premiumPopup.limitCurrent}
          limitMax={premiumPopup.limitMax}
          benefits={premiumPopup.benefits}
          plusBenefits={premiumPopup.plusBenefits}
          familyId={familyId}
        />

        <InAppNotification
          notifications={notifs.inAppNotifs}
          onDismiss={notifs.bildirimKaldir}
          uyeler={family_.uyeler}
          onNotificationClick={(notif) => notificationTikla(notif).catch(() => {})}
        />

        <BildirimPopup
          acik={notifs.bildirimPopupAcik} kapat={() => notifs.setBildirimPopupAcik(false)}
          bildirimler={notifs.merkeziBildirimler} chatBildirimler={notifs.merkeziChatBildirimler}
          familyId={familyId} userUid={user?.firebase_uid || user?.uid} user={user}
          onBildirimlerGuncelle={notifs.setMerkeziBildirimler}
          onChatBildirimlerGuncelle={notifs.setMerkeziChatBildirimler}
          uyeler={family_.uyeler} onBildirimTikla={(notif) => notificationTikla(notif).catch(() => {})}
        />

        {/* Konfeti animasyonu — hoşgeldin */}
        <KonfetiCanvas
          active={konfetiAktif}
          onDone={() => setKonfetiAktif(false)}
        />

        {/* Hoşgeldin popup — aileye katılma / aile oluşturma sonrası ana sayfada gösterilir */}
        {family_.basariMesaji && (
          <div className="modalOverlay">
            <div className="welcomeJoinPopup">
              <div className="welcomeJoinGlow" />
              <div className="welcomeJoinHeader">
                <div className="welcomeJoinIconWrap">
                  <img src="/logo512.png" alt={t("app.name")} className="welcomeJoinLogo" />
                </div>
                <h2 className="welcomeJoinTitle">{t("family.welcomePopup.title")}</h2>
                <p className="welcomeJoinSubtitle">{t("family.welcomePopup.subtitle", { familyName: family_.basariMesaji })}</p>
              </div>
              <div className="welcomeJoinFeatures">
                <div className="welcomeJoinFeatureItem">
                  <img src={wpHarita}    className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature1")}</span>
                </div>
                {!areAndroidHealthFeaturesDisabled() && <div className="welcomeJoinFeatureItem">
                  <img src={wpCocuk}     className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature2")}</span>
                </div>}
                <div className="welcomeJoinFeatureItem">
                  <img src={wpFinans}    className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature3")}</span>
                </div>
                <div className="welcomeJoinFeatureItem">
                  <img src={wpAsistan}   className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature4")}</span>
                </div>
                <div className="welcomeJoinFeatureItem">
                  <img src={wpBelgeler}  className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature5")}</span>
                </div>
                <div className="welcomeJoinFeatureItem">
                  <img src={wpPisirsem}  className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature6")}</span>
                </div>
                <div className="welcomeJoinFeatureItem">
                  <img src={wpAlisveris} className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature7")}</span>
                </div>
                <div className="welcomeJoinFeatureItem">
                  <img src={wpHarcama}   className="welcomeJoinFeatureImg" alt="" />
                  <span>{t("family.welcomePopup.feature8")}</span>
                </div>
              </div>
              <button className="primaryButton welcomeJoinCta" onClick={hosgeldinSonrasiDevamEt}>
                {t("family.welcomePopup.cta")}
              </button>
            </div>
          </div>
        )}

        <AppTour
          active={appTourActive}
          onComplete={appTourBitir}
          onSkip={appTourBitir}
        />

        {birthdayCelebration && (
          <BirthdayCelebrationScreen
            data={birthdayCelebration}
            onClose={() => setBirthdayCelebration(null)}
            onOpenWishes={() => {
              const firstOccurrence = birthdayCelebration?.occurrences?.[0];
              setBirthdayCelebration(null);
              if (firstOccurrence?.id) setBirthdayDetailId(firstOccurrence.id);
            }}
          />
        )}

        {birthdayDetailId && (
          <BirthdayDetailSheet
            occurrenceId={birthdayDetailId}
            onClose={() => setBirthdayDetailId("")}
          />
        )}

        <GunlukHatirlaticiPopup
          acik={gunlukPopupAcik}
          kapat={() => setGunlukPopupAcik(false)}
          maddeler={gunlukMaddeler}
          onItemClick={(madde) => {
            if (madde.tip === "birthday") {
              openBirthdayDetail({ celebrantUid: madde.celebrantUid, tarih: madde.tarihStr });
              return;
            }
            if (madde.sayfa) navigateTo(madde.sayfa);
          }}
        />
      </div>
    </div>
  );
}

function App() {
  const path = (window.location.pathname || "/").replace(/\/+$/, "") || "/";
  const legalRouteMap = {
    "/privacy": "privacy",
    "/terms": "terms",
    "/account-deletion": "account-deletion",
    "/en/privacy": "privacy",
    "/en/terms": "terms",
    "/en/account-deletion": "account-deletion",
  };
  const documentKey = legalRouteMap[path];
  if (documentKey) return <LegalDocumentPage documentKey={documentKey} forcedLang={path.startsWith("/en/") ? "en" : undefined} />;
  return <MainApp />;
}

export default App;
